// File: src/components/OmniCine/Timeline/TimelineShell.tsx
//
// Authoritative render shell for OmniCine timeline.
// - Single scroll authority
// - Timeline-space rendering only
// - No reducer mutations outside dispatch
//
// ✅ This revision hardens:
// - Canonical viewport plumbing (viewport.* first; safe fallback only)
// - Phase ranges are label-driven ("phase:*") BUT use allowed kind ("selection")
// - Patch: computePhaseFromRanges tolerant to kind="phase" (legacy/interop)
// - "Add Phase Ranges" installs singleton listeners (supports multiple event names)
// - timeline:add clip/add payload matches Timeline reducer (payload.clip)
// - ✅ timeline:add accepts detail.url/detail.previewUrl and stores it on clip (tolerant multi-key write)
// - ✅ upgrade-in-place: requestId mapped OR recoverable by scanning clips -> PATCH existing clip
// - ✅ UX:
//    - After import: immediately focus camera on imported coklip (playhead + viewport + scrollLeft)
//      (works even when the event is a deduped upgrade / requestId remount path)
//    - Stop runaway scrolling: Follow Playhead mode (ONLY while playing && follow enabled)
//    - Import disables Follow Playhead by default (so frame doesn’t drift)
// - ✅ BUGFIX: follow/playing are STATE-backed (not ref-only), so changes actually take effect
// - ✅ PATCH: emit "omnicine:clip:selected" whenever TimelineShell selects a clip (create + patch paths)
//
// ✅ R1 foundation:
// - ruler is viewport-sized
// - ruler projection is viewport-relative
// - ruler uses canonical playhead payloads
// - ctrl+wheel zoom is cursor-centered
//
// ✅ R1.1 hardening:
// - ruler is visually locked to viewport (no content-offset gap)
// - ruler wheel / touchpad pans timeline horizontally
// - ruler ctrl+wheel / ctrl+touchpad zoom is rAF-coalesced and smoother
// - ruler remains intent-only; TimelineShell remains authority

import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";

import TransportBar from "./Transport/TransportBar";
import Ruler, { RULER_HEIGHT_PX } from "./Ruler/Ruler";
import RulerSemanticOverlay, {
  isRulerSemanticMarkerVisible,
  type RulerSemanticMarkerView,
} from "./Ruler/RulerSemanticOverlay";

import MarkerOverlay from "../Markers/MarkerOverlay";
import RangeOverlay from "./Ranges/RangeOverlay";

import { createImportedLaneFocus } from "./laneFocus";
import type { LaneDefinition, LaneItem } from "./Lanes/lane.types";
import { summarizeCapsule } from "./Lanes/capsules/capsule.summary";
import type { TimelineCapsule } from "./Lanes/capsules/capsule.types";
import { markStaleProjections, summarizeProjectionHealth } from "./Lanes/projections/projection.health";
import type { LaneProjection } from "./Lanes/projections/projection.types";
import { deriveLaneSyncBadges } from "./Lanes/sync/sync.badges";
import type { LaneDependencyRelation, LaneSyncBadge, TimelineCrossLaneCoupling } from "./Lanes/sync/sync.types";

import { useOmniCineTimeline } from "@/state/useOmniCineTimeline";
import { useOptionalOmniStudioGraph } from "@/state/useOmniStudioGraph";

import XRRangeIntentBridge from "./XR/XRRangeIntentBridge";
import XRMarkerIntentBridge from "./XR/XRMarkerIntentBridge";
import XRVideoIntentBridge from "./XR/XRVideoIntentBridge";

import TimelineHologramBridge from "./Hologram/TimelineHologramBridge";
import {
  MARKER_SESSION_STORAGE_KEY,
  removeTimelineMarkersWithPersistence,
  writePersistedMarkerSession,
} from "./markerSession.helpers";
import AudioWitnessBar, {
  ClipFocusLensOverlay,
  defaultAudioWitnessUiState,
  type AudioWitnessUiState,
  type SonicsLensId,
} from "./AudioWitnessBar";
import EmotionalArcOverlay from "./EmotionalArcOverlay";
import ContinuityWarningOverlay from "./ContinuityWarningOverlay";
import FxRegionOverlay from "./FxRegionOverlay";

type AtomicRunLaneInput = {
  id: string;
  startSec: number;
  durSec: number;
  label: string;
  status?: string | null;
};

const LANE_MODEL_DEBUG_EVENT = "omnicine:lane-model-debug";

type InitialProductLaneModel = {
  laneDefinitions: LaneDefinition[];
  laneItemsByLaneId: Record<string, LaneItem[]>;
  syncBadges: LaneSyncBadge[];
  dependencyRelations: LaneDependencyRelation[];
  capsules: TimelineCapsule[];
  crossLaneCouplings: TimelineCrossLaneCoupling[];
};

type ProductLanePublishApi = {
  registerLanes: (lanes: LaneDefinition[]) => void;
  setLaneItems: (laneId: string, items: LaneItem[]) => void;
  upsertCapsule: (capsule: TimelineCapsule) => void;
  setSyncBadges: (badges: LaneSyncBadge[]) => void;
  setDependencyRelations: (relations: LaneDependencyRelation[]) => void;
};

export function discardAuthoredSonicsByIds(args: {
  segmentIds: string[];
  fxNodeIds: string[];
  discardEmotionalIntentSegment: (segmentId: string) => void;
  discardFxIntentNode: (nodeId: string) => void;
}): void {
  const {
    segmentIds,
    fxNodeIds,
    discardEmotionalIntentSegment,
    discardFxIntentNode,
  } = args;

  for (const segmentId of segmentIds) {
    discardEmotionalIntentSegment(segmentId);
  }
  for (const nodeId of fxNodeIds) {
    discardFxIntentNode(nodeId);
  }
}

export function discardAuthoredSonicsForClip(args: {
  clipId: string;
  emotionalIntentSegments: EmotionalIntentSegment[];
  fxIntentNodes: FxIntentNode[];
  discardEmotionalIntentSegment: (segmentId: string) => void;
  discardFxIntentNode: (nodeId: string) => void;
}): { segmentIds: string[]; fxNodeIds: string[] } {
  const resolvedClipId = String(args.clipId ?? "").trim();
  if (!resolvedClipId) {
    return { segmentIds: [], fxNodeIds: [] };
  }

  const segmentIds = args.emotionalIntentSegments
    .filter((segment) => String(segment.focusClipId ?? "") === resolvedClipId)
    .map((segment) => segment.id);
  const fxNodeIds = args.fxIntentNodes
    .filter((node) => String(node.focusClipId ?? "") === resolvedClipId)
    .map((node) => node.id);

  discardAuthoredSonicsByIds({
    segmentIds,
    fxNodeIds,
    discardEmotionalIntentSegment: args.discardEmotionalIntentSegment,
    discardFxIntentNode: args.discardFxIntentNode,
  });

  return { segmentIds, fxNodeIds };
}

import EtchedContextLine from "./EtchedContextLine";
import { compileTimelinePublishStrip } from "@/contracts/omnicine/TimelinePublishStrip";
import { buildPublishStripSignals } from "@/contracts/omnicine/TimelinePublishStripSignals";
import {
  averageContinuityScore,
  deriveContinuityDiagnostics,
  summarizeContinuityDiagnostics,
} from "./NarrativeSonicsContinuity";
import {
  deriveMediaBindingEditorState,
  resolvePlaybackProjection,
  type AuditionMode,
  type ClipMediaMetadata,
  type EditorialClipBinding,
  type MediaMember,
  type PlaybackProjection,
  type ProxyDescriptor,
  type ResolvedAudioPlayback,
  type ResolvedVideoPlayback,
} from "./MediaUnion";
import type {
  ContinuityDiagnostic,
  EmotionalIntentSegment,
  FxIntentNode,
  NarrativeSonicsContext,
} from "./NarrativeSonics.types";
import { useTimelineWhisperHUD } from "./timelineWhisper";
import { useNarrativeReadModel } from "@/state/useNarrativeReadModel";
import { useOmniCineNarrative } from "@/state/useOmniCineNarrative";
import { selectActiveEmotionalIntentSegments, selectActiveFxIntentNodes } from "@/state/sonics/sonics.selectors";
import {
  buildTimelineAddClip,
  buildTimelineAddClipMediaMetadata,
  buildTimelineAddClipPatch,
} from "@/state/timeline/timelineAdd.commands";

import { startEnvelopeAutoGen } from "@/services/omnicine/envelopeAutoGen";
import { startNpcRuntimeLoop } from "@/runtime/npc/npcRuntimeLoop";

import {
  createSurfaceIntentEmitter,
  OMNICAST_SURFACE_INTENT_EVENT,
  type SurfaceIntentV1,
} from "@/bridge/omnicast/emitSurfaceIntentV1";
import {
  installSingletonTimelineAddEvent,
  onMediaPanelSetMode,
  type MediaPanelMode,
  type MediaPanelSetModeDetail,
  type TimelineAddDetail,
} from "@/contracts/media/MediaPanelEvents";
import {
  onAddPhaseRanges,
  OMNICINE_VIDEO_ANNOTATE_ADD_MARKER_EVENT,
  OMNICINE_TIMELINE_HELP_CLOSE_EVENT,
  OMNICINE_TIMELINE_HELP_TOGGLE_EVENT,
  emitMarkerAdded,
  emitTimelineHelpOpen,
  onAtomicRunsOpen,
  onAtomicRunsUpdate,
  onFollowPlayheadSet,
  onFollowPlayheadToggle,
  onMarkerContextRequest as onTimelineMarkerContextRequest,
  onMarkerEditRequest as onTimelineMarkerEditRequest,
  onTimelineHelpClose,
  onTimelineHelpOpen,
  onTimelineHelpToggle,
  onTransportPlaying,
  onVideoAnnotateAddMarker as onTimelineVideoAnnotateAddMarker,
} from "@/contracts/omnicine/TimelineSurfaceEvents";
import {
  emitOmniCineClipSelected,
  emitOmniStudioSceneSelect,
  onOmniCineClipSelected,
  onOmniCineInlineWitnessOpenGlass,
  onOmniCineInlineWitnessRemoveClip,
  onOmniStudioCompilePreviewClear,
  onOmniStudioCompilePreviewSet,
} from "@/lib/omnistudio/bridge/omnistudioBridgeContract";

import { resolveMediaIdentityTitle } from "@/components/OmniCine/media/resolveMediaTitle";

const LazyTimelineHelpDrawer = React.lazy(() => import("./Help/TimelineHelpDrawer"));
const LazyInlineWitness = React.lazy(() => import("./Overlays/InlineWitness"));
const LazyNarrativePreviewOverlay = React.lazy(() => import("./Overlays/NarrativePreviewOverlay"));
const LazyNarrativeSonicsLensHost = React.lazy(() => import("./NarrativeSonicsLensHost"));
const LazyAudioSpectrumLane = React.lazy(() => import("./Tracks/AudioSpectrumLane"));
const LazyAtomicRunsTrack = React.lazy(() => import("./Tracks/AtomicRunsTrack"));
const LazyNarrativePreviewLane = React.lazy(() => import("./Tracks/NarrativePreviewLane"));
const LazyVideoTrack = React.lazy(() => import("./Tracks/VideoTrack"));
const LazyAudioTrack = React.lazy(() => import("./Tracks/AudioTrack"));
const LazyNPCTrack = React.lazy(() => import("./Tracks/NPCTrack"));
const LazyTimelineLaneVisualStack = React.lazy(() => import("./Lanes/renderers/TimelineLaneVisualStack"));
const LazyTurningPointMarkers = React.lazy(() => import("./Overlays/TurningPointMarkers"));
const LazyTurningPointProbe = React.lazy(() => import("./__dev__/TurningPointProbe"));
const LazyMediaPanel = React.lazy(() => import("@/components/OmniCine/media/MediaPanel"));

const EMPTY_ARR: any[] = [];
const DEFAULT_SNAP = { enabled: false, mode: "second", fps: 30 } as const;

/* ------------------------------------------------------------------ */
/* Constants                                                           */
/* ------------------------------------------------------------------ */

const TOUR_KEY = "omnicine.timeline.tour.v1";

const OMNICINE_NPC_RUNTIME_EVENT = "omnicine:npc:runtime";

const OMNICAST_NPC_RUNTIME_EVENT = "omnicast:npc:runtime";
const OMNICAST_PHASE_EVENT = "omnicast:timeline:phase";

const LUMINA_VIBE_SCORE_EVENT = "lumina:vibe:score";
const OMNICINE_HUD_NPC_EVENT = "omnicine:hud:npc";

const ADD_PHASE_RANGES_LEGACY_EVENTS = [
  "omnicine:phase:ranges:add",
  "omnicine:phaseRanges:add",
  "omnicine:timeline:phaseRanges:add",
] as const;

const AUTO_SCROLL_EDGE_PAD_PX = 96;
const AUTO_SCROLL_LERP = 0.24;
const RULER_CTRL_ZOOM_SMOOTHING_ALPHA = 0.82;
const RULER_CTRL_ZOOM_GAIN = 0.0042;
const RULER_CTRL_ZOOM_DEADZONE_PX = 0.18;
const RULER_CTRL_ZOOM_IDLE_RESET_MS = 90;
const RULER_EDGE_SCRUB_ZONE_PX = 20;
const RULER_EDGE_SCRUB_MAX_SCROLL_PX_PER_FRAME = 20;

/* ------------------------------------------------------------------ */
/* OmniCast phase emission (from ranges)                               */
/* ------------------------------------------------------------------ */

type NarrativePhase = "setup" | "rising" | "peak" | "resolve";

function clamp01(n: number) {
  return Math.max(0, Math.min(1, n));
}

function parsePhaseLabel(label?: string | null): NarrativePhase | null {
  const s = String(label ?? "").trim().toLowerCase();
  if (!s.startsWith("phase:")) return null;
  const p = s.slice("phase:".length) as NarrativePhase;
  return p === "setup" || p === "rising" || p === "peak" || p === "resolve" ? p : null;
}

function isPhaseLike(r: { kind?: unknown; label?: string | null }) {
  const k = String(r.kind ?? "").toLowerCase().trim();
  if (k === "phase") return true;
  return parsePhaseLabel(r.label) != null;
}

function computePhaseFromRanges(
  ranges: Array<{ startSec: number; endSec: number; label?: string | null; kind?: unknown }>,
  playheadSec: number,
): { phase: NarrativePhase; intensity01: number; durationSec: number } | null {
  for (const r of ranges) {
    if (!isPhaseLike(r)) continue;

    const phase = parsePhaseLabel(r.label);
    if (!phase) continue;

    const a = Number(r.startSec ?? 0);
    const b = Number(r.endSec ?? a);
    const lo = Math.min(a, b);
    const hi = Math.max(a, b);

    if (playheadSec < lo || playheadSec > hi) continue;

    const dur = Math.max(0.001, hi - lo);
    const u = clamp01((playheadSec - lo) / dur);
    const intensity01 = clamp01(4 * u * (1 - u));

    return { phase, intensity01, durationSec: dur };
  }
  return null;
}

function bucket01(x: number, steps = 20) {
  const k = Math.max(1, Math.floor(steps));
  return Math.round(clamp01(x) * k) / k;
}

function finiteNum(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v);
}

/* ------------------------------------------------------------------ */
/* Small helpers                                                      */
/* ------------------------------------------------------------------ */

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function makeId(): string {
  try {
    const c = globalThis.crypto as Crypto | undefined;
    if (c?.randomUUID) return c.randomUUID();
  } catch {
    // ignore
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function parseMaybeNumber(v: unknown): number | null {
  if (typeof v === "string") {
    const t = v.trim();
    if (!t) return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  }
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function readScrollerViewportWidthPx(el: HTMLDivElement | null): number {
  if (!el) return 1;

  const styles = window.getComputedStyle(el);
  const paddingLeft = Number.parseFloat(styles.paddingLeft || "0") || 0;
  const paddingRight = Number.parseFloat(styles.paddingRight || "0") || 0;

  return Math.max(1, Math.floor(el.clientWidth - paddingLeft - paddingRight));
}

function readPxPerSec(st: unknown): number {
  const s = st as any;
  const viewport = s?.viewport ?? null;

  const pxPerSec = finiteNum(viewport?.pxPerSec)
    ? Number(viewport.pxPerSec)
    : finiteNum(s?.pxPerSec)
      ? Number(s.pxPerSec)
      : 100;

  return clamp(pxPerSec, 1e-6, 10_000);
}

function readViewportStartSec(st: unknown): number {
  const s = st as any;
  const viewport = s?.viewport ?? null;

  return finiteNum(viewport?.startSec)
    ? Number(viewport.startSec)
    : finiteNum(s?.viewportStartSec)
      ? Number(s.viewportStartSec)
      : 0;
}

function readViewportEndSec(st: unknown, fallbackStart: number, durationSec: number): number {
  const s = st as any;
  const viewport = s?.viewport ?? null;

  const raw = finiteNum(viewport?.endSec)
    ? Number(viewport.endSec)
    : finiteNum(s?.viewportEndSec)
      ? Number(s.viewportEndSec)
      : fallbackStart + 15;

  return clamp(raw, fallbackStart, Math.max(fallbackStart, durationSec));
}

function installSingletonListener(key: string, type: string, handler: EventListener) {
  const w = window as any;
  w.__oc_debug = w.__oc_debug || {};
  w.__oc_debug.__listeners = w.__oc_debug.__listeners || {};

  const prev = w.__oc_debug.__listeners[key] as EventListener | undefined;
  if (prev) window.removeEventListener(type, prev);

  w.__oc_debug.__listeners[key] = handler;
  window.addEventListener(type, handler);

  return () => {
    const cur = w.__oc_debug?.__listeners?.[key] as EventListener | undefined;
    if (cur === handler) {
      window.removeEventListener(type, handler);
      delete w.__oc_debug.__listeners[key];
    }
  };
}

/* ------------------------------------------------------------------ */
/* Singleton service registry                                         */
/* ------------------------------------------------------------------ */

type StopFn = () => void;

function installSingletonService(key: string, start: () => StopFn) {
  const w = window as any;
  w.__oc_debug = w.__oc_debug || {};
  w.__oc_debug.__services = w.__oc_debug.__services || {};

  type Entry = { stop: StopFn; refs: number };
  const services: Record<string, Entry> = w.__oc_debug.__services;

  if (services[key]) {
    services[key].refs += 1;
    return () => {
      services[key].refs -= 1;
      if (services[key].refs <= 0) {
        try {
          services[key].stop?.();
        } finally {
          delete services[key];
        }
      }
    };
  }

  const stop = start();
  services[key] = { stop, refs: 1 };

  return () => {
    services[key].refs -= 1;
    if (services[key].refs <= 0) {
      try {
        services[key].stop?.();
      } finally {
        delete services[key];
      }
    }
  };
}

function normalizeSource(raw: unknown): "user" | "xr-user" | "ai-assist" | "system" | "console" {
  const s = String(raw ?? "user").toLowerCase().trim();
  if (s === "xr-user") return "xr-user";
  if (s === "ai-assist") return "ai-assist";
  if (s === "system") return "system";
  if (s === "console") return "console";
  return "user";
}

function safe01(v: unknown, fallback = 0): number {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? clamp01(n) : clamp01(fallback);
}

function getBoundSessionId(): string | null {
  const w = window as any;
  const sid =
    (typeof w?.__oc_debug?.sessionId === "string" && w.__oc_debug.sessionId.trim()) ||
    (typeof w?.__oc_debug?.projectId === "string" && w.__oc_debug.projectId.trim()) ||
    null;

  return sid ? String(sid) : null;
}

function isEditableKeyboardTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;

  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;

  return !!target.closest("input, textarea, select, [contenteditable='true'], [role='textbox']");
}

function isNpcLikeMarker(marker: any): boolean {
  const label = String(marker?.label ?? "").trim().toLowerCase();
  const type = String(marker?.type ?? "").trim().toLowerCase();
  return label.startsWith("npc:") || type.startsWith("npc:") || type === "npc";
}

function isPhaseLikeMarker(marker: any): boolean {
  const type = String(marker?.type ?? "").trim().toLowerCase();
  return type === "phase" || type === "selection";
}

function normalizeImportedKind(raw: unknown): "video" | "audio" | "image" | "npc" {
  const value = String(raw ?? "").trim().toLowerCase();
  if (value === "audio") return "audio";
  if (value === "image") return "image";
  if (value === "npc") return "npc";
  return "video";
}

function collectNpcThresholds(probs: Record<string, unknown> | null | undefined): string[] {
  if (!probs) return [];

  const thresholds: string[] = [];
  if (safe01(probs.overload, 0) >= 0.75) thresholds.push("overload>0.75");
  if (safe01(probs.safety, 1) <= 0.5) thresholds.push("safety<0.50");
  if (safe01(probs.attention, 0) >= 0.7) thresholds.push("attention>0.70");
  if (safe01(probs.emotion, 0) >= 0.7) thresholds.push("emotion>0.70");
  return thresholds;
}

const VIDEO_PRIMARY_LANE_ID = "lane.video.primary";
const AUDIO_PRIMARY_LANE_ID = "lane.audio.primary";
const AUDIO_ANALYSIS_LANE_ID = "lane.audio.analysis";
const NPC_LOGIC_LANE_ID = "lane.npc.logic";
const PROJECTION_HAPTIC_LANE_ID = "lane.projection.haptic";
const PROJECTION_EMOTION_LANE_ID = "lane.projection.emotion";
const PROJECTION_CONTINUITY_LANE_ID = "lane.projection.continuity";
const CAPSULE_TEMPORAL_LANE_ID = "lane.capsule.temporal";
const ACTIVE_TIMELINE_CAPSULE_ID = "capsule.timeline.active";

function laneHealthFromDependencyHealth(health: LaneSyncBadge["health"]): LaneDefinition["syncHealth"] {
  switch (health) {
    case "aligned":
      return "aligned";
    case "drift_detected":
    case "partially_invalid":
      return "warning";
    case "orphaned":
      return "broken";
    case "needs_review":
    default:
      return "review_required";
  }
}

function dependencyHealthFromLaneSyncHealth(health: LaneDefinition["syncHealth"]): LaneSyncBadge["health"] {
  switch (health) {
    case "aligned":
      return "aligned";
    case "warning":
      return "drift_detected";
    case "broken":
      return "orphaned";
    case "review_required":
    default:
      return "needs_review";
  }
}

function aggregateDependencyHealth(healths: LaneSyncBadge["health"][]): LaneSyncBadge["health"] {
  if (!healths.length) return "aligned";
  if (healths.some((health) => health === "orphaned")) return "orphaned";
  if (healths.some((health) => health === "partially_invalid")) return "partially_invalid";
  if (healths.some((health) => health === "needs_review")) return "needs_review";
  if (healths.some((health) => health === "drift_detected")) return "drift_detected";
  return "aligned";
}

function continuitySeverity(status: ContinuityDiagnostic["status"]): "info" | "warning" | "critical" {
  switch (status) {
    case "coherent":
      return "info";
    case "abrupt":
    case "mismatched":
    case "unresolved":
      return "critical";
    default:
      return "warning";
  }
}

function continuityBadgeHealth(diagnostics: ContinuityDiagnostic[]): LaneSyncBadge["health"] {
  if (!diagnostics.length) return "needs_review";
  if (diagnostics.some((diagnostic) => diagnostic.status === "abrupt" || diagnostic.status === "mismatched" || diagnostic.status === "unresolved")) {
    return "orphaned";
  }
  if (diagnostics.some((diagnostic) => diagnostic.status !== "coherent")) return "drift_detected";
  return "aligned";
}

function audioAnalysisProgressScore(analysisState: string | undefined, metadataRichnessScore: number | undefined): number {
  switch (analysisState) {
    case "ready":
      return Math.max(0.8, safe01(metadataRichnessScore, 0.9));
    case "running":
      return 0.7;
    case "partial":
      return Math.max(0.45, safe01(metadataRichnessScore, 0.55));
    case "queued":
      return 0.4;
    case "failed":
      return 0.12;
    case "idle":
      return 0.25;
    default:
      return safe01(metadataRichnessScore, 0.3);
  }
}

function audioProxyConfidenceScore(proxyStatus: string | undefined, usingDeprecatedProxyFallback: boolean): number {
  switch (proxyStatus) {
    case "ready":
      return usingDeprecatedProxyFallback ? 0.72 : 1;
    case "generating":
      return 0.5;
    case "queued":
      return 0.38;
    case "stale":
      return 0.34;
    case "offline":
    case "missing":
    case "failed":
      return 0.1;
    default:
      return 0.22;
  }
}

function audioAnalysisBadgeHealth(mediaState: ReturnType<typeof describeClipMediaState>): LaneSyncBadge["health"] {
  if (mediaState.analysisState === "failed") return "partially_invalid";
  if (mediaState.offlineState === "offline" || mediaState.offlineState === "missing") return "partially_invalid";
  if (mediaState.analysisState === "queued" || mediaState.analysisState === "running" || mediaState.analysisState === "partial") return "drift_detected";
  if (typeof mediaState.metadataRichnessScore === "number" && mediaState.metadataRichnessScore < 0.65) return "drift_detected";
  if (typeof mediaState.decodePerformanceScore === "number" && mediaState.decodePerformanceScore < 0.5) return "drift_detected";
  if (mediaState.analysisState === "ready") return "aligned";
  return "needs_review";
}

function npcRuntimeBadgeHealth(runtime: Record<string, unknown> | null | undefined, runtimeTimeSec: number | null): LaneSyncBadge["health"] {
  const probs = runtime?.probs && typeof runtime.probs === "object"
    ? (runtime.probs as Record<string, unknown>)
    : null;
  const thresholds = collectNpcThresholds(probs);

  if (!runtime) return "needs_review";
  if (runtimeTimeSec == null) return "needs_review";
  if (thresholds.includes("safety<0.50")) return "partially_invalid";
  if (thresholds.includes("overload>0.75")) return "drift_detected";
  return "aligned";
}

function npcRuntimeDominantScore(runtime: Record<string, unknown> | null | undefined): number {
  const probs = runtime?.probs && typeof runtime.probs === "object"
    ? (runtime.probs as Record<string, unknown>)
    : null;

  return Math.max(
    safe01(probs?.attention, 0),
    safe01(probs?.emotion, 0),
    safe01(probs?.overload, 0),
    safe01(probs?.safety, 0),
  );
}

type TimelineAnchorRange = {
  rangeId: string;
  label: string;
  kind: string;
  startSec: number;
  endSec: number;
  followUpdates: boolean;
};

function resolveTrackAnchorRange(
  tracks: Array<{ id?: string | null; kind?: string | null; rangeAnchor?: any }> | undefined,
  ranges: Array<{ id?: string | null; startSec?: number; endSec?: number; label?: string | null; kind?: string | null }> | undefined,
  trackKind: "video" | "audio" | "npc",
): TimelineAnchorRange | null {
  const track = (tracks ?? []).find((entry) => String(entry?.kind ?? "").trim() === trackKind);
  const rangeId = String(track?.rangeAnchor?.rangeId ?? "").trim();
  if (!rangeId) return null;

  const range = (ranges ?? []).find((entry) => String(entry?.id ?? "").trim() === rangeId);
  if (!range) return null;

  const start0 = Number(range?.startSec ?? 0);
  const end0 = Number(range?.endSec ?? start0);
  const startSec = Math.max(0, Math.min(start0, end0));
  const endSec = Math.max(startSec, Math.max(start0, end0));

  return {
    rangeId,
    label: String(range?.label ?? range?.kind ?? "Anchored Range").trim() || "Anchored Range",
    kind: String(range?.kind ?? "selection").trim() || "selection",
    startSec,
    endSec,
    followUpdates: track?.rangeAnchor?.followUpdates !== false,
  };
}

function snapWindowToAnchor(startSec: number, endSec: number, anchor: TimelineAnchorRange | null) {
  if (!anchor) return { startSec, endSec };

  const nextStart = Math.max(anchor.startSec, Math.min(startSec, endSec));
  const nextEnd = Math.min(anchor.endSec, Math.max(startSec, endSec));
  if (nextEnd - nextStart >= 0.05) {
    return { startSec: nextStart, endSec: nextEnd };
  }

  return { startSec: anchor.startSec, endSec: anchor.endSec };
}

function readPreferredProxyDescriptor(metadata: ClipMediaMetadata | null | undefined, preferredKinds: string[]): ProxyDescriptor | null {
  if (!metadata) return null;

  for (const proxyId of [metadata.preferredTimelineProxyId, metadata.preferredScrubProxyId]) {
    if (!proxyId) continue;
    const proxy = metadata.proxies?.find((entry) => entry.id === proxyId) ?? null;
    if (proxy && preferredKinds.includes(proxy.kind)) return proxy;
  }

  const readyProxy = metadata.proxies?.find((entry) => preferredKinds.includes(entry.kind) && entry.status === "ready") ?? null;
  if (readyProxy) return readyProxy;

  const fallbackProxy = metadata.proxies?.find((entry) => preferredKinds.includes(entry.kind)) ?? null;
  if (fallbackProxy) return fallbackProxy;

  const hasMatchingStructuredProxyCandidate = !!metadata.proxies?.some((entry) => preferredKinds.includes(entry.kind));
  if (hasMatchingStructuredProxyCandidate) return null;

  if (preferredKinds.includes("preview_proxy") && metadata.previewProxyUrl) {
    return {
      id: `${metadata.clipId}:previewProxyUrl`,
      kind: "preview_proxy",
      tier: "low",
      status: "ready",
      uri: metadata.previewProxyUrl,
    };
  }

  if (preferredKinds.includes("waveform_proxy") && metadata.waveformProxyUrl) {
    return {
      id: `${metadata.clipId}:waveformProxyUrl`,
      kind: "waveform_proxy",
      tier: "low",
      status: "ready",
      uri: metadata.waveformProxyUrl,
    };
  }

  return null;
}

function describeClipMediaState(metadata: ClipMediaMetadata | null | undefined, preferredKinds: string[]) {
  const preferredProxy = readPreferredProxyDescriptor(metadata, preferredKinds);
  const hasStructuredProxyCandidate = !!metadata?.proxies?.some((entry) => preferredKinds.includes(entry.kind));
  const hasDirectMediaUrl = typeof metadata?.mediaUrl === "string" && metadata.mediaUrl.trim().length > 0;
  const usingDeprecatedProxyFallback = !!preferredProxy && (
    preferredProxy.id === `${metadata?.clipId}:previewProxyUrl` || preferredProxy.id === `${metadata?.clipId}:waveformProxyUrl`
  );
  const offlineState = metadata?.offlineState ?? (metadata?.mediaUrl ? "online" : undefined);
  const proxyStatus = preferredProxy?.status ?? metadata?.proxyStatus ?? undefined;
  const proxyKind = preferredProxy?.kind ?? undefined;
  const analysisState = metadata?.analysisState ?? undefined;
  const metadataRichnessScore = metadata?.metadataRichnessScore ?? undefined;
  const growingFile = metadata?.growingFile ?? undefined;
  const decodePerformanceScore = metadata?.decodePerformanceScore ?? undefined;
  const proxySource = preferredProxy
    ? usingDeprecatedProxyFallback
      ? "deprecated_proxy_fallback"
      : "structured_proxy"
    : hasDirectMediaUrl
      ? "direct_media"
      : "none";

  let proxyContractState:
    | "proxy_ready"
    | "proxy_queued"
    | "proxy_generating"
    | "proxy_stale"
    | "proxy_failed"
    | "proxy_missing"
    | "deprecated_proxy_fallback"
    | "direct_media_only"
    | "source_degraded"
    | "offline"
    | "missing" = "proxy_missing";

  if (offlineState === "offline") {
    proxyContractState = "offline";
  } else if (offlineState === "missing") {
    proxyContractState = "missing";
  } else if (proxyStatus === "ready") {
    proxyContractState = usingDeprecatedProxyFallback ? "deprecated_proxy_fallback" : "proxy_ready";
  } else if (proxyStatus === "queued") {
    proxyContractState = "proxy_queued";
  } else if (proxyStatus === "generating") {
    proxyContractState = "proxy_generating";
  } else if (proxyStatus === "stale") {
    proxyContractState = "proxy_stale";
  } else if (proxyStatus === "failed" || proxyStatus === "offline") {
    proxyContractState = "proxy_failed";
  } else if (offlineState === "partial" || offlineState === "relinked") {
    proxyContractState = "source_degraded";
  } else if (!hasStructuredProxyCandidate && hasDirectMediaUrl) {
    proxyContractState = "direct_media_only";
  }

  let summary: string | null = null;
  if (offlineState && offlineState !== "online") {
    summary = preferredProxy ? `Source ${offlineState}; rendering from ${proxyKind?.replace(/_/g, " ") ?? "proxy"}.` : `Source ${offlineState}; no proxy is currently available.`;
  } else if (proxyStatus === "stale") {
    summary = `${proxyKind?.replace(/_/g, " ") ?? "Proxy"} is stale and should be refreshed before editorial review.`;
  } else if (proxyStatus === "failed" || proxyStatus === "offline") {
    summary = hasDirectMediaUrl
      ? `${proxyKind?.replace(/_/g, " ") ?? "Proxy"} failed; rendering is falling back to the direct source path.`
      : `${proxyKind?.replace(/_/g, " ") ?? "Proxy"} failed and no direct source fallback is available.`;
  } else if (proxyStatus === "generating" || proxyStatus === "queued") {
    summary = `Proxy ${proxyStatus} for ${proxyKind?.replace(/_/g, " ") ?? "preferred tier"}.`;
  } else if (proxyStatus === "ready") {
    summary = usingDeprecatedProxyFallback
      ? `${proxyKind?.replace(/_/g, " ") ?? "proxy"} is ready via deprecated compatibility metadata.`
      : `${proxyKind?.replace(/_/g, " ") ?? "proxy"} is ready.`;
  } else if (!hasStructuredProxyCandidate && hasDirectMediaUrl) {
    summary = "No structured proxy is registered; rendering is currently bound to the direct source path.";
  } else if (analysisState && analysisState !== "ready") {
    summary = `Media analysis is ${analysisState}.`;
  } else if (growingFile) {
    summary = "Source is still growing; timing and proxy coverage may change.";
  } else if (typeof metadataRichnessScore === "number" && metadataRichnessScore < 0.65) {
    summary = "Metadata is partial; lane diagnostics are running with reduced confidence.";
  } else if (typeof decodePerformanceScore === "number" && decodePerformanceScore < 0.5) {
    summary = "Decode performance is below the preferred threshold.";
  }

  return {
    preferredProxy,
    usingDeprecatedProxyFallback,
    offlineState,
    proxyStatus,
    proxyKind,
    proxySource,
    proxyContractState,
    hasStructuredProxyCandidate,
    hasDirectMediaUrl,
    analysisState,
    growingFile,
    metadataRichnessScore,
    decodePerformanceScore,
    summary,
  };
}

export function buildInitialProductLaneModel(args: {
  playbackProjection: PlaybackProjection;
  emotionalIntentSegments: EmotionalIntentSegment[];
  fxIntentNodes: FxIntentNode[];
  continuityDiagnostics: ContinuityDiagnostic[];
  derivedContinuityWarningCount: number;
  npcRuntime: Record<string, unknown> | null;
  atomicRuns?: AtomicRunLaneInput[];
  selectedAtomicRunId?: string | null;
  clipMediaMetadataByClipId: Record<string, ClipMediaMetadata>;
  tracks: Array<{ id?: string | null; kind?: string | null; rangeAnchor?: any }>;
  ranges: Array<{ id?: string | null; startSec?: number; endSec?: number; label?: string | null; kind?: string | null }>;
}): InitialProductLaneModel {
  const {
    playbackProjection,
    emotionalIntentSegments,
    fxIntentNodes,
    continuityDiagnostics,
    derivedContinuityWarningCount,
    npcRuntime,
    atomicRuns = [],
    selectedAtomicRunId = null,
    clipMediaMetadataByClipId,
    tracks,
    ranges,
  } = args;

  const activeVideoMetadata = playbackProjection.activeVideoClipId ? clipMediaMetadataByClipId[playbackProjection.activeVideoClipId] ?? null : null;
  const activeAudioMetadata = playbackProjection.activeAudioClipId ? clipMediaMetadataByClipId[playbackProjection.activeAudioClipId] ?? null : null;
  const videoMediaState = describeClipMediaState(activeVideoMetadata, ["timeline_proxy", "scrub_proxy", "preview_proxy"]);
  const audioMediaState = describeClipMediaState(activeAudioMetadata, ["waveform_proxy", "scrub_proxy", "preview_proxy"]);
  const npcAnchor = resolveTrackAnchorRange(tracks, ranges, "npc");
  const npcRuntimeProbs = npcRuntime?.probs && typeof npcRuntime.probs === "object"
    ? (npcRuntime.probs as Record<string, unknown>)
    : null;
  const npcThresholds = collectNpcThresholds(npcRuntimeProbs);
  const npcRuntimeTimeSec = finiteNum((npcRuntime as any)?.playheadSec)
    ? Number((npcRuntime as any).playheadSec)
    : finiteNum((npcRuntime as any)?.timeSec)
      ? Number((npcRuntime as any).timeSec)
      : null;

  const hasVideoProjection = playbackProjection.activeVideo.length > 0;
  const hasAudioProjection = playbackProjection.activeAudio.length > 0;
  const hasAudioAnalysisProjection = hasAudioProjection;
  const hasNpcLogicProjection = !!npcRuntime;
  const hasHapticProjection = fxIntentNodes.length > 0;
  const hasEmotionProjection = emotionalIntentSegments.length > 0;
  const hasContinuityProjection = continuityDiagnostics.length > 0;

  const derivedProjections = markStaleProjections([
    ...fxIntentNodes.map<LaneProjection>((node) => ({
      id: `projection.haptic:${node.id}`,
      laneId: PROJECTION_HAPTIC_LANE_ID,
      kind: "haptic_prediction",
      status: "fresh",
      confidence: safe01(node.strength, 0.5),
      sourceRefs: [{ sourceType: "fx_intent_node", sourceId: node.id }],
    })),
    ...emotionalIntentSegments.map<LaneProjection>((segment) => ({
      id: `projection.emotion:${segment.id}`,
      laneId: PROJECTION_EMOTION_LANE_ID,
      kind: "emotion_inference",
      status: "fresh",
      confidence: safe01(segment.intensity, 0.5),
      sourceRefs: [{ sourceType: "emotional_intent_segment", sourceId: segment.id }],
    })),
    ...continuityDiagnostics.map<LaneProjection>((diagnostic) => ({
      id: `projection.continuity:${diagnostic.id}`,
      laneId: PROJECTION_CONTINUITY_LANE_ID,
      kind: "continuity_warning",
      status: diagnostic.status === "coherent" ? "fresh" : "review_required",
      confidence: safe01(diagnostic.continuityScore, 0.5),
      sourceRefs: [{ sourceType: "continuity_diagnostic", sourceId: diagnostic.id }],
    })),
  ], 45_000);

  const hapticProjectionSummary = summarizeProjectionHealth(
    derivedProjections.filter((projection) => projection.laneId === PROJECTION_HAPTIC_LANE_ID),
  );
  const emotionProjectionSummary = summarizeProjectionHealth(
    derivedProjections.filter((projection) => projection.laneId === PROJECTION_EMOTION_LANE_ID),
  );
  const continuityProjectionSummary = summarizeProjectionHealth(
    derivedProjections.filter((projection) => projection.laneId === PROJECTION_CONTINUITY_LANE_ID),
  );

  const videoHealth: LaneSyncBadge["health"] = hasVideoProjection ? "aligned" : "needs_review";
  const audioHealth: LaneSyncBadge["health"] = hasAudioProjection ? "aligned" : "needs_review";
  const audioAnalysisHealth: LaneSyncBadge["health"] = hasAudioAnalysisProjection ? audioAnalysisBadgeHealth(audioMediaState) : "needs_review";
  const npcLogicHealth: LaneSyncBadge["health"] = npcRuntimeBadgeHealth(npcRuntime, npcRuntimeTimeSec);
  const hapticHealth: LaneSyncBadge["health"] = hasHapticProjection ? "aligned" : "needs_review";
  const emotionHealth: LaneSyncBadge["health"] = hasEmotionProjection ? "aligned" : "needs_review";
  const continuityHealth = continuityBadgeHealth(continuityDiagnostics);
  const audioAnchor = resolveTrackAnchorRange(tracks, ranges, "audio");
  const continuityAnchor = resolveTrackAnchorRange(tracks, ranges, "video");

  const laneDefinitions: LaneDefinition[] = [
    {
      id: VIDEO_PRIMARY_LANE_ID,
      class: "segment",
      productType: "video_primary",
      authority: "read_only_derived",
      title: "Video Primary",
      order: 100,
      visibility: hasVideoProjection ? "expanded" : "summary_only",
      heightPx: 72,
      minHeightPx: 56,
      maxHeightPx: 96,
      dependencyMode: "independent",
      syncHealth: laneHealthFromDependencyHealth(videoHealth),
      selectable: false,
      editable: false,
      chipLabel: playbackProjection.activeVideoMemberId ?? playbackProjection.activeVideoClipId ?? null,
      metadata: {
        activeClipId: playbackProjection.activeVideoClipId ?? null,
        activeMemberId: playbackProjection.activeVideoMemberId ?? null,
        bindingMode: playbackProjection.bindingMode ?? null,
        anchoredRangeId: null,
        anchoredRangeLabel: null,
        offlineState: videoMediaState.offlineState ?? null,
        proxyStatus: videoMediaState.proxyStatus ?? null,
        preferredProxyKind: videoMediaState.proxyKind ?? null,
        proxySource: videoMediaState.proxySource,
        proxyContractState: videoMediaState.proxyContractState,
        analysisState: videoMediaState.analysisState ?? null,
        growingFile: videoMediaState.growingFile ?? null,
        metadataRichnessScore: videoMediaState.metadataRichnessScore ?? null,
        decodePerformanceScore: videoMediaState.decodePerformanceScore ?? null,
        usingDeprecatedProxyFallback: videoMediaState.usingDeprecatedProxyFallback,
      },
    },
    {
      id: AUDIO_PRIMARY_LANE_ID,
      class: "segment",
      productType: "audio_primary",
      authority: "read_only_derived",
      title: "Audio Primary",
      order: 110,
      visibility: hasAudioProjection ? "expanded" : "summary_only",
      heightPx: 68,
      minHeightPx: 56,
      maxHeightPx: 92,
      dependencyMode: "independent",
      syncHealth: laneHealthFromDependencyHealth(audioHealth),
      selectable: false,
      editable: false,
      chipLabel: audioAnchor?.label ?? playbackProjection.activeAudioMemberId ?? playbackProjection.activeAudioClipId ?? null,
      metadata: {
        activeClipId: playbackProjection.activeAudioClipId ?? null,
        activeMemberId: playbackProjection.activeAudioMemberId ?? null,
        bindingMode: playbackProjection.bindingMode ?? null,
        anchoredRangeId: audioAnchor?.rangeId ?? null,
        anchoredRangeLabel: audioAnchor?.label ?? null,
        offlineState: audioMediaState.offlineState ?? null,
        proxyStatus: audioMediaState.proxyStatus ?? null,
        preferredProxyKind: audioMediaState.proxyKind ?? null,
        proxySource: audioMediaState.proxySource,
        proxyContractState: audioMediaState.proxyContractState,
        analysisState: audioMediaState.analysisState ?? null,
        growingFile: audioMediaState.growingFile ?? null,
        metadataRichnessScore: audioMediaState.metadataRichnessScore ?? null,
        decodePerformanceScore: audioMediaState.decodePerformanceScore ?? null,
        usingDeprecatedProxyFallback: audioMediaState.usingDeprecatedProxyFallback,
      },
    },
    {
      id: CAPSULE_TEMPORAL_LANE_ID,
      class: "capsule",
      productType: "capsule_temporal",
      authority: "container_only",
      title: "Timeline Capsule",
      order: 120,
      visibility: hasAudioAnalysisProjection || hasNpcLogicProjection || hasHapticProjection || hasEmotionProjection || hasContinuityProjection ? "summary_only" : "hidden",
      heightPx: 56,
      minHeightPx: 52,
      maxHeightPx: 72,
      dependencyMode: "derived_from_source",
      syncHealth: "aligned",
      selectable: false,
      editable: false,
      chipLabel: "Derived lane cluster",
      childLaneIds: [AUDIO_ANALYSIS_LANE_ID, NPC_LOGIC_LANE_ID, PROJECTION_HAPTIC_LANE_ID, PROJECTION_EMOTION_LANE_ID, PROJECTION_CONTINUITY_LANE_ID],
      metadata: {
        capsuleId: ACTIVE_TIMELINE_CAPSULE_ID,
      },
    },
    {
      id: AUDIO_ANALYSIS_LANE_ID,
      class: "signal",
      productType: "audio_analysis",
      authority: "read_only_derived",
      title: "Audio Analysis",
      order: 120,
      visibility: hasAudioAnalysisProjection ? "expanded" : "summary_only",
      heightPx: 56,
      minHeightPx: 48,
      maxHeightPx: 76,
      dependencyMode: "derived_from_source",
      syncHealth: laneHealthFromDependencyHealth(audioAnalysisHealth),
      selectable: false,
      editable: false,
      chipLabel: audioMediaState.analysisState ?? audioAnchor?.label ?? (hasAudioAnalysisProjection ? "Derived" : "Awaiting audio"),
      sampleResolution: "coarse",
      metadata: {
        activeClipId: playbackProjection.activeAudioClipId ?? null,
        activeMemberId: playbackProjection.activeAudioMemberId ?? null,
        anchoredRangeId: audioAnchor?.rangeId ?? null,
        anchoredRangeLabel: audioAnchor?.label ?? null,
        analysisState: audioMediaState.analysisState ?? null,
        proxyStatus: audioMediaState.proxyStatus ?? null,
        preferredProxyKind: audioMediaState.proxyKind ?? null,
        proxySource: audioMediaState.proxySource,
        proxyContractState: audioMediaState.proxyContractState,
        offlineState: audioMediaState.offlineState ?? null,
        metadataRichnessScore: audioMediaState.metadataRichnessScore ?? null,
        decodePerformanceScore: audioMediaState.decodePerformanceScore ?? null,
        channelLayout: activeAudioMetadata?.channelLayout ?? null,
        sampleRate: activeAudioMetadata?.sampleRate ?? null,
      },
    },
    {
      id: NPC_LOGIC_LANE_ID,
      class: "event",
      productType: "npc_logic",
      authority: "read_only_derived",
      title: "NPC Logic",
      order: 130,
      visibility: hasNpcLogicProjection ? "expanded" : "summary_only",
      heightPx: 56,
      minHeightPx: 48,
      maxHeightPx: 80,
      dependencyMode: "derived_from_source",
      syncHealth: laneHealthFromDependencyHealth(npcLogicHealth),
      selectable: false,
      editable: false,
      chipLabel: npcAnchor?.label ?? (typeof npcRuntime?.mode === "string" ? npcRuntime.mode : hasNpcLogicProjection ? "runtime" : "Awaiting NPC"),
      metadata: {
        mode: typeof npcRuntime?.mode === "string" ? npcRuntime.mode : null,
        thresholdCount: npcThresholds.length,
        thresholds: npcThresholds,
        anchoredRangeId: npcAnchor?.rangeId ?? null,
        anchoredRangeLabel: npcAnchor?.label ?? null,
      },
    },
    {
      id: PROJECTION_HAPTIC_LANE_ID,
      class: "segment",
      productType: "projection_haptic",
      authority: "read_only_derived",
      title: "Projection Haptic",
      order: 190,
      visibility: hasHapticProjection ? "expanded" : "summary_only",
      heightPx: 56,
      minHeightPx: 48,
      maxHeightPx: 80,
      dependencyMode: "derived_from_source",
      syncHealth: laneHealthFromDependencyHealth(hapticHealth),
      selectable: false,
      editable: false,
      chipLabel: hapticProjectionSummary.byStatus.stale > 0
        ? `${hapticProjectionSummary.byStatus.stale} stale`
        : hasHapticProjection
          ? `${fxIntentNodes.length} pulse${fxIntentNodes.length === 1 ? "" : "s"}`
          : "No haptics",
      metadata: {
        authoredFxNodeCount: fxIntentNodes.length,
        projectionHealth: hapticProjectionSummary,
        anchoredRangeId: audioAnchor?.rangeId ?? null,
        anchoredRangeLabel: audioAnchor?.label ?? null,
      },
    },
    {
      id: PROJECTION_EMOTION_LANE_ID,
      class: "segment",
      productType: "projection_emotion",
      authority: "projection_promotable",
      title: "Projection Emotion",
      order: 200,
      visibility: hasEmotionProjection ? "expanded" : "summary_only",
      heightPx: 64,
      minHeightPx: 52,
      maxHeightPx: 88,
      dependencyMode: "derived_from_source",
      syncHealth: laneHealthFromDependencyHealth(emotionHealth),
      selectable: false,
      editable: false,
      chipLabel: emotionProjectionSummary.byStatus.stale > 0
        ? `${emotionProjectionSummary.byStatus.stale} stale`
        : hasEmotionProjection
          ? `${emotionalIntentSegments.length} segments`
          : "No arc intent",
      metadata: {
        authoredArcSegmentCount: emotionalIntentSegments.length,
        projectionHealth: emotionProjectionSummary,
      },
    },
    {
      id: PROJECTION_CONTINUITY_LANE_ID,
      class: "overlay",
      productType: "projection_continuity",
      authority: "read_only_derived",
      title: "Projection Continuity",
      order: 210,
      visibility: hasContinuityProjection ? "expanded" : "summary_only",
      heightPx: 60,
      minHeightPx: 52,
      maxHeightPx: 84,
      dependencyMode: "derived_from_source",
      syncHealth: laneHealthFromDependencyHealth(continuityHealth),
      selectable: false,
      editable: false,
      chipLabel: continuityAnchor?.label ?? (hasContinuityProjection ? `${derivedContinuityWarningCount} warnings` : "No diagnostics"),
      metadata: {
        warningCount: derivedContinuityWarningCount,
        diagnosticCount: continuityDiagnostics.length,
        projectionHealth: continuityProjectionSummary,
        anchoredRangeId: continuityAnchor?.rangeId ?? null,
        anchoredRangeLabel: continuityAnchor?.label ?? null,
      },
    },
  ];

  const laneItemsByLaneId: Record<string, LaneItem[]> = {
    [VIDEO_PRIMARY_LANE_ID]: playbackProjection.activeVideo.map((entry: ResolvedVideoPlayback, index: number) => {
      const clipMetadata = clipMediaMetadataByClipId[entry.clipId] ?? null;
      const clipMediaState = describeClipMediaState(clipMetadata, ["timeline_proxy", "scrub_proxy", "preview_proxy"]);
      const mediaUrl = clipMediaState.preferredProxy?.uri ?? clipMetadata?.mediaUrl ?? null;

      return {
        id: `video-primary-${entry.clipId}-${index}`,
        laneId: VIDEO_PRIMARY_LANE_ID,
        itemType: "segment",
        startSec: entry.startSec,
        endSec: entry.endSec,
        label: entry.label ?? playbackProjection.familyLabel ?? entry.clipId,
        sourceId: entry.clipId,
        sourceType: "timeline_clip",
        metadata: {
          familyId: entry.familyId,
          memberId: entry.videoMemberId,
          sourceAssetId: entry.sourceAssetId ?? null,
          proxyStatus: clipMediaState.proxyStatus ?? null,
          preferredProxyKind: clipMediaState.proxyKind ?? null,
          proxySource: clipMediaState.proxySource,
          proxyContractState: clipMediaState.proxyContractState,
          offlineState: clipMediaState.offlineState ?? null,
          analysisState: clipMediaState.analysisState ?? null,
          growingFile: clipMediaState.growingFile ?? null,
          metadataRichnessScore: clipMediaState.metadataRichnessScore ?? null,
          decodePerformanceScore: clipMediaState.decodePerformanceScore ?? null,
          usingDeprecatedProxyFallback: clipMediaState.usingDeprecatedProxyFallback,
          mediaUrl,
          displayAspectRatio: clipMetadata?.displayAspectRatio ?? null,
        },
      };
    }),
    [AUDIO_PRIMARY_LANE_ID]: playbackProjection.activeAudio.map((entry: ResolvedAudioPlayback, index: number) => {
      const clipMetadata = clipMediaMetadataByClipId[entry.clipId] ?? null;
      const clipMediaState = describeClipMediaState(clipMetadata, ["waveform_proxy", "scrub_proxy", "preview_proxy"]);
      const snappedWindow = snapWindowToAnchor(entry.startSec, entry.endSec, audioAnchor);

      return {
        id: `audio-primary-${entry.clipId}-${index}`,
        laneId: AUDIO_PRIMARY_LANE_ID,
        itemType: "segment",
        startSec: snappedWindow.startSec,
        endSec: snappedWindow.endSec,
        label: entry.label ?? playbackProjection.familyLabel ?? entry.clipId,
        sourceId: entry.clipId,
        sourceType: "timeline_clip",
        muted: entry.muted,
        metadata: {
          familyId: entry.familyId,
          memberId: entry.audioMemberId,
          sourceAssetId: entry.sourceAssetId ?? null,
          memberKind: entry.memberKind ?? null,
          gainDb: entry.gainDb ?? null,
          proxyStatus: clipMediaState.proxyStatus ?? null,
          preferredProxyKind: clipMediaState.proxyKind ?? null,
          proxySource: clipMediaState.proxySource,
          proxyContractState: clipMediaState.proxyContractState,
          offlineState: clipMediaState.offlineState ?? null,
          analysisState: clipMediaState.analysisState ?? null,
          growingFile: clipMediaState.growingFile ?? null,
          metadataRichnessScore: clipMediaState.metadataRichnessScore ?? null,
          decodePerformanceScore: clipMediaState.decodePerformanceScore ?? null,
          usingDeprecatedProxyFallback: clipMediaState.usingDeprecatedProxyFallback,
          channelLayout: clipMetadata?.channelLayout ?? null,
          sampleRate: clipMetadata?.sampleRate ?? null,
          anchoredRangeId: audioAnchor?.rangeId ?? null,
          anchoredRangeLabel: audioAnchor?.label ?? null,
        },
      };
    }),
    [AUDIO_ANALYSIS_LANE_ID]: playbackProjection.activeAudio.map((entry: ResolvedAudioPlayback, index: number) => {
      const clipMetadata = clipMediaMetadataByClipId[entry.clipId] ?? null;
      const clipMediaState = describeClipMediaState(clipMetadata, ["waveform_proxy", "scrub_proxy", "preview_proxy"]);
      const snappedWindow = snapWindowToAnchor(entry.startSec, entry.endSec, audioAnchor);
      const proxyConfidence = audioProxyConfidenceScore(clipMediaState.proxyStatus, clipMediaState.usingDeprecatedProxyFallback);
      const metadataConfidence = safe01(clipMediaState.metadataRichnessScore, 0.55);
      const decodeConfidence = safe01(clipMediaState.decodePerformanceScore, 0.65);
      const analysisScore = clipMediaState.analysisState === "ready"
        ? 0.95
        : clipMediaState.analysisState === "queued"
          ? 0.62
          : clipMediaState.analysisState === "failed"
            ? 0.22
            : 0.48;

      return {
        id: `audio-analysis-${entry.clipId}-${index}`,
        laneId: AUDIO_ANALYSIS_LANE_ID,
        itemType: "signal",
        startSec: snappedWindow.startSec,
        endSec: snappedWindow.endSec,
        sourceId: entry.clipId,
        sourceType: "timeline_clip",
        metadata: {
          analysisState: clipMediaState.analysisState ?? null,
          proxyStatus: clipMediaState.proxyStatus ?? null,
          preferredProxyKind: clipMediaState.proxyKind ?? null,
          proxySource: clipMediaState.proxySource,
          proxyContractState: clipMediaState.proxyContractState,
          offlineState: clipMediaState.offlineState ?? null,
          channelLayout: clipMetadata?.channelLayout ?? null,
          sampleRate: clipMetadata?.sampleRate ?? null,
          confidence: analysisScore,
          metadataRichnessScore: clipMediaState.metadataRichnessScore ?? null,
          decodePerformanceScore: clipMediaState.decodePerformanceScore ?? null,
          anchoredRangeId: audioAnchor?.rangeId ?? null,
          anchoredRangeLabel: audioAnchor?.label ?? null,
        },
        bands: [
          {
            key: "analysis",
            points: [
              { tSec: snappedWindow.startSec, value: analysisScore },
              { tSec: snappedWindow.endSec, value: analysisScore },
            ],
          },
          {
            key: "proxy",
            points: [
              { tSec: snappedWindow.startSec, value: proxyConfidence },
              { tSec: snappedWindow.endSec, value: proxyConfidence },
            ],
          },
          {
            key: "decode",
            points: [
              { tSec: snappedWindow.startSec, value: Math.min(metadataConfidence, decodeConfidence) },
              { tSec: snappedWindow.endSec, value: Math.min(metadataConfidence, decodeConfidence) },
            ],
          },
        ],
      };
    }),
    [NPC_LOGIC_LANE_ID]: npcRuntime && npcRuntimeTimeSec != null ? [(() => {
      const startSec = npcAnchor
        ? Math.max(npcAnchor.startSec, npcRuntimeTimeSec - 0.35)
        : Math.max(0, npcRuntimeTimeSec - 0.35);
      const endSec = npcAnchor
        ? Math.min(npcAnchor.endSec, npcRuntimeTimeSec + 0.35)
        : npcRuntimeTimeSec + 0.35;
      const mode = typeof npcRuntime.mode === "string" ? npcRuntime.mode : "runtime";
      const dominantScore = npcRuntimeDominantScore(npcRuntime);

      return {
        id: `npc-logic-${Math.round(npcRuntimeTimeSec * 10)}`,
        laneId: NPC_LOGIC_LANE_ID,
        itemType: "event",
        startSec,
        endSec,
        eventKey: mode,
        strength: dominantScore,
        sourceId: "npc-runtime",
        sourceType: "npc_runtime",
        metadata: {
          actor: "runtime",
          mode,
          status: npcThresholds.length > 0 ? npcThresholds.join(", ") : "steady",
          strength: dominantScore,
          attention: safe01(npcRuntimeProbs?.attention, 0),
          emotion: safe01(npcRuntimeProbs?.emotion, 0),
          overload: safe01(npcRuntimeProbs?.overload, 0),
          safety: safe01(npcRuntimeProbs?.safety, 1),
          anchoredRangeId: npcAnchor?.rangeId ?? null,
          anchoredRangeLabel: npcAnchor?.label ?? null,
        },
      };
    })()] : [],
    [PROJECTION_HAPTIC_LANE_ID]: fxIntentNodes.map((node) => ({
      id: node.id,
      laneId: PROJECTION_HAPTIC_LANE_ID,
      itemType: "segment",
      startSec: audioAnchor ? Math.max(audioAnchor.startSec, node.startSec) : node.startSec,
      endSec: audioAnchor ? Math.min(audioAnchor.endSec, node.endSec) : node.endSec,
      label: `${node.type} pulse`,
      sourceId: node.id,
      sourceType: "fx_intent_node",
      metadata: {
        pattern: node.type,
        intensity: node.strength,
        focusScope: node.focusScope ?? null,
        focusClipId: node.focusClipId ?? null,
        focusSourceId: node.focusSourceId ?? null,
        rationale: node.rationale ?? null,
        anchoredRangeId: audioAnchor?.rangeId ?? null,
        anchoredRangeLabel: audioAnchor?.label ?? null,
      },
    })),
    [PROJECTION_EMOTION_LANE_ID]: emotionalIntentSegments.map((segment) => ({
      id: segment.id,
      laneId: PROJECTION_EMOTION_LANE_ID,
      itemType: "segment",
      startSec: segment.startSec,
      endSec: segment.endSec,
      label: `V ${segment.valence.toFixed(2)} I ${segment.intensity.toFixed(2)} U ${segment.urgency.toFixed(2)}`,
      sourceId: segment.focusClipId ?? segment.focusSourceId ?? segment.id,
      sourceType: "emotional_intent_segment",
      metadata: {
        focusScope: segment.focusScope,
        authoredBy: segment.authoredBy,
        valence: segment.valence,
        intensity: segment.intensity,
        urgency: segment.urgency,
      },
    })),
    [PROJECTION_CONTINUITY_LANE_ID]: continuityDiagnostics.map((diagnostic) => ({
      ...(function () {
        const snappedWindow = snapWindowToAnchor(diagnostic.startSec, diagnostic.endSec, continuityAnchor);
        return {
      id: diagnostic.id,
      laneId: PROJECTION_CONTINUITY_LANE_ID,
      itemType: "overlay",
      startSec: snappedWindow.startSec,
      endSec: snappedWindow.endSec,
      overlayKey: diagnostic.status,
      confidence: diagnostic.continuityScore,
      severity: continuitySeverity(diagnostic.status),
      sourceId: diagnostic.id,
      sourceType: "continuity_diagnostic",
      metadata: {
        warning: diagnostic.warning ?? null,
        continuityScore: diagnostic.continuityScore,
        relatedArcIds: diagnostic.relatedArcIds ?? [],
        relatedFxNodeIds: diagnostic.relatedFxNodeIds ?? [],
        anchoredRangeId: continuityAnchor?.rangeId ?? null,
        anchoredRangeLabel: continuityAnchor?.label ?? null,
      },
        };
      })()
    })),
  };

  const capsuleChildLaneIds = laneDefinitions
    .filter((lane) => lane.id !== CAPSULE_TEMPORAL_LANE_ID && lane.id !== VIDEO_PRIMARY_LANE_ID && lane.id !== AUDIO_PRIMARY_LANE_ID)
    .map((lane) => lane.id);

  const dependencyRelations: LaneDependencyRelation[] = [
    ...laneDefinitions
      .filter((lane) => capsuleChildLaneIds.includes(lane.id))
      .map((lane) => ({
        id: `dep.${lane.id}.capsule_temporal`,
        fromLaneId: lane.id,
        toLaneId: CAPSULE_TEMPORAL_LANE_ID,
        relationType: "bounds_child_of" as const,
        health: dependencyHealthFromLaneSyncHealth(lane.syncHealth),
        details: `${lane.title} remains derived under the active timeline capsule and must invalidate when its parent cluster health changes.`,
        metadata: {
          capsuleId: ACTIVE_TIMELINE_CAPSULE_ID,
          childLaneId: lane.id,
          childLaneTitle: lane.title,
          couplingId: `coupling.${lane.id.replace(/^lane\./, "lane.")}.capsule_temporal`,
          couplingKind: "capsule_membership",
          itemCount: laneItemsByLaneId[lane.id]?.length ?? 0,
          laneMetadata: lane.metadata ?? {},
        },
      })),
    {
      id: "dep.npc_logic.runtime_source",
      fromLaneId: NPC_LOGIC_LANE_ID,
      toLaneId: NPC_LOGIC_LANE_ID,
      relationType: "derived_from",
      health: npcLogicHealth,
      details: hasNpcLogicProjection
        ? npcAnchor
          ? `NPC logic is constrained by anchored NPC range ${npcAnchor.label}.`
          : "NPC logic is derived from the runtime feed without separate editorial authority."
        : "No NPC runtime feed to project yet.",
      metadata: {
        couplingId: "coupling.npc.runtime",
        couplingKind: "npc_runtime_input",
      },
    },
    {
      id: "dep.projection_haptic.audio_primary",
      fromLaneId: PROJECTION_HAPTIC_LANE_ID,
      toLaneId: AUDIO_PRIMARY_LANE_ID,
      relationType: "derived_from",
      health: hapticHealth,
      details: hasHapticProjection
        ? audioAnchor
          ? `Projection haptics are evaluated against anchored audio range ${audioAnchor.label}.`
          : "Projection haptics are derived from authored FX intent over the audio context."
        : "No authored FX intent to project as haptics yet.",
      metadata: {
        couplingId: "coupling.audio.haptic",
        couplingKind: "projection_authoring_input",
      },
    },
    {
      id: "dep.audio_analysis.audio_primary",
      fromLaneId: AUDIO_ANALYSIS_LANE_ID,
      toLaneId: AUDIO_PRIMARY_LANE_ID,
      relationType: "derived_from",
      health: audioAnalysisHealth,
      details: hasAudioAnalysisProjection
        ? audioAnchor
          ? `Audio analysis is derived from the anchored audio range ${audioAnchor.label}.`
          : "Audio analysis is derived from active audio projection windows and clip media metadata."
        : "No active audio projection to analyze yet.",
      metadata: {
        couplingId: "coupling.audio.analysis",
        couplingKind: "audio_analysis_input",
      },
    },
    {
      id: "dep.projection_emotion.audio_primary",
      fromLaneId: PROJECTION_EMOTION_LANE_ID,
      toLaneId: AUDIO_PRIMARY_LANE_ID,
      relationType: "derived_from",
      health: emotionHealth,
      details: hasEmotionProjection
        ? audioAnchor
          ? `Emotion projection is evaluated against anchored audio range ${audioAnchor.label}.`
          : "Emotion projection is derived from authored sonics intent over the active audio context."
        : "No authored sonics intent to project yet.",
      metadata: {
        couplingId: "coupling.audio.emotion",
        couplingKind: "projection_authoring_input",
      },
    },
    {
      id: "dep.projection_continuity.projection_emotion",
      fromLaneId: PROJECTION_CONTINUITY_LANE_ID,
      toLaneId: PROJECTION_EMOTION_LANE_ID,
      relationType: "derived_from",
      health: continuityHealth,
      details: hasContinuityProjection
        ? continuityAnchor
          ? `Continuity diagnostics are bounded by anchored range ${continuityAnchor.label}.`
          : "Continuity diagnostics are derived from authored emotion and FX continuity analysis."
        : "No continuity diagnostics to relate yet.",
      metadata: {
        couplingId: "coupling.projection.continuity",
        couplingKind: "continuity_projection_input",
      },
    },
  ];

  const syncBadges: LaneSyncBadge[] = deriveLaneSyncBadges(laneDefinitions, dependencyRelations).map((derivedBadge) => {
    switch (derivedBadge.laneId) {
      case VIDEO_PRIMARY_LANE_ID:
        return {
          ...derivedBadge,
          label: hasVideoProjection ? `${playbackProjection.activeVideo.length} active span${playbackProjection.activeVideo.length === 1 ? "" : "s"}` : "No active video span",
          reason: hasVideoProjection
            ? videoMediaState.summary ?? playbackProjection.resolvedMemberSummary ?? null
            : videoMediaState.summary ?? "Playback projection has no active video range.",
        } satisfies LaneSyncBadge;
      case AUDIO_PRIMARY_LANE_ID:
        return {
          ...derivedBadge,
          label: audioAnchor?.label ?? (hasAudioProjection ? `${playbackProjection.activeAudio.length} active span${playbackProjection.activeAudio.length === 1 ? "" : "s"}` : "No active audio span"),
          reason: hasAudioProjection
            ? audioAnchor
              ? `Snapped to anchored range ${audioAnchor.label}. ${audioMediaState.summary ?? playbackProjection.resolvedMemberSummary ?? ""}`.trim()
              : audioMediaState.summary ?? playbackProjection.resolvedMemberSummary ?? null
            : audioAnchor
              ? `Audio lane is anchored to ${audioAnchor.label}.`
              : audioMediaState.summary ?? "Playback projection has no active audio range.",
        } satisfies LaneSyncBadge;
      case AUDIO_ANALYSIS_LANE_ID:
        return {
          ...derivedBadge,
          label: audioMediaState.analysisState
            ? `Analysis ${audioMediaState.analysisState}`
            : audioAnchor?.label ?? (hasAudioAnalysisProjection ? "Analysis pending" : "No analysis window"),
          reason: hasAudioAnalysisProjection
            ? audioAnchor
              ? `Audio analysis is snapped to anchored range ${audioAnchor.label}. ${audioMediaState.summary ?? ""}`.trim()
              : audioMediaState.summary ?? "Derived audio diagnostics are built from the active audio projection and media metadata."
            : audioAnchor
              ? `Audio analysis will bind to ${audioAnchor.label} once an audio projection is active.`
              : "No active audio projection is available for analysis yet.",
        } satisfies LaneSyncBadge;
      case NPC_LOGIC_LANE_ID:
        return {
          ...derivedBadge,
          label: npcThresholds.length > 0
            ? `NPC ${npcThresholds.join(", ")}`
            : typeof npcRuntime?.mode === "string"
              ? `NPC ${npcRuntime.mode}`
              : npcAnchor?.label ?? (hasNpcLogicProjection ? "NPC runtime" : "No NPC runtime"),
          reason: hasNpcLogicProjection
            ? npcAnchor
              ? `NPC logic is derived from runtime state within anchored range ${npcAnchor.label}.`
              : "NPC logic is derived from the active NPC runtime feed."
            : npcAnchor
              ? `NPC logic will bind to ${npcAnchor.label} once runtime data is available.`
              : "No NPC runtime feed is available yet.",
        } satisfies LaneSyncBadge;
      case PROJECTION_HAPTIC_LANE_ID:
        return {
          ...derivedBadge,
          label: hapticProjectionSummary.byStatus.stale > 0
            ? `${hapticProjectionSummary.byStatus.stale} stale haptic projection${hapticProjectionSummary.byStatus.stale === 1 ? "" : "s"}`
            : hasHapticProjection
              ? `${fxIntentNodes.length} haptic pulse${fxIntentNodes.length === 1 ? "" : "s"}`
              : audioAnchor?.label ?? "No haptics",
          reason: hasHapticProjection
            ? audioAnchor
              ? `Projection haptics are derived from authored FX nodes within anchored audio range ${audioAnchor.label}.`
              : "Projection haptics are derived from authored FX intent nodes."
            : audioAnchor
              ? `Projection haptics will bind to ${audioAnchor.label} once FX intent is authored.`
              : "No authored FX intent is available for haptic projection yet.",
        } satisfies LaneSyncBadge;
      case PROJECTION_EMOTION_LANE_ID:
        return {
          ...derivedBadge,
          label: emotionProjectionSummary.byStatus.stale > 0
            ? `${emotionProjectionSummary.byStatus.stale} stale emotion projection${emotionProjectionSummary.byStatus.stale === 1 ? "" : "s"}`
            : hasEmotionProjection
              ? `${emotionalIntentSegments.length} authored segment${emotionalIntentSegments.length === 1 ? "" : "s"}`
              : "No emotion projection",
          reason: hasEmotionProjection ? "Derived from authored arc intent segments." : "No authored emotional intent segments are present.",
        } satisfies LaneSyncBadge;
      case PROJECTION_CONTINUITY_LANE_ID:
        return {
          ...derivedBadge,
          label: continuityProjectionSummary.byStatus.stale > 0
            ? `${continuityProjectionSummary.byStatus.stale} stale continuity projection${continuityProjectionSummary.byStatus.stale === 1 ? "" : "s"}`
            : continuityAnchor?.label ?? (hasContinuityProjection ? `${continuityDiagnostics.length} diagnostic${continuityDiagnostics.length === 1 ? "" : "s"}` : "No continuity diagnostics"),
          reason: hasContinuityProjection
            ? continuityAnchor
              ? `Continuity overlays are constrained to ${continuityAnchor.label}.`
              : derivedContinuityWarningCount > 0
                ? `${derivedContinuityWarningCount} diagnostic warning${derivedContinuityWarningCount === 1 ? "" : "s"} require review.`
                : "Derived continuity diagnostics are coherent."
            : continuityAnchor
              ? `Continuity lane is anchored to ${continuityAnchor.label}.`
              : "No continuity diagnostics are available yet.",
        } satisfies LaneSyncBadge;
      case CAPSULE_TEMPORAL_LANE_ID:
        return {
          ...derivedBadge,
          label: "Derived lane cluster",
          reason: "Aggregates the active derived lane set without taking editorial authority away from TimelineShell.",
        } satisfies LaneSyncBadge;
      default:
        return derivedBadge;
    }
  });

  const capsuleStartSec = Math.min(
    ...Object.values(laneItemsByLaneId)
      .flat()
      .filter((item) => item.laneId !== VIDEO_PRIMARY_LANE_ID && item.laneId !== AUDIO_PRIMARY_LANE_ID)
      .map((item) => item.startSec),
  );
  const capsuleEndSec = Math.max(
    ...Object.values(laneItemsByLaneId)
      .flat()
      .filter((item) => item.laneId !== VIDEO_PRIMARY_LANE_ID && item.laneId !== AUDIO_PRIMARY_LANE_ID)
      .map((item) => item.endSec),
  );
  const capsuleLaneIds = capsuleChildLaneIds;
  const capsuleChildRelations = dependencyRelations.filter((relation) => relation.toLaneId === CAPSULE_TEMPORAL_LANE_ID);
  const capsuleDependencyHealth = aggregateDependencyHealth(capsuleChildRelations.map((relation) => relation.health));
  const capsuleInvalidationCount = capsuleChildRelations.filter((relation) => relation.health !== "aligned" && relation.health !== "needs_review").length;
  const capsuleAwaitingCount = capsuleChildRelations.filter((relation) => relation.health === "needs_review").length;
  const overlappingAtomicRuns = atomicRuns.filter((run) => (run.startSec + Math.max(0, run.durSec)) > capsuleStartSec && run.startSec < capsuleEndSec);
  const selectedAtomicRun = overlappingAtomicRuns.find((run) => run.id === selectedAtomicRunId) ?? null;
  const capsuleChildHealthCounts = capsuleChildRelations.reduce<Record<string, number>>((counts, relation) => {
    counts[relation.health] = (counts[relation.health] ?? 0) + 1;
    return counts;
  }, {});
  const crossLaneCouplings: TimelineCrossLaneCoupling[] = [
    {
      id: "coupling.audio.analysis",
      sourceId: AUDIO_PRIMARY_LANE_ID,
      targetId: AUDIO_ANALYSIS_LANE_ID,
      kind: "audio_analysis_input",
      state: hasAudioAnalysisProjection ? "active" : "awaiting_input",
      details: hasAudioAnalysisProjection
        ? "Audio analysis is derived from the active audio authority path."
        : "Audio analysis awaits an active projected audio window.",
      evidence: [audioAnchor?.label ?? playbackProjection.activeAudioClipId ?? "audio_primary"],
      anchoredRangeId: audioAnchor?.rangeId ?? null,
      anchoredRangeLabel: audioAnchor?.label ?? null,
      relatedLaneIds: [AUDIO_PRIMARY_LANE_ID, AUDIO_ANALYSIS_LANE_ID],
    },
    {
      id: "coupling.audio.haptic",
      sourceId: AUDIO_PRIMARY_LANE_ID,
      targetId: PROJECTION_HAPTIC_LANE_ID,
      kind: "projection_authoring_input",
      state: hasHapticProjection ? "active" : "awaiting_input",
      details: hasHapticProjection
        ? "Haptic projection is derived from authored FX intent over audio timing."
        : "Haptic projection awaits authored FX intent.",
      evidence: [audioAnchor?.label ?? "audio context", `${fxIntentNodes.length} fx node${fxIntentNodes.length === 1 ? "" : "s"}`],
      anchoredRangeId: audioAnchor?.rangeId ?? null,
      anchoredRangeLabel: audioAnchor?.label ?? null,
      relatedLaneIds: [AUDIO_PRIMARY_LANE_ID, PROJECTION_HAPTIC_LANE_ID],
    },
    {
      id: "coupling.audio.emotion",
      sourceId: AUDIO_PRIMARY_LANE_ID,
      targetId: PROJECTION_EMOTION_LANE_ID,
      kind: "projection_authoring_input",
      state: hasEmotionProjection ? "active" : "awaiting_input",
      details: hasEmotionProjection
        ? "Emotion projection is derived from authored segments over the audio context."
        : "Emotion projection awaits authored emotional intent.",
      evidence: [audioAnchor?.label ?? "audio context", `${emotionalIntentSegments.length} authored segment${emotionalIntentSegments.length === 1 ? "" : "s"}`],
      anchoredRangeId: audioAnchor?.rangeId ?? null,
      anchoredRangeLabel: audioAnchor?.label ?? null,
      relatedLaneIds: [AUDIO_PRIMARY_LANE_ID, PROJECTION_EMOTION_LANE_ID],
    },
    {
      id: "coupling.npc.runtime",
      sourceId: "runtime.npc",
      targetId: NPC_LOGIC_LANE_ID,
      kind: "npc_runtime_input",
      state: hasNpcLogicProjection ? "active" : "awaiting_input",
      details: hasNpcLogicProjection
        ? "NPC logic is projected from reducer-backed runtime state."
        : "NPC logic awaits runtime state with a projectable time window.",
      evidence: [npcAnchor?.label ?? (typeof npcRuntime?.mode === "string" ? npcRuntime.mode : "npc runtime")],
      anchoredRangeId: npcAnchor?.rangeId ?? null,
      anchoredRangeLabel: npcAnchor?.label ?? null,
      relatedLaneIds: [NPC_LOGIC_LANE_ID],
    },
    {
      id: "coupling.projection.continuity",
      sourceId: PROJECTION_EMOTION_LANE_ID,
      targetId: PROJECTION_CONTINUITY_LANE_ID,
      kind: "continuity_projection_input",
      state: hasContinuityProjection ? "active" : "awaiting_input",
      details: hasContinuityProjection
        ? "Continuity diagnostics derive from authored projection continuity analysis."
        : "Continuity diagnostics await projection inputs.",
      evidence: [continuityAnchor?.label ?? `${continuityDiagnostics.length} diagnostic${continuityDiagnostics.length === 1 ? "" : "s"}`],
      anchoredRangeId: continuityAnchor?.rangeId ?? null,
      anchoredRangeLabel: continuityAnchor?.label ?? null,
      relatedLaneIds: [PROJECTION_EMOTION_LANE_ID, PROJECTION_CONTINUITY_LANE_ID],
    },
    ...capsuleLaneIds.map((laneId) => ({
      id: `coupling.${laneId.replace(/^lane\./, "lane.")}.capsule_temporal`,
      sourceId: laneId,
      targetId: CAPSULE_TEMPORAL_LANE_ID,
      kind: "capsule_membership" as const,
      state: (laneItemsByLaneId[laneId]?.length ?? 0) > 0 ? "active" as const : "awaiting_input" as const,
      details: `${laneDefinitions.find((lane) => lane.id === laneId)?.title ?? laneId} remains bound to the active derived capsule cluster.`,
      evidence: [`${laneItemsByLaneId[laneId]?.length ?? 0} item${(laneItemsByLaneId[laneId]?.length ?? 0) === 1 ? "" : "s"}`],
      relatedLaneIds: [laneId, CAPSULE_TEMPORAL_LANE_ID],
    })),
    {
      id: "coupling.atomic_runs.capsule_temporal",
      sourceId: "track.atomic_runs",
      targetId: CAPSULE_TEMPORAL_LANE_ID,
      kind: "atomic_run_alignment",
      state: overlappingAtomicRuns.length > 0 ? "active" : "awaiting_input",
      details: overlappingAtomicRuns.length > 0
        ? selectedAtomicRun
          ? `Selected atomic run ${selectedAtomicRun.label} overlaps the active capsule window.`
          : `${overlappingAtomicRuns.length} atomic run${overlappingAtomicRuns.length === 1 ? "" : "s"} overlap the active capsule window.`
        : "No atomic runs overlap the active capsule window yet.",
      evidence: overlappingAtomicRuns.map((run) => run.label),
      relatedLaneIds: [CAPSULE_TEMPORAL_LANE_ID],
      relatedRunIds: overlappingAtomicRuns.map((run) => run.id),
    },
  ];
  const activeCapsule: TimelineCapsule = {
    id: ACTIVE_TIMELINE_CAPSULE_ID,
    kind: "temporal_cluster",
    title: "Active Timeline Capsule",
    startSec: Number.isFinite(capsuleStartSec) ? capsuleStartSec : 0,
    endSec: Number.isFinite(capsuleEndSec) ? Math.max(Number.isFinite(capsuleStartSec) ? capsuleStartSec : 0, capsuleEndSec) : Math.max(Number.isFinite(capsuleStartSec) ? capsuleStartSec : 0, 0),
    laneIds: capsuleLaneIds,
    parentClipId: playbackProjection.focusClipId ?? playbackProjection.activeAudioClipId ?? playbackProjection.activeVideoClipId ?? null,
    collapsed: false,
    syncPolicy: "projection_recompute",
    summary: {
      syncHealth: laneHealthFromDependencyHealth(capsuleDependencyHealth),
      itemCount: capsuleLaneIds.reduce((count, laneId) => count + (laneItemsByLaneId[laneId]?.length ?? 0), 0),
      warningCount: derivedContinuityWarningCount,
      invalidationCount: capsuleInvalidationCount,
      awaitingCount: capsuleAwaitingCount,
    },
    metadata: {
      playbackFocusClipId: playbackProjection.focusClipId ?? null,
      crossLaneCouplings: crossLaneCouplings.filter((coupling) => coupling.targetId === CAPSULE_TEMPORAL_LANE_ID),
      childHealthCounts: capsuleChildHealthCounts,
      childRelations: capsuleChildRelations.map((relation) => ({
        laneId: relation.fromLaneId,
        title: relation.metadata?.childLaneTitle ?? relation.fromLaneId,
        health: relation.health,
        stateLabel: relation.health === "needs_review" && Number(relation.metadata?.itemCount ?? 0) === 0
          ? (() => {
              const laneMetadata = (relation.metadata?.laneMetadata ?? {}) as Record<string, unknown>;
              if (relation.fromLaneId === AUDIO_ANALYSIS_LANE_ID) return "awaiting audio analysis";
              if (relation.fromLaneId === NPC_LOGIC_LANE_ID) return "awaiting npc runtime";
              if (relation.fromLaneId === PROJECTION_HAPTIC_LANE_ID) {
                return Number(laneMetadata.authoredFxNodeCount ?? 0) > 0 ? "awaiting haptic projection" : "awaiting authored fx";
              }
              if (relation.fromLaneId === PROJECTION_EMOTION_LANE_ID) {
                return Number(laneMetadata.authoredArcSegmentCount ?? 0) > 0 ? "awaiting emotion projection" : "awaiting arc intent";
              }
              if (relation.fromLaneId === PROJECTION_CONTINUITY_LANE_ID) {
                return Number(laneMetadata.diagnosticCount ?? 0) > 0 ? "awaiting continuity projection" : "awaiting diagnostics";
              }
              return "awaiting input";
            })()
          : relation.health === "drift_detected"
            ? "drift detected"
            : relation.health === "partially_invalid"
              ? "partially invalid"
              : relation.health.replace(/_/g, " "),
        details: relation.details ?? null,
      })),
    },
  };
  const activeCapsuleSummary = summarizeCapsule(activeCapsule);

  const capsuleLane = laneDefinitions.find((lane) => lane.id === CAPSULE_TEMPORAL_LANE_ID);
  if (capsuleLane) {
    capsuleLane.chipLabel = `${activeCapsuleSummary.itemCount} items · ${activeCapsuleSummary.warningCount} warnings`;
    capsuleLane.syncHealth = activeCapsule.summary?.syncHealth === "aligned"
      ? "aligned"
      : activeCapsule.summary?.syncHealth === "warning"
        ? "warning"
        : activeCapsule.summary?.syncHealth === "broken"
          ? "broken"
          : "review_required";
    capsuleLane.metadata = {
      ...(capsuleLane.metadata ?? {}),
      summary: activeCapsuleSummary,
      crossLaneCouplings: crossLaneCouplings.filter((coupling) => coupling.targetId === CAPSULE_TEMPORAL_LANE_ID),
      childHealthCounts: capsuleChildHealthCounts,
      childRelations: activeCapsule.metadata?.childRelations ?? [],
    };
  }

  for (const lane of laneDefinitions) {
    lane.metadata = {
      ...(lane.metadata ?? {}),
      crossLaneCouplings: crossLaneCouplings.filter(
        (coupling) => coupling.sourceId === lane.id || coupling.targetId === lane.id,
      ),
    };
  }

  return {
    laneDefinitions,
    laneItemsByLaneId,
    syncBadges,
    dependencyRelations,
    crossLaneCouplings,
    capsules: [activeCapsule],
  };
}

export function publishInitialProductLaneModel(api: ProductLanePublishApi, model: InitialProductLaneModel): void {
  api.registerLanes(model.laneDefinitions);

  for (const lane of model.laneDefinitions) {
    api.setLaneItems(lane.id, model.laneItemsByLaneId[lane.id] ?? []);
  }

  for (const capsule of model.capsules) {
    api.upsertCapsule(capsule);
  }

  api.setSyncBadges(model.syncBadges);
  api.setDependencyRelations(model.dependencyRelations);
}

/* ------------------------------------------------------------------ */
/* State normalization                                                */
/* ------------------------------------------------------------------ */

type TrackKind = "video" | "audio" | "npc";
type Track = {
  id: string;
  name: string;
  kind: TrackKind;
  height?: number;
};

const FALLBACK_TRACKS: Track[] = [
  { id: "v1", name: "Video", kind: "video", height: 72 },
  { id: "a1", name: "Audio", kind: "audio", height: 72 },
  { id: "n1", name: "NPC", kind: "npc", height: 72 },
];

function normalizeTimelineState(st: unknown) {
  const s = st as any;

  const durationSec = finiteNum(s?.durationSec) ? Number(s.durationSec) : 120;
  const playheadSec = finiteNum(s?.playheadSec) ? Number(s.playheadSec) : 0;

  const pxPerSec = readPxPerSec(s);
  const viewportStartSec = readViewportStartSec(s);
  const viewportEndSec = readViewportEndSec(s, viewportStartSec, durationSec);

  const markers = Array.isArray(s?.markers) ? s.markers : [];
  const ranges = Array.isArray(s?.ranges) ? s.ranges : [];
  const clips = Array.isArray(s?.clips) ? s.clips : [];

  const snap =
    s?.snap ??
    ({
      enabled: false,
      mode: "second",
      fps: 30,
    } as const);

  const selectedRangeId = s?.selectedRangeId ?? null;
  const laneFocus =
    s?.laneFocus ??
    ({
      mode: "all",
      visibleTrackIds: [],
      anchorTrackId: null,
      hideAuxiliaryLanes: false,
      sourceClipId: null,
    } as const);

  const stateTracks = Array.isArray(s?.tracks) ? (s.tracks as Track[]) : null;
  const baseTracks: Track[] = stateTracks && stateTracks.length > 0 ? stateTracks : FALLBACK_TRACKS;
  const visibleTrackIds = Array.isArray(laneFocus?.visibleTrackIds)
    ? laneFocus.visibleTrackIds.map((trackId: unknown) => String(trackId ?? "").trim()).filter(Boolean)
    : [];
  const renderTracks: Track[] =
    laneFocus?.mode === "import" && visibleTrackIds.length > 0
      ? baseTracks.filter((track) => visibleTrackIds.includes(String(track?.id ?? "")) && (track as any)?.hidden !== true)
      : baseTracks.filter((track) => (track as any)?.hidden !== true);

  return {
    durationSec,
    playheadSec,
    pxPerSec,
    viewportStartSec,
    viewportEndSec,
    markers,
    ranges,
    clips,
    snap,
    selectedRangeId,
    renderTracks,
    laneFocus,
    hideAuxiliaryLanes: !!laneFocus?.hideAuxiliaryLanes,
    hasStateTracks: !!(stateTracks && stateTracks.length > 0),
  };
}

/* ------------------------------------------------------------------ */
/* Phase authoring helper                                             */
/* ------------------------------------------------------------------ */

function findPhaseRangesByLabel(ranges: any[]) {
  const map = new Map<string, any>();
  for (const r of ranges) {
    const lbl = String(r?.label ?? "").trim().toLowerCase();
    if (!lbl.startsWith("phase:")) continue;
    map.set(lbl, r);
  }
  return map;
}

/* ------------------------------------------------------------------ */
/* Strict-mode/remount recovery helpers                               */
/* ------------------------------------------------------------------ */

function pickFirstClipsContainer(st: any): any[] {
  if (!st) return [];
  if (Array.isArray(st.clips)) return st.clips;
  if (Array.isArray(st.timeline?.clips)) return st.timeline.clips;
  if (Array.isArray(st.present?.timeline?.clips)) return st.present.timeline.clips;
  if (Array.isArray(st.present?.clips)) return st.present.clips;
  if (Array.isArray(st.doc?.timeline?.clips)) return st.doc.timeline.clips;
  if (Array.isArray(st.doc?.clips)) return st.doc.clips;
  return [];
}

function findExistingClipIdByRequestOrMedia(
  rid: string | null,
  mediaId: string | null,
  options?: { allowMediaIdMatch?: boolean },
): string | null {
  try {
    const st = (window as any).__oc_debug?.getState?.() ?? (window as any).__oc_debug_state?.() ?? null;
    const clips = pickFirstClipsContainer(st);

    if (rid) {
      const hit = clips.find((c: any) => String(c?.requestId ?? "").trim() === rid);
      if (hit?.id != null) return String(hit.id);
    }

    if (options?.allowMediaIdMatch && mediaId) {
      const hit = clips.find((c: any) => String(c?.mediaId ?? "").trim() === mediaId);
      if (hit?.id != null) return String(hit.id);
    }

    return null;
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------------ */
/* TimelineShell                                                      */
/* ------------------------------------------------------------------ */

type LastPhase = { sig: string; ts: number };
type MediaPanelAuthorityRequest = MediaPanelSetModeDetail;
type MediaPanelIntent = MediaPanelAuthorityRequest;

type MarkerActionSurfaceMode = "menu" | "edit";

type MarkerActionSurfaceState = {
  markerId: string;
  clientX: number;
  clientY: number;
  mode: MarkerActionSurfaceMode;
};

type MarkerSurfaceDraft = {
  label: string;
  type: string;
  note: string;
  color: string;
  visible: boolean;
};

type MarkerBrowserEntry = {
  id: string;
  timeSec: number;
  label: string;
  note: string;
  type: string;
  origin: string;
  frameIndex: number | null;
  visible: boolean;
};

type MarkerBrowserGroupMode = "time" | "origin" | "type";

type MarkerBrowserPrefs = {
  query: string;
  typeFilter: string;
  originFilter: string;
  groupMode: MarkerBrowserGroupMode;
};

type RangeInspectorDraft = {
  label: string;
  kind: string;
};

const MARKER_TYPE_OPTIONS = [
  { value: "note", label: "Note" },
  { value: "bookmark", label: "Bookmark" },
  { value: "chapter", label: "Chapter" },
  { value: "cue", label: "Cue" },
  { value: "beat", label: "Beat" },
  { value: "npc", label: "NPC" },
  { value: "phase", label: "Phase" },
  { value: "marker", label: "Generic" },
] as const;

const MARKER_COLOR_SWATCHES = ["#38bdf8", "#34d399", "#f59e0b", "#f472b6", "#fb7185", "#a78bfa"] as const;
const MANUAL_MARKER_DEFAULT_COLOR = "#38bdf8";
const NPC_MARKER_DEFAULT_COLOR = "#34d399";
const RANGE_KIND_OPTIONS = [
  { value: "selection", label: "Selection" },
  { value: "loop", label: "Loop" },
  { value: "attention", label: "Attention" },
  { value: "emotion", label: "Emotion" },
  { value: "overload", label: "Overload" },
  { value: "music", label: "Music" },
  { value: "safety", label: "Safety" },
  { value: "phase", label: "Phase" },
] as const;
const MARKER_SURFACE_ESTIMATED_MENU_WIDTH_PX = 286;
const MARKER_SURFACE_ESTIMATED_EDIT_WIDTH_PX = 520;
const MARKER_SURFACE_ESTIMATED_MENU_HEIGHT_PX = 332;
const MARKER_SURFACE_ESTIMATED_EDIT_HEIGHT_PX = 340;
const MARKER_SURFACE_VIEWPORT_GAP_PX = 10;
const MARKER_SURFACE_ANCHOR_GAP_PX = 12;
const MARKER_BROWSER_PREFS_STORAGE_KEY = "omnicine:marker-browser:prefs";
const EMPTY_MARKER_SURFACE_DRAFT: MarkerSurfaceDraft = {
  label: "",
  type: "note",
  note: "",
  color: "",
  visible: true,
};

const EMPTY_RANGE_INSPECTOR_DRAFT: RangeInspectorDraft = {
  label: "",
  kind: "selection",
};

const DEFAULT_MARKER_BROWSER_PREFS: MarkerBrowserPrefs = {
  query: "",
  typeFilter: "all",
  originFilter: "all",
  groupMode: "time",
};

function normalizeMarkerSurfaceType(raw: unknown, fallback = "note"): string {
  const value = String(raw ?? "").trim().toLowerCase();
  return value || fallback;
}

function normalizeMarkerOrigin(raw: unknown, fallback = ""): string {
  const value = String(raw ?? "").trim().toLowerCase();
  return value || fallback;
}

function readMarkerBrowserPrefs(): MarkerBrowserPrefs {
  if (typeof window === "undefined") return DEFAULT_MARKER_BROWSER_PREFS;
  try {
    const raw = window.localStorage.getItem(MARKER_BROWSER_PREFS_STORAGE_KEY);
    if (!raw) return DEFAULT_MARKER_BROWSER_PREFS;
    const parsed = JSON.parse(raw) as Partial<MarkerBrowserPrefs> | null;
    return {
      query: typeof parsed?.query === "string" ? parsed.query : "",
      typeFilter: typeof parsed?.typeFilter === "string" ? parsed.typeFilter : "all",
      originFilter: typeof parsed?.originFilter === "string" ? parsed.originFilter : "all",
      groupMode: parsed?.groupMode === "origin" || parsed?.groupMode === "type" ? parsed.groupMode : "time",
    };
  } catch {
    return DEFAULT_MARKER_BROWSER_PREFS;
  }
}

function writeMarkerBrowserPrefs(prefs: MarkerBrowserPrefs): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(MARKER_BROWSER_PREFS_STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // ignore storage failures
  }
}

function readPersistedMarkerSession(): any[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(MARKER_SESSION_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function timeBucketLabel(timeSec: number): string {
  const minute = Math.floor(Math.max(0, timeSec) / 60);
  return minute === 0 ? "00:00-00:59" : `${String(minute).padStart(2, "0")}:00-${String(minute).padStart(2, "0")}:59`;
}

function normalizeMarkerSurfaceNote(marker: any): string {
  const note = String(marker?.note ?? marker?.description ?? marker?.annotation?.summary ?? marker?.annotation?.rawNote ?? "").trim();
  return note;
}

function normalizeMarkerSurfaceColor(raw: unknown): string {
  const value = String(raw ?? "").trim();
  return /^#[0-9a-fA-F]{6}$/.test(value) ? value : "";
}

function resolveAnnotationMarkerDefaultColor(annotationType: unknown, annotationSeverity: unknown): string {
  const type = String(annotationType ?? "").trim().toLowerCase();
  const severity = String(annotationSeverity ?? "").trim().toLowerCase();

  if (severity === "critical" || severity === "error" || severity === "high") return "#fb7185";
  if (severity === "warning" || severity === "medium") return "#f59e0b";

  switch (type) {
    case "issue":
    case "risk":
    case "problem":
      return "#fb7185";
    case "technical":
    case "sync":
    case "timing":
      return "#60a5fa";
    case "performance":
    case "action":
    case "npc":
      return NPC_MARKER_DEFAULT_COLOR;
    case "story":
    case "narrative":
    case "chapter":
      return "#a78bfa";
    case "voice":
    case "emotion":
      return "#f472b6";
    case "creative":
    case "idea":
    case "note":
    default:
      return "#f59e0b";
  }
}

function resolveMarkerAccentColor(marker: any): string {
  const explicitColor = normalizeMarkerSurfaceColor(marker?.color);
  if (explicitColor) return explicitColor;

  const markerType = normalizeMarkerSurfaceType(marker?.type, "marker");
  if (marker?.annotation) {
    return resolveAnnotationMarkerDefaultColor(marker?.annotation?.type ?? markerType, marker?.annotation?.severity);
  }
  if (markerType === "npc") return NPC_MARKER_DEFAULT_COLOR;
  return MANUAL_MARKER_DEFAULT_COLOR;
}

function formatMarkerTypeLabel(type: string | null | undefined): string {
  const normalized = normalizeMarkerSurfaceType(type, "marker");
  const predefined = MARKER_TYPE_OPTIONS.find((option) => option.value === normalized);
  if (predefined) return predefined.label;
  return normalized.replace(/[_-]+/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatMarkerOriginLabel(origin: string | null | undefined): string {
  const normalized = normalizeMarkerOrigin(origin, "marker");
  switch (normalized) {
    case "annotation_surface":
      return "Annotation";
    case "transport_panel":
      return "Panel";
    case "command_palette":
      return "Palette";
    case "xr_bridge":
      return "XR";
    default:
      return normalized.replace(/[_-]+/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
  }
}

type MarkerBrowserChipTone = "cyan" | "violet" | "emerald" | "amber" | "rose" | "slate";

function markerBrowserChipClass(tone: MarkerBrowserChipTone): string {
  switch (tone) {
    case "cyan":
      return "border-cyan-300/24 bg-cyan-400/12 text-cyan-50 shadow-[0_0_0_1px_rgba(34,211,238,0.08)]";
    case "violet":
      return "border-fuchsia-300/24 bg-fuchsia-500/12 text-fuchsia-50 shadow-[0_0_0_1px_rgba(168,85,247,0.08)]";
    case "emerald":
      return "border-emerald-300/24 bg-emerald-400/12 text-emerald-50 shadow-[0_0_0_1px_rgba(52,211,153,0.08)]";
    case "amber":
      return "border-amber-300/24 bg-amber-400/12 text-amber-50 shadow-[0_0_0_1px_rgba(251,191,36,0.08)]";
    case "rose":
      return "border-rose-300/24 bg-rose-400/12 text-rose-50 shadow-[0_0_0_1px_rgba(251,113,133,0.08)]";
    default:
      return "border-sky-300/18 bg-sky-300/[0.08] text-sky-300/82 dark:text-cyan-200/64 shadow-[0_0_0_1px_rgba(56,189,248,0.06)]";
  }
}

function resolveMarkerBrowserOriginTone(origin: string | null | undefined): MarkerBrowserChipTone {
  const normalized = normalizeMarkerOrigin(origin, "marker");
  switch (normalized) {
    case "annotation_surface":
      return "violet";
    case "transport_panel":
      return "cyan";
    case "command_palette":
      return "amber";
    case "xr_bridge":
      return "emerald";
    default:
      return "slate";
  }
}

function resolveMarkerBrowserTypeTone(type: string | null | undefined): MarkerBrowserChipTone {
  const normalized = normalizeMarkerSurfaceType(type, "marker");
  switch (normalized) {
    case "npc":
      return "emerald";
    case "phase":
      return "violet";
    case "cue":
      return "cyan";
    case "beat":
      return "amber";
    case "bookmark":
    case "chapter":
      return "rose";
    default:
      return "slate";
  }
}

function extractMarkerPhaseLabel(marker: any): string | null {
  const values = [marker?.label, marker?.note, marker?.description]
    .map((value) => String(value ?? "").trim().toLowerCase())
    .filter(Boolean);

  for (const value of values) {
    if (value === "phase:setup" || value === "phase:rising" || value === "phase:peak" || value === "phase:resolve") {
      return value;
    }
    if (normalizeMarkerSurfaceType(marker?.type, "marker") === "phase") {
      if (value === "setup" || value === "rising" || value === "peak" || value === "resolve") {
        return `phase:${value}`;
      }
    }
  }

  return null;
}

function buildPromotedRangeFromMarkers(markers: any[], promotedAt: number) {
  const ordered = [...markers]
    .filter(Boolean)
    .sort((left, right) => {
      const timeDelta = Number(left?.timeSec ?? 0) - Number(right?.timeSec ?? 0);
      if (Math.abs(timeDelta) > 1e-9) return timeDelta;
      return String(left?.id ?? "").localeCompare(String(right?.id ?? ""));
    });

  if (ordered.length < 2) return null;

  const first = ordered[0];
  const last = ordered[ordered.length - 1];
  const typeSet = Array.from(new Set(ordered.map((marker) => normalizeMarkerSurfaceType(marker?.type, "marker")).filter(Boolean)));
  const originSet = Array.from(new Set(ordered.map((marker) => normalizeMarkerOrigin(marker?.origin, "")).filter(Boolean)));
  const phaseLabels = Array.from(new Set(ordered.map((marker) => extractMarkerPhaseLabel(marker)).filter(Boolean)));
  const sharedType = typeSet.length === 1 ? typeSet[0] : null;
  const sharedOrigin = originSet.length === 1 ? originSet[0] : null;
  const labelBase = sharedType ? `${formatMarkerTypeLabel(sharedType)} Span` : "Marker Span";
  const label = sharedType === "phase" && phaseLabels.length === 1
    ? phaseLabels[0]
    : labelBase;
  const kind = sharedType === "phase" && phaseLabels.length === 1 ? "phase" : "selection";

  return {
    id: makeId(),
    startSec: Math.min(Number(first?.timeSec ?? 0), Number(last?.timeSec ?? 0)),
    endSec: Math.max(Number(first?.timeSec ?? 0), Number(last?.timeSec ?? 0)),
    kind,
    label,
    source: "user" as const,
    markerSpan: {
      source: "marker_span" as const,
      markerIds: ordered.map((marker) => String(marker?.id ?? "")).filter(Boolean),
      startMarkerId: String(first?.id ?? "") || null,
      endMarkerId: String(last?.id ?? "") || null,
      markerType: sharedType,
      markerOrigin: sharedOrigin,
      promotedAt,
    },
  };
}

function normalizeRangeInspectorKind(raw: unknown, fallback = "selection"): string {
  const value = String(raw ?? "").trim().toLowerCase();
  return value || fallback;
}

function formatRangeKindLabel(kind: string | null | undefined): string {
  const normalized = normalizeRangeInspectorKind(kind, "selection");
  const predefined = RANGE_KIND_OPTIONS.find((option) => option.value === normalized);
  if (predefined) return predefined.label;
  return normalized.replace(/[_-]+/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

function buildRangeInspectorDraft(range: any): RangeInspectorDraft {
  return {
    label: typeof range?.label === "string" ? range.label : "",
    kind: normalizeRangeInspectorKind(range?.kind, "selection"),
  };
}

function buildRangeAnchorBinding(range: any, boundAt: number, followUpdates = true) {
  const markerSpan = range?.markerSpan ?? null;
  return {
    source: "marker_span" as const,
    rangeId: String(range?.id ?? ""),
    startMarkerId: String(markerSpan?.startMarkerId ?? "").trim() || null,
    endMarkerId: String(markerSpan?.endMarkerId ?? "").trim() || null,
    mode: "span" as const,
    followUpdates,
    boundAt,
  };
}

function buildMarkerSurfaceDraft(marker: any): MarkerSurfaceDraft {
  return {
    label: typeof marker?.label === "string" ? marker.label : "",
    type: normalizeMarkerSurfaceType(marker?.type, "note"),
    note: normalizeMarkerSurfaceNote(marker),
    color: normalizeMarkerSurfaceColor(marker?.color),
    visible: marker?.visible !== false,
  };
}

function applyMarkerOriginPreset(marker: any, draft: MarkerSurfaceDraft): MarkerSurfaceDraft {
  const origin = normalizeMarkerOrigin(marker?.origin ?? marker?.annotation?.origin, "");
  const currentType = normalizeMarkerSurfaceType(draft.type || marker?.type, "note");

  if (origin === "transport_panel") {
    const isNpc = currentType === "npc";
    return {
      ...draft,
      type: isNpc ? "npc" : "cue",
      note: draft.note.trim() || (isNpc ? "NPC cue" : "Manual marker"),
      color: isNpc ? NPC_MARKER_DEFAULT_COLOR : MANUAL_MARKER_DEFAULT_COLOR,
      visible: true,
    };
  }

  if (origin === "annotation_surface") {
    return {
      ...draft,
      type: normalizeMarkerSurfaceType(marker?.type, currentType || "note"),
      note: draft.note.trim() || normalizeMarkerSurfaceNote(marker) || "Annotation note",
      color:
        resolveAnnotationMarkerDefaultColor(
          marker?.annotation?.type ?? marker?.type,
          marker?.annotation?.severity,
        ),
      visible: marker?.visible !== false,
    };
  }

  return draft;
}

const RULER_BASE_SCROLLBAR_GAP_PX = 18;
const RULER_SEMANTIC_BAR_HEIGHT_PX = 18;
const RULER_TEMP_ZOOM_MODE_MS = 10_000;
const MEDIA_PANEL_AUDIO_FOCUS_TOP_INSET_PX = 10;
const MEDIA_PANEL_VISUAL_FOCUS_TOP_INSET_PX = 10;
const RULER_BASE_CONTENT_CLEARANCE_PX =
  RULER_HEIGHT_PX + RULER_SEMANTIC_BAR_HEIGHT_PX + RULER_BASE_SCROLLBAR_GAP_PX + 12;
const MIN_TIMELINE_LANE_VISIBILITY_PX = 96;
const MIN_TIMELINE_SCROLLER_HEIGHT_PX =
  RULER_BASE_CONTENT_CLEARANCE_PX + MIN_TIMELINE_LANE_VISIBILITY_PX;
const AUDIO_WITNESS_LENS_IDS: SonicsLensId[] = ["arc", "fx", "continuity", "aiPreview"];

type TimelineClipLike = {
  id?: string;
  clipId?: string;
  name?: string;
  trackId?: string;
  track?: string;
  kind?: string;
  label?: string;
  title?: string;
  startSec?: number;
  start?: number;
  endSec?: number;
  end?: number;
  durSec?: number;
  dur?: number;
  mediaId?: string;
  mediaUrl?: string;
  previewUrl?: string;
  url?: string;
  src?: string;
  rangeAnchor?: {
    rangeId?: string;
    followUpdates?: boolean;
  } | null;
  media?: { id?: string; url?: string };
  fileName?: string;
  filename?: string;
  assetName?: string;
  mediaName?: string;
};

function getClipStartSec(clip: TimelineClipLike | null | undefined): number | null {
  return parseMaybeNumber(clip?.startSec ?? clip?.start);
}

function getClipEndSec(clip: TimelineClipLike | null | undefined): number | null {
  const end = parseMaybeNumber(clip?.endSec ?? clip?.end);
  if (end != null) return end;

  const start = getClipStartSec(clip);
  const dur = parseMaybeNumber(clip?.durSec ?? clip?.dur);
  if (start == null || dur == null) return null;
  return start + dur;
}

function getClipDurationSec(clip: TimelineClipLike | null | undefined): number {
  const start = getClipStartSec(clip);
  const end = getClipEndSec(clip);
  if (start == null || end == null) return 0;
  return Math.max(0, end - start);
}

function readClipMediaUrl(clip: TimelineClipLike | null | undefined): string | null {
  return clip?.mediaUrl ?? clip?.previewUrl ?? clip?.url ?? clip?.src ?? clip?.media?.url ?? null;
}

function isAudioClip(clip: TimelineClipLike | null | undefined): boolean {
  return String(clip?.kind ?? "").trim().toLowerCase() === "audio";
}

function resolveClipMediaKind(
  clip: TimelineClipLike | null | undefined,
  fallbackKind?: string | null,
): "audio" | "video" | "image" {
  const normalizedKind = String(clip?.kind ?? fallbackKind ?? "").trim().toLowerCase();
  if (normalizedKind === "image") return "image";
  if (normalizedKind === "video") return "video";
  return "audio";
}

function resolveTimelineClipTitle(
  clip: TimelineClipLike | null | undefined,
  fallbackLabel?: string | null,
  fallbackKind?: string | null,
): string {
  return resolveMediaIdentityTitle({
    clipTitle: clip?.title ?? clip?.label ?? clip?.name ?? fallbackLabel ?? null,
    assetDisplayName: clip?.assetName ?? clip?.mediaName ?? clip?.name ?? null,
    importedLabel: clip?.fileName ?? clip?.filename ?? clip?.name ?? null,
    mediaUrl: readClipMediaUrl(clip),
    mediaId: clip?.mediaId ?? clip?.media?.id ?? null,
    kind: clip?.kind ?? fallbackKind ?? "audio",
  });
}

export default function TimelineShell(): JSX.Element {
  const markerBrowserPrefs = useMemo(() => readMarkerBrowserPrefs(), []);
  const omniStudioGraph = useOptionalOmniStudioGraph();
  const {
    state,
    dispatch,
    lanes,
    sonics,
    selectedClipId,
    auditionMode,
    setAuditionMode,
    mediaFamilies,
    mediaMembers,
    editorialClipBindings,
    editorialClipBindingByClipId,
    bindEditorialClip,
    clearEditorialClipBinding,
    clipMediaMetadataByClipId,
    recordClipMediaMetadata,
  } = useOmniCineTimeline();
  const registerTimelineLanes = lanes.registerLanes;
  const setTimelineLaneItems = lanes.setLaneItems;
  const upsertTimelineLaneCapsule = lanes.upsertCapsule;
  const setTimelineLaneSyncBadges = lanes.setSyncBadges;
  const setTimelineLaneDependencyRelations = lanes.setDependencyRelations;
  const narrative = useOmniCineNarrative();
  const scrollerRef = useRef<HTMLDivElement>(null);
  const lastLaneFocusAnchorRef = useRef<string | null>(null);
  const mediaPanelFocusDockRef = useRef<HTMLDivElement>(null);
  const mediaPanelIngestDockRef = useRef<HTMLDivElement>(null);

  const [mediaPanelMode, setMediaPanelMode] = useState<MediaPanelMode>("closed");
  const mediaPanelModeRef = useRef<MediaPanelMode>("closed");
  const [mediaPanelPinned, setMediaPanelPinned] = useState<boolean>(false);
  const mediaPanelPinnedRef = useRef<boolean>(false);
  const [mediaPanelIntent, setMediaPanelIntent] = useState<MediaPanelIntent | null>(null);
  const mediaPanelIntentRef = useRef<MediaPanelIntent | null>(null);
  const { state: whisperHudState, showIntent: showTimelineWhisper } = useTimelineWhisperHUD();
  const [audioWitnessUi, setAudioWitnessUi] = useState<AudioWitnessUiState>(defaultAudioWitnessUiState);
  const [viewportWidthPx, setViewportWidthPx] = useState<number>(1);
  const [showRulerEventMarkers, setShowRulerEventMarkers] = useState<boolean>(true);
  const [showRulerBeatMarkers, setShowRulerBeatMarkers] = useState<boolean>(true);
  const [rulerZoomModeEnabled, setRulerZoomModeEnabled] = useState<boolean>(false);
  const rulerZoomModeEnabledRef = useRef<boolean>(false);
  const [rulerOnlyViewportStartSec, setRulerOnlyViewportStartSec] = useState<number | null>(null);
  const [rulerOnlyPxPerSec, setRulerOnlyPxPerSec] = useState<number | null>(null);
  const rulerOnlyViewportStartSecRef = useRef<number | null>(null);
  const rulerOnlyPxPerSecRef = useRef<number | null>(null);
  const [rulerHoverViewportX, setRulerHoverViewportX] = useState<number | null>(null);
  const [rulerScrubViewportX, setRulerScrubViewportX] = useState<number | null>(null);
  const rulerScrubViewportXRef = useRef<number | null>(null);
  const viewportWidthPxRef = useRef<number>(1);
  const pxPerSecRef = useRef<number>(100);
  const rulerZoomModeTimeoutRef = useRef<number | null>(null);
  const edgeScrubRafRef = useRef<number | null>(null);
  const emptyPanRef = useRef<{ pointerId: number; lastClientX: number } | null>(null);
  const [isEmptySpacePanning, setIsEmptySpacePanning] = useState<boolean>(false);
  const [markerSurface, setMarkerSurface] = useState<MarkerActionSurfaceState | null>(null);
  const [markerSurfaceDraft, setMarkerSurfaceDraft] = useState<MarkerSurfaceDraft>(EMPTY_MARKER_SURFACE_DRAFT);
  const [markerBrowserOpen, setMarkerBrowserOpen] = useState<boolean>(false);
  const [markerBrowserQuery, setMarkerBrowserQuery] = useState<string>(markerBrowserPrefs.query);
  const [markerBrowserTypeFilter, setMarkerBrowserTypeFilter] = useState<string>(markerBrowserPrefs.typeFilter);
  const [markerBrowserOriginFilter, setMarkerBrowserOriginFilter] = useState<string>(markerBrowserPrefs.originFilter);
  const [markerBrowserGroupMode, setMarkerBrowserGroupMode] = useState<MarkerBrowserGroupMode>(markerBrowserPrefs.groupMode);
  const [selectedMarkerIds, setSelectedMarkerIds] = useState<string[]>([]);
  const [rangeInspectorDraft, setRangeInspectorDraft] = useState<RangeInspectorDraft>(EMPTY_RANGE_INSPECTOR_DRAFT);
  const [activeMarkerId, setActiveMarkerId] = useState<string | null>(null);
  const activeMarkerIdRef = useRef<string | null>(null);
  const semanticLastNavCommandRef = useRef<string | null>(null);
  const markerSurfaceRef = useRef<HTMLDivElement | null>(null);
  const hasHydratedPersistedMarkersRef = useRef<boolean>(false);
  const narrativePreview = (state as any).narrativePreview ?? { active: false, blocks: [], overlays: [] };
  const activeOmniStudioSceneId = omniStudioGraph?.selectedNodeId ?? null;

  useEffect(() => {
    mediaPanelModeRef.current = mediaPanelMode;
  }, [mediaPanelMode]);

  useEffect(() => {
    mediaPanelPinnedRef.current = mediaPanelPinned;
  }, [mediaPanelPinned]);

  useEffect(() => {
    mediaPanelIntentRef.current = mediaPanelIntent;
  }, [mediaPanelIntent]);

  useEffect(() => {
    rulerZoomModeEnabledRef.current = rulerZoomModeEnabled;
  }, [rulerZoomModeEnabled]);

  useEffect(() => {
    rulerOnlyViewportStartSecRef.current = rulerOnlyViewportStartSec;
  }, [rulerOnlyViewportStartSec]);

  useEffect(() => {
    rulerOnlyPxPerSecRef.current = rulerOnlyPxPerSec;
  }, [rulerOnlyPxPerSec]);

  useEffect(() => {
    rulerScrubViewportXRef.current = rulerScrubViewportX;
  }, [rulerScrubViewportX]);

  useEffect(() => {
    viewportWidthPxRef.current = viewportWidthPx;
  }, [viewportWidthPx]);

  useEffect(() => {
    activeMarkerIdRef.current = activeMarkerId;
  }, [activeMarkerId]);

  const setTemporaryRulerZoomMode = useCallback((enabled: boolean) => {
    if (rulerZoomModeTimeoutRef.current != null) {
      window.clearTimeout(rulerZoomModeTimeoutRef.current);
      rulerZoomModeTimeoutRef.current = null;
    }

    rulerZoomModeEnabledRef.current = enabled;
    setRulerZoomModeEnabled(enabled);

    if (enabled) {
      rulerZoomModeTimeoutRef.current = window.setTimeout(() => {
        rulerZoomModeTimeoutRef.current = null;
        rulerZoomModeEnabledRef.current = false;
        setRulerZoomModeEnabled(false);
      }, RULER_TEMP_ZOOM_MODE_MS);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (rulerZoomModeTimeoutRef.current != null) {
        window.clearTimeout(rulerZoomModeTimeoutRef.current);
        rulerZoomModeTimeoutRef.current = null;
      }
    };
  }, []);

  const scrollRafRef = useRef<number | null>(null);
  const autoScrollRafRef = useRef<number | null>(null);
  const autoScrollTargetRef = useRef<number | null>(null);
  const rulerZoomDeltaSmoothRef = useRef<number>(0);
  const rulerZoomLastEventTsRef = useRef<number>(0);
  const rulerPanAnimRef = useRef<{ rafId: number | null; velocityPx: number }>({
    rafId: null,
    velocityPx: 0,
  });
  const lastViewportSyncRef = useRef<{
    viewportStartSec: number;
    viewportEndSec: number;
    scrollLeftPx: number;
    scrollTopPx: number;
  } | null>(null);

  const __OC_PERF = (import.meta as any)?.env?.DEV
    ? (((window as any).__oc_perf = (window as any).__oc_perf || {
        scrollHandled: 0,
        scrollDispatched: 0,
        scrollSkipped: 0,
      }) as { scrollHandled: number; scrollDispatched: number; scrollSkipped: number })
    : null;

  useEffect(() => {
    if (!((import.meta as any)?.env?.DEV)) return;
    if (typeof window === "undefined") return;
    try {
      (window as any).__oc_scroller = scrollerRef.current ?? null;
    } catch {
      /* ignore */
    }
    return () => {
      try {
        if ((window as any).__oc_scroller === scrollerRef.current) (window as any).__oc_scroller = null;
      } catch {}
    };
  }, []);

  const suppressViewportSyncUntilRef = useRef<number>(0);

  const lastAddSigRef = useRef<{ sig: string; t: number } | null>(null);
  const lastPhaseRef = useRef<LastPhase | null>(null);
  const latestPhaseRuntimeRef = useRef<Record<string, unknown> | null>(null);
  const latestNpcRuntimeRef = useRef<Record<string, unknown> | null>(null);
  const [latestNpcLaneRuntime, setLatestNpcLaneRuntime] = useState<Record<string, unknown> | null>(null);
  const latestNpcLaneSigRef = useRef<string>("");
  const latestSurfaceIntentRef = useRef<SurfaceIntentV1 | null>(null);
  const lastImportedPublishContextRef = useRef<{
    importedKind: "video" | "audio" | "image" | "npc";
    clipId: string;
    trackId: string;
    sourceLabel?: string | null;
  } | null>(null);
  const previousSelectedClipIdRef = useRef<string | null>(selectedClipId ?? null);
  const previousAuditionModeRef = useRef<AuditionMode>(auditionMode);
  const previousContinuityWarningIdsRef = useRef<string[]>([]);
  const authoredEmotionalIntentSegmentsRef = useRef<EmotionalIntentSegment[]>([]);
  const authoredFxIntentNodesRef = useRef<FxIntentNode[]>([]);

  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const authoredEmotionalIntentSegments = useMemo(
    () => selectActiveEmotionalIntentSegments(sonics.state),
    [sonics.state],
  );

  const authoredFxIntentNodes = useMemo(
    () => selectActiveFxIntentNodes(sonics.state),
    [sonics.state],
  );

  useEffect(() => {
    authoredEmotionalIntentSegmentsRef.current = authoredEmotionalIntentSegments;
  }, [authoredEmotionalIntentSegments]);

  useEffect(() => {
    authoredFxIntentNodesRef.current = authoredFxIntentNodes;
  }, [authoredFxIntentNodes]);

  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const isPlayingRef = useRef<boolean>(isPlaying);
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  const [followPlayhead, setFollowPlayhead] = useState<boolean>(false);
  const followPlayheadRef = useRef<boolean>(followPlayhead);
  useEffect(() => {
    followPlayheadRef.current = followPlayhead;
  }, [followPlayhead]);

  const norm = useMemo(() => normalizeTimelineState(state as unknown), [state]);
    const applyImportedLaneFocus = useCallback((args: {
      importedKind: "video" | "audio" | "image" | "npc";
      clipId: string;
      includeAudioCompanion?: boolean;
      source: string;
    }) => {
      const timelineTracks = Array.isArray(stateRef.current?.tracks) ? stateRef.current.tracks : FALLBACK_TRACKS;
      const nextLaneFocus = createImportedLaneFocus({
        tracks: timelineTracks,
        importedKind: args.importedKind,
        sourceClipId: args.clipId,
        includeAudioCompanion: args.includeAudioCompanion,
      });
      const sourceClip = pickFirstClipsContainer(stateRef.current).find(
        (clip: any) => String(clip?.id ?? clip?.clipId ?? "").trim() === args.clipId,
      ) as Record<string, unknown> | undefined;

      lastImportedPublishContextRef.current = {
        importedKind: args.importedKind,
        clipId: args.clipId,
        trackId: String(nextLaneFocus.anchorTrackId ?? "").trim() || String(sourceClip?.trackId ?? "").trim(),
        sourceLabel: String(sourceClip?.label ?? sourceClip?.fileName ?? "").trim() || null,
      };

      dispatch({
        type: "timeline/setLaneFocus",
        payload: nextLaneFocus,
        meta: { source: args.source as any, note: "timeline lane focus from import" },
      } as any);
    }, [dispatch]);
  const playbackProjection = useMemo(
    () =>
      resolvePlaybackProjection({
        clips: norm.clips as TimelineClipLike[],
        mediaFamilies,
        mediaMembers,
        editorialClipBindings,
        mediaMetadataByClipId: clipMediaMetadataByClipId,
        playheadSec: norm.playheadSec,
        auditionMode,
        selectedClipId,
        requestedClipId: mediaPanelIntent?.clipId ?? selectedClipId ?? audioWitnessUi.activeCapsuleId ?? null,
      }),
    [
      auditionMode,
      audioWitnessUi.activeCapsuleId,
      clipMediaMetadataByClipId,
      editorialClipBindings,
      mediaFamilies,
      mediaMembers,
      mediaPanelIntent?.clipId,
      norm.clips,
      norm.playheadSec,
      selectedClipId,
    ],
  );
  const clipById = useMemo(
    () =>
      new Map(
        (norm.clips as TimelineClipLike[])
          .map((clip) => {
            const clipId = String(clip?.id ?? clip?.clipId ?? "").trim();
            return clipId ? ([clipId, clip] as const) : null;
          })
          .filter(Boolean) as Array<readonly [string, TimelineClipLike]>,
      ),
    [norm.clips],
  );
  const audioClips = useMemo(
    () => (norm.clips as TimelineClipLike[]).filter((clip) => isAudioClip(clip)),
    [norm.clips],
  );
  const witnessBoundClips = useMemo(
    () =>
      (norm.clips as TimelineClipLike[]).filter((clip) => {
        const clipId = String(clip?.id ?? clip?.clipId ?? "").trim();
        return !!clipId && !!editorialClipBindingByClipId[clipId];
      }),
    [editorialClipBindingByClipId, norm.clips],
  );

  const activeAudioClip = useMemo(() => {
    const projectedClipId = String(
      playbackProjection.activeAudioClipId ?? playbackProjection.activeVideoClipId ?? playbackProjection.focusClipId ?? "",
    ).trim();
    if (projectedClipId) return clipById.get(projectedClipId) ?? null;

    const requestedId = String(audioWitnessUi.activeCapsuleId ?? mediaPanelIntent?.clipId ?? "").trim();
    if (requestedId) return clipById.get(requestedId) ?? null;

    return null;
  }, [
    audioWitnessUi.activeCapsuleId,
    clipById,
    mediaPanelIntent?.clipId,
    playbackProjection.activeAudioClipId,
    playbackProjection.activeVideoClipId,
    playbackProjection.focusClipId,
  ]);

  const activeAudioClipId = useMemo(() => {
    return String(
      playbackProjection.activeAudioClipId ?? playbackProjection.activeVideoClipId ?? playbackProjection.focusClipId ?? "",
    ).trim() || undefined;
  }, [playbackProjection.activeAudioClipId, playbackProjection.activeVideoClipId, playbackProjection.focusClipId]);

  const activeAudioSourceId = useMemo(() => {
    return String(playbackProjection.activeAudioMemberId ?? playbackProjection.activeVideoMemberId ?? "").trim() || undefined;
  }, [playbackProjection.activeAudioMemberId, playbackProjection.activeVideoMemberId]);
  const activeWitnessMediaUrl = useMemo(
    () => readClipMediaUrl(activeAudioClip) ?? mediaPanelIntent?.mediaUrl ?? undefined,
    [activeAudioClip, mediaPanelIntent?.mediaUrl],
  );
  const activeAudioFamilyLabel = useMemo(
    () => playbackProjection.witnessLabel ?? playbackProjection.familyLabel ?? undefined,
    [playbackProjection.familyLabel, playbackProjection.witnessLabel],
  );
  const activeWitnessMediaType = useMemo(
    () =>
      resolveClipMediaKind(
        activeAudioClip,
        mediaPanelIntent?.kind ?? (playbackProjection.hasLinkedVideo ? "video" : playbackProjection.hasLinkedAudio ? "audio" : null),
      ),
    [activeAudioClip, mediaPanelIntent?.kind, playbackProjection.hasLinkedAudio, playbackProjection.hasLinkedVideo],
  );

  const activeAudioStartSec = useMemo(
    () => playbackProjection.activeAudio[0]?.startSec ?? getClipStartSec(activeAudioClip),
    [activeAudioClip, playbackProjection.activeAudio],
  );
  const activeAudioEndSec = useMemo(
    () => playbackProjection.activeAudio[0]?.endSec ?? getClipEndSec(activeAudioClip),
    [activeAudioClip, playbackProjection.activeAudio],
  );
  const activeAudioDurationSec = useMemo(() => {
    const projectedDurationSec = Number(playbackProjection.activeAudioDurationSec ?? NaN);
    if (Number.isFinite(projectedDurationSec) && projectedDurationSec > 0) return projectedDurationSec;
    return getClipDurationSec(activeAudioClip);
  }, [activeAudioClip, playbackProjection.activeAudioDurationSec]);
  const audioWitnessTitle = useMemo(
    () =>
      playbackProjection.witnessLabel ??
      resolveTimelineClipTitle(
        activeAudioClip,
        mediaPanelIntent?.clipLabel ?? mediaPanelIntent?.label ?? null,
        activeWitnessMediaType,
      ),
    [
      activeAudioClip,
      activeWitnessMediaType,
      mediaPanelIntent?.clipLabel,
      mediaPanelIntent?.label,
      playbackProjection.witnessLabel,
    ],
  );
  const audioWitnessCurrentTimeSec = useMemo(() => {
    if (activeAudioStartSec == null) return undefined;
    return clamp(norm.playheadSec - activeAudioStartSec, 0, Math.max(activeAudioDurationSec, 0));
  }, [activeAudioDurationSec, activeAudioStartSec, norm.playheadSec]);
  const isActiveAudioAtPlayhead =
    activeAudioStartSec != null &&
    activeAudioEndSec != null &&
    norm.playheadSec >= activeAudioStartSec &&
    norm.playheadSec <= activeAudioEndSec;
  const audioWitnessStatus = isActiveAudioAtPlayhead && isPlaying ? "playing" : "ready";
  const audioWitnessSubtitle =
    auditionMode === "sequence"
      ? "Sequence focus"
      : auditionMode === "clip"
        ? "Clip focus"
        : "Source focus";
  const audioWitnessBarVisible =
    audioWitnessUi.witnessBarVisible && !!(activeAudioClip || playbackProjection.focusClipId || playbackProjection.activeAudio.length);
  const continuityDiagnostics = useMemo(
    () => deriveContinuityDiagnostics(authoredEmotionalIntentSegments, authoredFxIntentNodes, activeAudioDurationSec),
    [activeAudioDurationSec, authoredEmotionalIntentSegments, authoredFxIntentNodes],
  );
  const continuityScore01 = useMemo(
    () => averageContinuityScore(continuityDiagnostics),
    [continuityDiagnostics],
  );
  const bindingEditorState = useMemo(
    () =>
      deriveMediaBindingEditorState({
        clips: norm.clips as TimelineClipLike[],
        mediaFamilies,
        mediaMembers,
        editorialClipBindings,
        mediaMetadataByClipId: clipMediaMetadataByClipId,
        playheadSec: norm.playheadSec,
        auditionMode,
        selectedClipId,
        requestedClipId: mediaPanelIntent?.clipId ?? selectedClipId ?? audioWitnessUi.activeCapsuleId ?? null,
      }),
    [
      auditionMode,
      audioWitnessUi.activeCapsuleId,
      clipMediaMetadataByClipId,
      editorialClipBindings,
      mediaFamilies,
      mediaMembers,
      mediaPanelIntent?.clipId,
      norm.clips,
      norm.playheadSec,
      selectedClipId,
    ],
  );
  const isAudioWitnessFocusMode = audioWitnessBarVisible && activeWitnessMediaType === "audio";
  const suppressMediaPanelForArcLens = audioWitnessBarVisible && audioWitnessUi.activeLensId === "arc";
  const shouldRenderInlineAudioFocusPanel =
    mediaPanelMode === "focus" &&
    isAudioWitnessFocusMode &&
    mediaPanelIntent?.kind === "audio" &&
    mediaPanelIntent?.source === "inlineWitness";
  const shouldRenderTallMediaPanel =
    !suppressMediaPanelForArcLens && (mediaPanelMode === "ingest" || !isAudioWitnessFocusMode || shouldRenderInlineAudioFocusPanel);
  const derivedContinuityWarningCount = useMemo(
    () => continuityDiagnostics.filter((diagnostic) => diagnostic.status !== "coherent").length,
    [continuityDiagnostics],
  );
  const narrativeSonicsContext = useMemo<NarrativeSonicsContext>(
    () => ({
      focusSourceId: activeAudioSourceId,
      focusClipId: activeAudioClipId,
      focusSequenceId: undefined,
      focusScope: audioWitnessUi.focusScope,
      emotionalSummary:
        authoredEmotionalIntentSegments.length > 0
          ? `${authoredEmotionalIntentSegments.length} authored arc segment${authoredEmotionalIntentSegments.length === 1 ? "" : "s"}`
          : "No authored arc intent yet",
      fxSummary:
        authoredFxIntentNodes.length > 0
          ? `${authoredFxIntentNodes.length} authored FX node${authoredFxIntentNodes.length === 1 ? "" : "s"}`
          : "No authored FX intent yet",
      continuitySummary: summarizeContinuityDiagnostics(continuityDiagnostics),
      currentTimeSec: audioWitnessCurrentTimeSec,
      durationSec: activeAudioDurationSec,
      activePreviewBranchId: null,
      authoredFxNodeCount: authoredFxIntentNodes.length,
      derivedContinuityWarningCount,
      continuityScore01,
      hasAuthoredSonicsIntents: authoredEmotionalIntentSegments.length > 0 || authoredFxIntentNodes.length > 0,
      hasInferredSonicsIntents: false,
    }),
    [
      activeAudioClipId,
      activeAudioDurationSec,
      activeAudioSourceId,
      authoredEmotionalIntentSegments.length,
      authoredFxIntentNodes.length,
      audioWitnessCurrentTimeSec,
      audioWitnessUi.focusScope,
      continuityDiagnostics,
      continuityScore01,
      derivedContinuityWarningCount,
    ],
  );
  const initialProductLaneModel = useMemo(
    () => buildInitialProductLaneModel({
      playbackProjection,
      emotionalIntentSegments: authoredEmotionalIntentSegments,
      fxIntentNodes: authoredFxIntentNodes,
      continuityDiagnostics,
      derivedContinuityWarningCount,
      npcRuntime: latestNpcLaneRuntime,
      atomicRuns: Array.isArray(state.atomicRuns?.runs)
        ? state.atomicRuns.runs.map((run) => ({
            id: String(run?.id ?? ""),
            startSec: Number(run?.startSec ?? 0),
            durSec: Number(run?.durSec ?? 0),
            label: String(run?.label ?? run?.id ?? "Atomic Run"),
            status: typeof run?.status === "string" ? run.status : null,
          }))
        : [],
      selectedAtomicRunId: typeof state.atomicRuns?.selectedRunId === "string"
        ? state.atomicRuns.selectedRunId
        : null,
      clipMediaMetadataByClipId,
      tracks: norm.renderTracks,
      ranges: norm.ranges,
    }),
    [
      authoredEmotionalIntentSegments,
      authoredFxIntentNodes,
      clipMediaMetadataByClipId,
      continuityDiagnostics,
      derivedContinuityWarningCount,
      latestNpcLaneRuntime,
      norm.ranges,
      norm.renderTracks,
      playbackProjection,
      state.atomicRuns?.runs,
      state.atomicRuns?.selectedRunId,
    ],
  );
  useEffect(() => {
    publishInitialProductLaneModel({
      registerLanes: registerTimelineLanes,
      setLaneItems: setTimelineLaneItems,
      upsertCapsule: upsertTimelineLaneCapsule,
      setSyncBadges: setTimelineLaneSyncBadges,
      setDependencyRelations: setTimelineLaneDependencyRelations,
    }, initialProductLaneModel);
  }, [
    initialProductLaneModel,
    registerTimelineLanes,
    setTimelineLaneDependencyRelations,
    setTimelineLaneItems,
    setTimelineLaneSyncBadges,
    upsertTimelineLaneCapsule,
  ]);
  const hasExplicitBinding = bindingEditorState.bindingSource === "explicit";
  const canBindCurrentFamily = useMemo(
    () => !!playbackProjection.focusClipId && !!playbackProjection.focusFamilyId,
    [playbackProjection.focusClipId, playbackProjection.focusFamilyId],
  );
  const contextClipId = useMemo(
    () =>
      String(selectedClipId ?? playbackProjection.focusClipId ?? activeAudioClipId ?? "").trim() || null,
    [activeAudioClipId, playbackProjection.focusClipId, selectedClipId],
  );
  const contextClip = useMemo(
    () => (contextClipId ? clipById.get(contextClipId) ?? null : activeAudioClip),
    [activeAudioClip, clipById, contextClipId],
  );
  const contextClipTitle = useMemo(() => {
    if (!contextClipId) return "No clip selected";
    const metadata = clipMediaMetadataByClipId[contextClipId] ?? null;
    return resolveTimelineClipTitle(
      contextClip,
      metadata?.sourceLabel ?? metadata?.mediaUrl ?? null,
      metadata?.kind ?? activeWitnessMediaType,
    );
  }, [activeWitnessMediaType, clipMediaMetadataByClipId, contextClip, contextClipId]);
  const retainedContextLine = useMemo(() => {
    if (audioWitnessBarVisible || audioWitnessUi.activeLensId != null) return null;

    const selectionLabel = contextClipId ? contextClipTitle : "No selection";
    const auditionLabel = `${auditionMode} audition`;
    const narrativeLabel = (narrative.storyline?.trim() || "ambient").toLowerCase();

    if (derivedContinuityWarningCount > 0) {
      return `${selectionLabel} · ${auditionLabel} · ${derivedContinuityWarningCount} warn`;
    }

    return `${selectionLabel} · ${auditionLabel} · ${narrativeLabel}`;
  }, [
    audioWitnessBarVisible,
    audioWitnessUi.activeLensId,
    auditionMode,
    contextClipId,
    contextClipTitle,
    derivedContinuityWarningCount,
    narrative.storyline,
  ]);

  const setWitnessAuditionMode = useCallback((nextMode: AuditionMode) => {
    setAuditionMode(nextMode);
    setAudioWitnessUi((prev) => ({ ...prev, focusScope: nextMode }));
  }, [setAuditionMode]);

  useEffect(() => {
    const nextSelectedClipId = String(selectedClipId ?? "").trim() || null;
    if (previousSelectedClipIdRef.current === nextSelectedClipId) return;

    previousSelectedClipIdRef.current = nextSelectedClipId;
    if (!nextSelectedClipId) {
      showTimelineWhisper({
        type: "selection/changed",
        clipTitle: "none",
      });
      return;
    }

    const selectedClip = clipById.get(nextSelectedClipId) ?? null;
    const selectedMetadata = clipMediaMetadataByClipId[nextSelectedClipId] ?? null;
    const selectedTitle = resolveTimelineClipTitle(
      selectedClip,
      selectedMetadata?.sourceLabel ?? selectedMetadata?.mediaUrl ?? null,
      selectedMetadata?.kind ?? null,
    );

    showTimelineWhisper({
      type: "selection/changed",
      clipTitle: selectedTitle,
    });
  }, [clipById, clipMediaMetadataByClipId, selectedClipId, showTimelineWhisper]);

  useEffect(() => {
    if (previousAuditionModeRef.current === auditionMode) return;

    previousAuditionModeRef.current = auditionMode;
    showTimelineWhisper({
      type: "audition/modeChanged",
      mode: auditionMode,
    });
  }, [auditionMode, showTimelineWhisper]);

  useEffect(() => {
    const warningDiagnostics = continuityDiagnostics.filter((diagnostic) => diagnostic.status !== "coherent");
    const previousIds = new Set(previousContinuityWarningIdsRef.current);
    const nextWarning = warningDiagnostics.find((diagnostic) => !previousIds.has(diagnostic.id));

    previousContinuityWarningIdsRef.current = warningDiagnostics.map((diagnostic) => diagnostic.id);

    if (!nextWarning) return;

    showTimelineWhisper({
      type: "continuity/warningSurfaced",
      atSec: nextWarning.startSec,
    });
  }, [continuityDiagnostics, showTimelineWhisper]);

  const upsertFocusClipBinding = useCallback(
    (patch: Partial<Pick<EditorialClipBinding, "familyId" | "audioMemberId" | "videoMemberId">>) => {
      const focusClipId = String(bindingEditorState.focusClipId ?? "").trim();
      const familyId = String(patch.familyId ?? bindingEditorState.selectedFamilyId ?? "").trim();
      if (!focusClipId || !familyId) return;

      const family = bindingEditorState.families.find((entry) => entry.id === familyId);
      if (!family) return;

      const existingBinding = editorialClipBindingByClipId[focusClipId] ?? null;
      bindEditorialClip({
        clipId: focusClipId,
        familyId: family.id,
        audioMemberId: patch.audioMemberId ?? existingBinding?.audioMemberId,
        videoMemberId: patch.videoMemberId ?? existingBinding?.videoMemberId,
      });
    },
    [
      bindEditorialClip,
      bindingEditorState.families,
      bindingEditorState.focusClipId,
      bindingEditorState.selectedFamilyId,
      editorialClipBindingByClipId,
    ],
  );

  const bindFocusClipToCurrentFamily = useCallback(() => {
    const focusClipId = String(playbackProjection.focusClipId ?? "").trim();
    const familyId = String(playbackProjection.focusFamilyId ?? "").trim();
    if (!focusClipId || !familyId) return;

    upsertFocusClipBinding({
      familyId,
      audioMemberId: playbackProjection.activeAudioMemberId,
      videoMemberId: playbackProjection.activeVideoMemberId,
    });
  }, [
    playbackProjection.activeAudioMemberId,
    playbackProjection.activeVideoMemberId,
    playbackProjection.focusClipId,
    playbackProjection.focusFamilyId,
    upsertFocusClipBinding,
  ]);

  const clearExplicitBindingForFocusClip = useCallback(() => {
    const focusClipId = String(playbackProjection.focusClipId ?? "").trim();
    if (!focusClipId) return;
    clearEditorialClipBinding(focusClipId);
  }, [clearEditorialClipBinding, playbackProjection.focusClipId]);

  const setFocusClipBindingFamily = useCallback(
    (familyId: string) => {
      upsertFocusClipBinding({ familyId });
    },
    [upsertFocusClipBinding],
  );

  const setFocusClipPreferredAudioMemberId = useCallback(
    (memberId: string) => {
      upsertFocusClipBinding({ audioMemberId: memberId || undefined });
    },
    [upsertFocusClipBinding],
  );

  const setFocusClipPreferredVideoMemberId = useCallback(
    (memberId: string) => {
      upsertFocusClipBinding({ videoMemberId: memberId || undefined });
    },
    [upsertFocusClipBinding],
  );

  const turningPoints = useNarrativeReadModel(state);
  const devNarrativeEnabled =
    (import.meta as any)?.env?.DEV &&
    typeof window !== "undefined" &&
    window.location.pathname.includes("/omnicine/dev");

  const pxPerSec = norm.pxPerSec;

  useEffect(() => {
    pxPerSecRef.current = pxPerSec;
  }, [pxPerSec]);

  useEffect(() => {
    if (norm.laneFocus?.mode !== "import") {
      lastLaneFocusAnchorRef.current = null;
      return;
    }

    const anchorTrackId = String(norm.laneFocus?.anchorTrackId ?? "").trim();
    if (!anchorTrackId || lastLaneFocusAnchorRef.current === anchorTrackId) return;

    const raf = window.requestAnimationFrame(() => {
      const scroller = scrollerRef.current;
      if (!scroller) return;

      const trackNodes = Array.from(scroller.querySelectorAll<HTMLElement>("[data-omnicine-track='1']"));
      const anchorNode = trackNodes.find((node) => node.dataset.trackId === anchorTrackId) ?? null;
      if (!anchorNode) return;

      const scrollerRect = scroller.getBoundingClientRect();
      const anchorRect = anchorNode.getBoundingClientRect();
      const nextScrollTop = Math.max(0, scroller.scrollTop + (anchorRect.top - scrollerRect.top) - 12);
      scroller.scrollTo({ top: nextScrollTop, behavior: "smooth" });
      lastLaneFocusAnchorRef.current = anchorTrackId;
    });

    return () => window.cancelAnimationFrame(raf);
  }, [norm.laneFocus?.anchorTrackId, norm.laneFocus?.mode, norm.renderTracks]);

  useEffect(() => {
    if (rulerZoomModeEnabled) {
      setRulerOnlyViewportStartSec((prev) => (finiteNum(prev) ? prev : norm.viewportStartSec));
      setRulerOnlyPxPerSec((prev) => (finiteNum(prev) ? prev : pxPerSec));
      return;
    }

    setRulerOnlyViewportStartSec(null);
    setRulerOnlyPxPerSec(null);
  }, [rulerZoomModeEnabled, norm.viewportStartSec, pxPerSec]);

  const laneWidthPx = useMemo(() => {
    const w = Math.ceil(Math.max(0, norm.durationSec) * Math.max(1, pxPerSec));
    return Math.max(1, w);
  }, [norm.durationSec, pxPerSec]);

  const effectiveRulerPxPerSec = useMemo(() => {
    if (!rulerZoomModeEnabled) return pxPerSec;
    const localPx = finiteNum(rulerOnlyPxPerSec) ? Number(rulerOnlyPxPerSec) : pxPerSec;
    return clamp(localPx, 20, 400);
  }, [rulerZoomModeEnabled, rulerOnlyPxPerSec, pxPerSec]);

  const effectiveRulerViewportStartSec = useMemo(() => {
    if (!rulerZoomModeEnabled) return norm.viewportStartSec;

    const localStart = finiteNum(rulerOnlyViewportStartSec)
      ? Number(rulerOnlyViewportStartSec)
      : norm.viewportStartSec;

    const maxStartSec = Math.max(
      0,
      norm.durationSec - Math.max(1e-6, viewportWidthPx) / Math.max(1e-6, effectiveRulerPxPerSec),
    );

    return clamp(localStart, 0, maxStartSec);
  }, [
    rulerZoomModeEnabled,
    rulerOnlyViewportStartSec,
    norm.viewportStartSec,
    norm.durationSec,
    viewportWidthPx,
    effectiveRulerPxPerSec,
  ]);

  const effectiveRulerViewportEndSec = useMemo(() => {
    if (!rulerZoomModeEnabled) return norm.viewportEndSec;

    const spanSec = Math.max(1e-6, viewportWidthPx) / Math.max(1e-6, effectiveRulerPxPerSec);
    return clamp(
      effectiveRulerViewportStartSec + spanSec,
      effectiveRulerViewportStartSec,
      Math.max(effectiveRulerViewportStartSec, norm.durationSec),
    );
  }, [
    rulerZoomModeEnabled,
    norm.viewportEndSec,
    viewportWidthPx,
    effectiveRulerPxPerSec,
    effectiveRulerViewportStartSec,
    norm.durationSec,
  ]);

  const playheadTimelineX = useMemo(() => {
    return clamp(norm.playheadSec * pxPerSec, 0, laneWidthPx);
  }, [norm.playheadSec, pxPerSec, laneWidthPx]);

  const rulerHoverTimelineX = useMemo(() => {
    if (rulerZoomModeEnabled) return null;
    if (!finiteNum(rulerHoverViewportX)) return null;
    const x = norm.viewportStartSec * pxPerSec + Number(rulerHoverViewportX);
    return clamp(x, 0, laneWidthPx);
  }, [rulerZoomModeEnabled, rulerHoverViewportX, norm.viewportStartSec, pxPerSec, laneWidthPx]);

  const rulerScrubTimelineX = useMemo(() => {
    if (rulerZoomModeEnabled) return null;
    if (!finiteNum(rulerScrubViewportX)) return null;
    const x = norm.viewportStartSec * pxPerSec + Number(rulerScrubViewportX);
    return clamp(x, 0, laneWidthPx);
  }, [rulerZoomModeEnabled, rulerScrubViewportX, norm.viewportStartSec, pxPerSec, laneWidthPx]);

  const rulerSemanticMarkers = useMemo<RulerSemanticMarkerView[]>(() => {
    return (norm.markers ?? [])
      .filter((m: any) => m?.visible !== false)
      .filter((m: any) => finiteNum(Number(m?.timeSec)))
      .filter((m: any) => !isNpcLikeMarker(m))
      .flatMap((m: any) => {
        const type = String(m?.type ?? "").trim().toLowerCase();
        const label =
          typeof m?.label === "string" && m.label.trim()
            ? m.label.trim()
            : typeof m?.description === "string" && m.description.trim()
              ? m.description.trim()
              : undefined;

        if (type === "beat") {
          return [
            {
              id: String(m.id),
              timeSec: Number(m.timeSec),
              kind: "beat" as const,
              label,
              emphasis: "normal" as const,
              accentColor: resolveMarkerAccentColor(m),
              note: normalizeMarkerSurfaceNote(m) || null,
              origin: normalizeMarkerOrigin(m?.origin ?? m?.annotation?.origin, ""),
              frameIndex: Number.isFinite(Number(m?.frameIndex)) ? Number(m.frameIndex) : null,
            },
          ];
        }

        if (
          type === "bookmark" ||
          type === "chapter" ||
          type === "cue" ||
          type === "note"
        ) {
          const emphasis =
            type === "chapter"
              ? ("high" as const)
              : type === "note"
                ? ("low" as const)
                : ("normal" as const);

          return [
            {
              id: String(m.id),
              timeSec: Number(m.timeSec),
              kind: "event" as const,
              label,
              emphasis,
              accentColor: resolveMarkerAccentColor(m),
              note: normalizeMarkerSurfaceNote(m) || null,
              origin: normalizeMarkerOrigin(m?.origin ?? m?.annotation?.origin, ""),
              frameIndex: Number.isFinite(Number(m?.frameIndex)) ? Number(m.frameIndex) : null,
            },
          ];
        }

        if (!isPhaseLikeMarker(m)) {
          return [
            {
              id: String(m.id),
              timeSec: Number(m.timeSec),
              kind: "event" as const,
              label,
              emphasis: "normal" as const,
              accentColor: resolveMarkerAccentColor(m),
              note: normalizeMarkerSurfaceNote(m) || null,
              origin: normalizeMarkerOrigin(m?.origin ?? m?.annotation?.origin, ""),
              frameIndex: Number.isFinite(Number(m?.frameIndex)) ? Number(m.frameIndex) : null,
            },
          ];
        }

        // R2 boundary:
        // phase markers stay out of the ruler semantic point-marker layer.
        // Phase semantics remain range-driven for now.
        return [];
      })
      .sort((a: { timeSec: number }, b: { timeSec: number }) => a.timeSec - b.timeSec);
  }, [norm.markers]);

  const visibleSemanticMarkerCount = useMemo(() => {
    return rulerSemanticMarkers.filter((marker) =>
      isRulerSemanticMarkerVisible(marker, {
        viewportStartSec: effectiveRulerViewportStartSec,
        viewportEndSec: effectiveRulerViewportEndSec,
        showEvents: showRulerEventMarkers,
        showBeats: showRulerBeatMarkers,
        activeMarkerId,
      }),
    ).length;
  }, [
    activeMarkerId,
    rulerSemanticMarkers,
    showRulerEventMarkers,
    showRulerBeatMarkers,
    effectiveRulerViewportStartSec,
    effectiveRulerViewportEndSec,
  ]);

  const markerById = useMemo(() => {
    const map = new Map<string, any>();
    for (const marker of norm.markers ?? []) {
      map.set(String((marker as any)?.id ?? ""), marker);
    }
    return map;
  }, [norm.markers]);

  const markerBrowserEntries = useMemo<MarkerBrowserEntry[]>(() => {
    const query = markerBrowserQuery.trim().toLowerCase();
    return (norm.markers ?? [])
      .map((marker: any) => {
        const label = String(marker?.label ?? "").trim();
        const note = normalizeMarkerSurfaceNote(marker);
        const type = normalizeMarkerSurfaceType(marker?.type, "marker");
        const origin = normalizeMarkerOrigin(marker?.origin ?? marker?.annotation?.origin, "");
        const timeSec = Number(marker?.timeSec ?? 0);
        const frameIndex = Number.isFinite(Number(marker?.frameIndex)) ? Number(marker.frameIndex) : null;
        const visible = marker?.visible !== false;
        return {
          id: String(marker?.id ?? "").trim(),
          timeSec,
          label,
          note,
          type,
          origin,
          frameIndex,
          visible,
        };
      })
      .filter((marker: MarkerBrowserEntry) => marker.id && finiteNum(marker.timeSec))
      .filter((marker: MarkerBrowserEntry) => {
        if (!query) return true;
        const haystack = [
          marker.label,
          marker.note,
          marker.type,
          marker.origin,
          marker.frameIndex != null ? `f${marker.frameIndex}` : "",
          marker.timeSec.toFixed(2),
        ]
          .join(" ")
          .toLowerCase();
        return haystack.includes(query);
      })
      .sort((a: MarkerBrowserEntry, b: MarkerBrowserEntry) => a.timeSec - b.timeSec);
  }, [markerBrowserQuery, norm.markers]);

  const markerBrowserTypeOptions = useMemo(() => {
    return Array.from(new Set(markerBrowserEntries.map((marker) => marker.type).filter(Boolean))).sort();
  }, [markerBrowserEntries]);

  const markerBrowserOriginOptions = useMemo(() => {
    return Array.from(new Set(markerBrowserEntries.map((marker) => marker.origin).filter(Boolean))).sort();
  }, [markerBrowserEntries]);

  const filteredMarkerBrowserEntries = useMemo(() => {
    return markerBrowserEntries.filter((marker) => {
      if (markerBrowserTypeFilter !== "all" && marker.type !== markerBrowserTypeFilter) return false;
      if (markerBrowserOriginFilter !== "all" && marker.origin !== markerBrowserOriginFilter) return false;
      return true;
    });
  }, [markerBrowserEntries, markerBrowserOriginFilter, markerBrowserTypeFilter]);

  const groupedMarkerBrowserEntries = useMemo(() => {
    const groups = new Map<string, MarkerBrowserEntry[]>();
    for (const marker of filteredMarkerBrowserEntries) {
      const key = markerBrowserGroupMode === "origin"
        ? formatMarkerOriginLabel(marker.origin || "marker")
        : markerBrowserGroupMode === "type"
          ? formatMarkerTypeLabel(marker.type)
          : timeBucketLabel(marker.timeSec);
      const current = groups.get(key) ?? [];
      current.push(marker);
      groups.set(key, current);
    }

    return Array.from(groups.entries()).map(([label, markers]) => ({
      label,
      markers: markers.slice().sort((a, b) => a.timeSec - b.timeSec),
    }));
  }, [filteredMarkerBrowserEntries, markerBrowserGroupMode]);

  const selectedMarkerIdSet = useMemo(() => new Set(selectedMarkerIds), [selectedMarkerIds]);

  const selectedMarkerBrowserEntries = useMemo(() => {
    return filteredMarkerBrowserEntries.filter((marker) => selectedMarkerIdSet.has(marker.id));
  }, [filteredMarkerBrowserEntries, selectedMarkerIdSet]);

  const markerJumpTargets = useMemo(() => {
    return rulerSemanticMarkers
      .map((marker) => ({
        id: String(marker.id ?? "").trim(),
        timeSec: Number(marker.timeSec),
        type: normalizeMarkerSurfaceType(markerById.get(String(marker.id ?? ""))?.type, "marker"),
        origin: normalizeMarkerOrigin(markerById.get(String(marker.id ?? ""))?.origin, ""),
      }))
      .filter((marker) => marker.id && finiteNum(marker.timeSec))
      .sort((a, b) => a.timeSec - b.timeSec);
  }, [markerById, rulerSemanticMarkers]);

  const activeMarkerNavType = useMemo(() => {
    const activeId = String(activeMarkerId ?? "").trim();
    if (!activeId) return "";
    return normalizeMarkerSurfaceType(markerById.get(activeId)?.type, "");
  }, [activeMarkerId, markerById]);

  const activeMarkerNavOrigin = useMemo(() => {
    const activeId = String(activeMarkerId ?? "").trim();
    if (!activeId) return "";
    return normalizeMarkerOrigin(markerById.get(activeId)?.origin, "");
  }, [activeMarkerId, markerById]);

  const filteredMarkerJumpTargets = useMemo(() => {
    if (activeMarkerNavOrigin && activeMarkerNavType) {
      const sameOriginAndType = markerJumpTargets.filter(
        (marker) => marker.origin === activeMarkerNavOrigin && marker.type === activeMarkerNavType,
      );
      if (sameOriginAndType.length >= 2) return sameOriginAndType;
    }

    if (activeMarkerNavOrigin) {
      const sameOrigin = markerJumpTargets.filter((marker) => marker.origin === activeMarkerNavOrigin);
      if (sameOrigin.length >= 2) return sameOrigin;
    }

    if (!activeMarkerNavType) return markerJumpTargets;
    const sameType = markerJumpTargets.filter((marker) => marker.type === activeMarkerNavType);
    return sameType.length >= 2 ? sameType : markerJumpTargets;
  }, [activeMarkerNavOrigin, activeMarkerNavType, markerJumpTargets]);

  const selectedRange = useMemo(() => {
    const selectedId = String(norm.selectedRangeId ?? "").trim();
    if (!selectedId) return null;
    return (norm.ranges ?? []).find((range: any) => String(range?.id ?? "").trim() === selectedId) ?? null;
  }, [norm.ranges, norm.selectedRangeId]);

  const selectedPromotedRange = useMemo(() => {
    if (!selectedRange || selectedRange?.markerSpan?.source !== "marker_span") return null;
    return selectedRange;
  }, [selectedRange]);

  const selectedRangeStartMarker = useMemo(() => {
    const markerId = String(selectedPromotedRange?.markerSpan?.startMarkerId ?? "").trim();
    return markerId ? markerById.get(markerId) ?? null : null;
  }, [markerById, selectedPromotedRange]);

  const selectedRangeEndMarker = useMemo(() => {
    const markerId = String(selectedPromotedRange?.markerSpan?.endMarkerId ?? "").trim();
    return markerId ? markerById.get(markerId) ?? null : null;
  }, [markerById, selectedPromotedRange]);

  const selectedTimelineClip = useMemo(() => {
    const clipId = String(selectedClipId ?? "").trim();
    return clipId ? clipById.get(clipId) ?? null : null;
  }, [clipById, selectedClipId]);

  const selectedTimelineTrack = useMemo(() => {
    const trackId = String(selectedTimelineClip?.trackId ?? "").trim();
    if (!trackId) return null;
    return (norm.renderTracks ?? []).find((track) => String(track?.id ?? "").trim() === trackId) ?? null;
  }, [norm.renderTracks, selectedTimelineClip]);

  const rangeById = useMemo(() => {
    const map = new Map<string, any>();
    for (const range of norm.ranges ?? []) {
      const rangeId = String((range as any)?.id ?? "").trim();
      if (rangeId) map.set(rangeId, range);
    }
    return map;
  }, [norm.ranges]);

  const selectedClipFollowsRangeUpdates = useMemo(() => {
    return selectedTimelineClip?.rangeAnchor?.followUpdates !== false;
  }, [selectedTimelineClip]);

  const anchoredTrackBadges = useMemo(() => {
    const tracks = Array.isArray((state as any)?.tracks) ? ((state as any).tracks as any[]) : [];
    return new Map(
      tracks
        .map((track) => {
          const trackId = String(track?.id ?? "").trim();
          const anchor = track?.rangeAnchor ?? null;
          const rangeId = String(anchor?.rangeId ?? "").trim();
          if (!trackId || !rangeId) return null;
          const boundRange = rangeById.get(rangeId) ?? null;
          return [trackId, {
            rangeId,
            label: String(boundRange?.label ?? formatRangeKindLabel(boundRange?.kind)).trim() || "Anchored Range",
            followUpdates: anchor?.followUpdates !== false,
          }] as const;
        })
        .filter(Boolean) as Array<readonly [string, { rangeId: string; label: string; followUpdates: boolean }]>,
    );
  }, [rangeById, state]);

  useEffect(() => {
    if (!selectedPromotedRange) {
      setRangeInspectorDraft(EMPTY_RANGE_INSPECTOR_DRAFT);
      return;
    }

    setRangeInspectorDraft(buildRangeInspectorDraft(selectedPromotedRange));
  }, [selectedPromotedRange]);

  const markerNavigationScopeLabel = useMemo(() => {
    if (!filteredMarkerJumpTargets.length || !activeMarkerId) return "all markers";
    if (
      activeMarkerNavOrigin &&
      activeMarkerNavType &&
      filteredMarkerJumpTargets.every(
        (marker) => marker.origin === activeMarkerNavOrigin && marker.type === activeMarkerNavType,
      )
    ) {
      return `${formatMarkerOriginLabel(activeMarkerNavOrigin).toLowerCase()} ${formatMarkerTypeLabel(activeMarkerNavType).toLowerCase()}s`;
    }
    if (activeMarkerNavOrigin && filteredMarkerJumpTargets.every((marker) => marker.origin === activeMarkerNavOrigin)) {
      return `${formatMarkerOriginLabel(activeMarkerNavOrigin).toLowerCase()} markers`;
    }
    if (activeMarkerNavType && filteredMarkerJumpTargets.every((marker) => marker.type === activeMarkerNavType)) {
      return `${formatMarkerTypeLabel(activeMarkerNavType).toLowerCase()} markers`;
    }
    return "all markers";
  }, [activeMarkerId, activeMarkerNavOrigin, activeMarkerNavType, filteredMarkerJumpTargets]);

  const activeMarkerTargetIndex = useMemo(() => {
    const activeId = String(activeMarkerId ?? "").trim();
    if (!activeId) return -1;
    return filteredMarkerJumpTargets.findIndex((marker) => marker.id === activeId);
  }, [activeMarkerId, filteredMarkerJumpTargets]);

  useEffect(() => {
    const activeId = String(activeMarkerIdRef.current ?? "").trim();
    if (!activeId) return;
    if (markerById.has(activeId)) return;
    setActiveMarkerId(null);
  }, [markerById]);

  useEffect(() => {
    writeMarkerBrowserPrefs({
      query: markerBrowserQuery,
      typeFilter: markerBrowserTypeFilter,
      originFilter: markerBrowserOriginFilter,
      groupMode: markerBrowserGroupMode,
    });
  }, [markerBrowserGroupMode, markerBrowserOriginFilter, markerBrowserQuery, markerBrowserTypeFilter]);

  useEffect(() => {
    setSelectedMarkerIds((prev) => prev.filter((id) => filteredMarkerBrowserEntries.some((marker) => marker.id === id)));
  }, [filteredMarkerBrowserEntries]);

  useEffect(() => {
    if (hasHydratedPersistedMarkersRef.current) return;
    if ((norm.markers ?? []).length > 0) {
      hasHydratedPersistedMarkersRef.current = true;
      return;
    }

    const persistedMarkers = readPersistedMarkerSession();
    if (!persistedMarkers.length) {
      hasHydratedPersistedMarkersRef.current = true;
      return;
    }

    for (const marker of persistedMarkers) {
      dispatch({
        type: "marker/add",
        payload: { marker },
        meta: { source: "system", note: "restore persisted markers" },
      } as any);
    }

    hasHydratedPersistedMarkersRef.current = true;
  }, [dispatch, norm.markers]);

  useEffect(() => {
    if (!hasHydratedPersistedMarkersRef.current) return;
    writePersistedMarkerSession(norm.markers ?? []);
  }, [norm.markers]);

  const setActiveMarker = useCallback((markerId: string | null, source: string) => {
    const nextId = String(markerId ?? "").trim() || null;
    if (source) semanticLastNavCommandRef.current = source;
    setActiveMarkerId(nextId);
  }, []);

  const persistMarkerSessionRemove = useCallback((markerIds: string[]) => {
    const removeSet = new Set(markerIds.map((markerId) => String(markerId ?? "").trim()).filter(Boolean));
    if (!removeSet.size) return;
    writePersistedMarkerSession((norm.markers ?? []).filter((marker: any) => !removeSet.has(String(marker?.id ?? "").trim())));
  }, [norm.markers]);

  const persistMarkerSessionUpsert = useCallback((markers: unknown[]) => {
    const nextById = new Map(
      (norm.markers ?? []).map((marker: any) => [String(marker?.id ?? "").trim(), marker] as const).filter(([id]: readonly [string, any]) => !!id),
    );

    for (const marker of markers ?? []) {
      const markerId = String((marker as any)?.id ?? "").trim();
      if (!markerId) continue;
      nextById.set(markerId, marker);
    }

    writePersistedMarkerSession(Array.from(nextById.values()));
  }, [norm.markers]);

  const selectedRangeWindow = useMemo(() => {
    const selectedId = String(norm.selectedRangeId ?? "").trim();
    if (!selectedId) return null;

    const selected = (norm.ranges ?? []).find(
      (range: any) => String(range?.id ?? "").trim() === selectedId,
    );
    if (!selected) return null;

    const start = Number((selected as any)?.startSec);
    const end = Number((selected as any)?.endSec);
    if (!finiteNum(start) || !finiteNum(end)) return null;

    const startSec = Math.max(0, Math.min(start, end));
    const endSec = Math.max(startSec, Math.max(start, end));
    return { startSec, endSec };
  }, [norm.selectedRangeId, norm.ranges]);

  const getMarkerSurfaceAnchor = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) {
      const fallbackX = typeof window !== "undefined" ? Math.round(window.innerWidth * 0.5) : 420;
      const fallbackY = typeof window !== "undefined" ? Math.round(window.innerHeight * 0.35) : 240;
      return { clientX: fallbackX, clientY: fallbackY };
    }

    const rect = scroller.getBoundingClientRect();
    return {
      clientX: Math.round(rect.left + Math.min(rect.width - 16, Math.max(16, rect.width * 0.5))),
      clientY: Math.round(rect.top + Math.min(rect.height - 24, Math.max(24, rect.height * 0.25))),
    };
  }, []);

  const openMarkerSurface = useCallback(
    (
      detail: {
        markerId?: string;
        clientX?: number;
        clientY?: number;
        label?: string | null;
      },
      mode: MarkerActionSurfaceMode,
    ) => {
      const markerId = String(detail?.markerId ?? "").trim();
      if (!markerId) return;

      const marker = markerById.get(markerId);
      if (!marker) return;

      const anchor = getMarkerSurfaceAnchor();
      const clientX = finiteNum(detail?.clientX) ? Number(detail.clientX) : anchor.clientX;
      const clientY = finiteNum(detail?.clientY) ? Number(detail.clientY) : anchor.clientY;

      setActiveMarker(markerId, `marker-surface:${mode}`);
      setMarkerSurfaceDraft(buildMarkerSurfaceDraft({
        ...marker,
        label:
          typeof marker?.label === "string"
            ? marker.label
            : typeof detail?.label === "string"
              ? detail.label
              : "",
      }));
      setMarkerSurface({ markerId, clientX, clientY, mode });
    },
    [getMarkerSurfaceAnchor, markerById, setActiveMarker],
  );

  const closeMarkerSurface = useCallback(() => {
    setMarkerSurface(null);
    setMarkerSurfaceDraft(EMPTY_MARKER_SURFACE_DRAFT);
  }, []);

  const markerSurfacePosition = useMemo(() => {
    if (!markerSurface) return null;

    const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1280;
    const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 720;
    const estimatedWidth = markerSurface.mode === "edit"
      ? MARKER_SURFACE_ESTIMATED_EDIT_WIDTH_PX
      : MARKER_SURFACE_ESTIMATED_MENU_WIDTH_PX;
    const estimatedHeight =
      markerSurface.mode === "edit"
        ? MARKER_SURFACE_ESTIMATED_EDIT_HEIGHT_PX
        : MARKER_SURFACE_ESTIMATED_MENU_HEIGHT_PX;

    const left = clamp(
      markerSurface.clientX + 8,
      MARKER_SURFACE_VIEWPORT_GAP_PX,
      Math.max(MARKER_SURFACE_VIEWPORT_GAP_PX, viewportWidth - estimatedWidth - MARKER_SURFACE_VIEWPORT_GAP_PX),
    );

    const availableAbove = markerSurface.clientY - MARKER_SURFACE_ANCHOR_GAP_PX - MARKER_SURFACE_VIEWPORT_GAP_PX;
    const desiredUpwardTop = markerSurface.clientY - estimatedHeight - MARKER_SURFACE_ANCHOR_GAP_PX;
    const desiredDownwardTop = markerSurface.clientY + MARKER_SURFACE_ANCHOR_GAP_PX;
    const availableBelow = viewportHeight - desiredDownwardTop - MARKER_SURFACE_VIEWPORT_GAP_PX;
    const canOpenUpward = desiredUpwardTop >= MARKER_SURFACE_VIEWPORT_GAP_PX;
    const canOpenDownward = desiredDownwardTop + estimatedHeight <= viewportHeight - MARKER_SURFACE_VIEWPORT_GAP_PX;
    const openUpward = canOpenUpward
      ? true
      : canOpenDownward
        ? false
        : availableAbove >= availableBelow;

    const height = Math.max(
      0,
      Math.min(estimatedHeight, openUpward ? availableAbove : availableBelow),
    );

    const top = openUpward
      ? clamp(
          markerSurface.clientY - height - MARKER_SURFACE_ANCHOR_GAP_PX,
          MARKER_SURFACE_VIEWPORT_GAP_PX,
          Math.max(MARKER_SURFACE_VIEWPORT_GAP_PX, viewportHeight - height - MARKER_SURFACE_VIEWPORT_GAP_PX),
        )
      : clamp(
          desiredDownwardTop,
          MARKER_SURFACE_VIEWPORT_GAP_PX,
          Math.max(MARKER_SURFACE_VIEWPORT_GAP_PX, viewportHeight - height - MARKER_SURFACE_VIEWPORT_GAP_PX),
        );

    return {
      left,
      top,
      width: estimatedWidth,
      height,
      transformOrigin: openUpward ? "bottom left" : "top left",
    };
  }, [markerSurface]);

  const openMarkerBrowser = useCallback(() => {
    setMarkerBrowserOpen(true);
  }, []);

  const closeMarkerBrowser = useCallback(() => {
    setMarkerBrowserOpen(false);
  }, []);

  const toggleMarkerBrowserSelection = useCallback((markerId: string) => {
    setSelectedMarkerIds((prev) => (
      prev.includes(markerId)
        ? prev.filter((id) => id !== markerId)
        : [...prev, markerId]
    ));
  }, []);

  const selectAllVisibleMarkers = useCallback(() => {
    setSelectedMarkerIds(filteredMarkerBrowserEntries.map((marker) => marker.id));
  }, [filteredMarkerBrowserEntries]);

  const clearSelectedMarkers = useCallback(() => {
    setSelectedMarkerIds([]);
  }, []);

  const promoteMarkersToRange = useCallback((markerIds: string[], source: string) => {
    const orderedMarkers = markerIds
      .map((markerId) => markerById.get(markerId))
      .filter(Boolean);
    if (orderedMarkers.length < 2) return null;

    const promotedAt = Date.now();
    const range = buildPromotedRangeFromMarkers(orderedMarkers, promotedAt);
    if (!range) return null;

    const transactionId = makeId();
    dispatch({
      type: "range/add",
      payload: { range },
      meta: { source: "user", note: source, ts: promotedAt, transactionId },
    } as any);
    dispatch({
      type: "range/select",
      payload: { rangeId: range.id },
      meta: { source: "user", note: `${source}:select`, ts: promotedAt, transactionId },
    } as any);

    return range.id;
  }, [dispatch, markerById]);

  const promoteSelectedMarkersToRange = useCallback(() => {
    if (selectedMarkerIds.length < 2) return;
    promoteMarkersToRange(selectedMarkerIds, "marker-browser/promote-range");
  }, [promoteMarkersToRange, selectedMarkerIds]);

  const closeRangeInspector = useCallback(() => {
    dispatch({
      type: "range/select",
      payload: { rangeId: null },
      meta: { source: "user", note: "range-inspector/close" },
    } as any);
  }, [dispatch]);

  const saveSelectedPromotedRange = useCallback(() => {
    if (!selectedPromotedRange) return;

    const nextRange = {
      ...(selectedPromotedRange as any),
      label: rangeInspectorDraft.label.trim() || undefined,
      kind: normalizeRangeInspectorKind(rangeInspectorDraft.kind, String(selectedPromotedRange?.kind ?? "selection")),
    };

    dispatch({
      type: "range/add",
      payload: { range: nextRange },
      meta: { source: "user", note: "range-inspector/save" },
    } as any);
  }, [dispatch, rangeInspectorDraft.kind, rangeInspectorDraft.label, selectedPromotedRange]);

  const rebindSelectedRangeFromMarkerSelection = useCallback(() => {
    if (!selectedPromotedRange || selectedMarkerIds.length < 2) return;

    const orderedMarkers = selectedMarkerIds
      .map((markerId) => markerById.get(markerId))
      .filter(Boolean);
    if (orderedMarkers.length < 2) return;

    const rebound = buildPromotedRangeFromMarkers(orderedMarkers, Date.now());
    if (!rebound) return;

    dispatch({
      type: "range/add",
      payload: {
        range: {
          ...rebound,
          id: selectedPromotedRange.id,
          label: rangeInspectorDraft.label.trim() || rebound.label,
          kind: normalizeRangeInspectorKind(rangeInspectorDraft.kind, rebound.kind),
        },
      },
      meta: { source: "user", note: "range-inspector/rebind-markers" },
    } as any);
  }, [dispatch, markerById, rangeInspectorDraft.kind, rangeInspectorDraft.label, selectedMarkerIds, selectedPromotedRange]);

  const bindSelectedClipToPromotedRange = useCallback(() => {
    if (!selectedPromotedRange || !selectedTimelineClip) return;

    const startSec = Math.max(0, Math.min(Number(selectedPromotedRange.startSec ?? 0), Number(selectedPromotedRange.endSec ?? 0)));
    const endSec = Math.max(startSec, Math.max(Number(selectedPromotedRange.startSec ?? 0), Number(selectedPromotedRange.endSec ?? 0)));
    const boundAt = Date.now();
    const followUpdates = selectedTimelineClip?.rangeAnchor?.followUpdates !== false;

    dispatch({
      type: "clip/patch",
      payload: {
        clipId: String(selectedTimelineClip.id),
        patch: {
          startSec,
          endSec,
          durSec: Math.max(0, endSec - startSec),
          rangeAnchor: buildRangeAnchorBinding(selectedPromotedRange, boundAt, followUpdates),
        },
      },
      meta: { source: "user", note: "range-inspector/bind-selected-clip" },
    } as any);
  }, [dispatch, selectedPromotedRange, selectedTimelineClip]);

  const toggleSelectedClipRangeFollowUpdates = useCallback((followUpdates: boolean) => {
    if (!selectedTimelineClip?.rangeAnchor?.rangeId) return;

    dispatch({
      type: "clip/patch",
      payload: {
        clipId: String(selectedTimelineClip.id),
        patch: {
          rangeAnchor: {
            ...(selectedTimelineClip.rangeAnchor as any),
            followUpdates,
          },
        },
      },
      meta: { source: "user", note: followUpdates ? "range-inspector/follow-updates-on" : "range-inspector/follow-updates-off" },
    } as any);
  }, [dispatch, selectedTimelineClip]);

  const anchorSelectedClipLaneToPromotedRange = useCallback(() => {
    const trackId = String(selectedTimelineClip?.trackId ?? "").trim();
    if (!selectedPromotedRange || !trackId) return;

    const boundAt = Date.now();
    dispatch({
      type: "track/patch",
      payload: {
        trackId,
        patch: {
          rangeAnchor: buildRangeAnchorBinding(selectedPromotedRange, boundAt, true),
        },
      },
      meta: { source: "user", note: "range-inspector/anchor-selected-lane" },
    } as any);
  }, [dispatch, selectedPromotedRange, selectedTimelineClip]);

  const applyBulkVisibility = useCallback((visible: boolean) => {
    const targets = selectedMarkerBrowserEntries;
    if (!targets.length) return;
    const nextMarkers: any[] = [];
    for (const markerEntry of targets) {
      const marker = markerById.get(markerEntry.id);
      if (!marker) continue;
      const nextMarker = {
        ...(marker as any),
        visible,
      };
      nextMarkers.push(nextMarker);
      dispatch({
        type: "marker/add",
        payload: {
          marker: nextMarker,
        },
        meta: { source: "user", note: visible ? "marker-browser/show-selected" : "marker-browser/hide-selected" },
      } as any);
    }
    persistMarkerSessionUpsert(nextMarkers);
  }, [dispatch, markerById, persistMarkerSessionUpsert, selectedMarkerBrowserEntries]);

  const deleteSelectedMarkers = useCallback(() => {
    const targets = selectedMarkerBrowserEntries;
    if (!targets.length) return;
    removeTimelineMarkersWithPersistence({
      dispatch,
      markers: norm.markers ?? [],
      markerIds: targets.map((markerEntry) => markerEntry.id),
      markerNote: "marker-browser/delete-selected",
      annotationNote: "marker-browser/delete-selected:hide-annotation",
    });
    setSelectedMarkerIds([]);
  }, [dispatch, norm.markers, selectedMarkerBrowserEntries]);

  const exportMarkerNotes = useCallback((format: "json" | "text") => {
    const targets = selectedMarkerBrowserEntries.length ? selectedMarkerBrowserEntries : filteredMarkerBrowserEntries;
    if (!targets.length || typeof window === "undefined") return;

    if (format === "text") {
      const text = targets
        .map((marker) => {
          const title = marker.label || "Untitled marker";
          const context = `${formatMarkerOriginLabel(marker.origin || "marker")} · ${formatMarkerTypeLabel(marker.type)} · ${marker.frameIndex != null ? `F${marker.frameIndex}` : `${marker.timeSec.toFixed(2)}s`}`;
          return `${title}\n${context}${marker.note ? `\n${marker.note}` : ""}`;
        })
        .join("\n\n");
      if (navigator.clipboard?.writeText) {
        void navigator.clipboard.writeText(text);
      }
      return;
    }

    const payload = targets.map((marker) => ({
      id: marker.id,
      label: marker.label,
      note: marker.note,
      type: marker.type,
      origin: marker.origin,
      timeSec: marker.timeSec,
      frameIndex: marker.frameIndex,
      visible: marker.visible,
    }));
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "omnicine-marker-notes.json";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  }, [filteredMarkerBrowserEntries, selectedMarkerBrowserEntries]);

  const jumpToMarker = useCallback(
    (markerId: string, source = "marker/jump") => {
      const marker = markerById.get(markerId);
      if (!marker) return;

      const timeSec = clamp(Number((marker as any)?.timeSec ?? 0), 0, Math.max(0, norm.durationSec));
      setActiveMarker(String(markerId), source);
      dispatch({
        type: "timeline/setPlayhead",
        payload: { playheadSec: timeSec },
        meta: { source: "user", note: "marker surface jump" },
      } as any);
    },
    [dispatch, markerById, norm.durationSec, setActiveMarker],
  );

  const deleteMarker = useCallback(
    (markerId: string) => {
      removeTimelineMarkersWithPersistence({
        dispatch,
        markers: norm.markers ?? [],
        markerIds: [markerId],
        markerNote: "marker surface delete",
        annotationNote: "marker surface delete:hide-annotation",
      });
      if (String(activeMarkerIdRef.current ?? "") === String(markerId)) {
        setActiveMarker(null, "marker/delete");
      }
      closeMarkerSurface();
    },
    [dispatch, closeMarkerSurface, norm.markers, setActiveMarker],
  );

  const saveMarkerDraft = useCallback(
    (markerId: string, draft: MarkerSurfaceDraft) => {
      const marker = markerById.get(markerId);
      if (!marker) return;

      const nextLabel = draft.label.trim();
      const nextType = normalizeMarkerSurfaceType(draft.type, "note");
      const nextNote = draft.note.trim();
      const nextColor = normalizeMarkerSurfaceColor(draft.color);
      const nextMarker = {
        ...(marker as any),
        label: nextLabel || undefined,
        type: nextType,
        note: nextNote || null,
        description: nextNote || null,
        color: nextColor || null,
        visible: draft.visible,
      };

      dispatch({
        type: "marker/add",
        payload: { marker: nextMarker },
        meta: { source: "user", note: "marker surface semantic edit" },
      } as any);

      persistMarkerSessionUpsert([nextMarker]);

      closeMarkerSurface();
    },
    [dispatch, markerById, closeMarkerSurface, persistMarkerSessionUpsert],
  );

  const nudgeActiveMarkerByFrames = useCallback(
    (frameDelta: number, source: string) => {
      const activeId = String(activeMarkerIdRef.current ?? "").trim();
      if (!activeId) return;

      const marker = markerById.get(activeId);
      if (!marker) return;

      const fps = Math.max(1, Number(norm.snap?.fps ?? 30) || 30);
      const currentTimeSec = clamp(Number((marker as any)?.timeSec ?? 0), 0, Math.max(0, norm.durationSec));
      const nextTimeSec = clamp(currentTimeSec + (frameDelta / fps), 0, Math.max(0, norm.durationSec));
      if (Math.abs(nextTimeSec - currentTimeSec) < 1e-9) return;

      setActiveMarker(activeId, source);
      dispatch({
        type: "marker/move",
        payload: { markerId: activeId, timeSec: nextTimeSec },
        meta: { source: "user", note: `${source}:${frameDelta}` },
      } as any);
      dispatch({
        type: "timeline/setPlayhead",
        payload: { playheadSec: nextTimeSec },
        meta: { source: "user", note: `${source}:playhead` },
      } as any);
    },
    [dispatch, markerById, norm.durationSec, norm.snap?.fps, setActiveMarker],
  );

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const update = () => {
      const next = readScrollerViewportWidthPx(el);
      setViewportWidthPx((prev) => (prev === next ? prev : next));
    };

    update();

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(update);
      ro.observe(el);
    }

    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("resize", update);
      ro?.disconnect();
    };
  }, []);

  const reframeScrollToStartSec = useCallback((startSec: number) => {
    const el = scrollerRef.current;
    if (!el) return;

    const st = stateRef.current as any;
    const px = readPxPerSec(st);

    const now = typeof performance !== "undefined" ? performance.now() : Date.now();
    suppressViewportSyncUntilRef.current = now + 250;

    el.scrollLeft = Math.max(0, startSec * px);
  }, []);

  const zoomViewportToWindow = useCallback(
    (startSecRaw: number, endSecRaw: number, note: string) => {
      const durationSec = Math.max(0, norm.durationSec);
      const widthPx = Math.max(1, viewportWidthPxRef.current || viewportWidthPx);

      let startSec = clamp(startSecRaw, 0, durationSec);
      let endSec = clamp(endSecRaw, startSec, durationSec);

      if (endSec - startSec < 0.25) {
        const mid = (startSec + endSec) * 0.5;
        startSec = clamp(mid - 0.125, 0, durationSec);
        endSec = clamp(mid + 0.125, startSec, durationSec);
      }

      const nextSpanSec = Math.max(1e-3, endSec - startSec);
      const nextPxPerSec = clamp(widthPx / nextSpanSec, 20, 400);
      const maxStartSec = Math.max(0, durationSec - widthPx / Math.max(1e-6, nextPxPerSec));
      const nextStartSec = clamp(startSec, 0, maxStartSec);
      const nextEndSec = Math.min(durationSec, nextStartSec + widthPx / Math.max(1e-6, nextPxPerSec));

      const nextScrollLeftPx = Math.max(0, nextStartSec * nextPxPerSec);
      const now = typeof performance !== "undefined" ? performance.now() : Date.now();
      suppressViewportSyncUntilRef.current = now + 220;

      const node = scrollerRef.current;
      if (node) {
        lastViewportSyncRef.current = {
          viewportStartSec: nextStartSec,
          viewportEndSec: nextEndSec,
          scrollLeftPx: nextScrollLeftPx,
          scrollTopPx: node.scrollTop || 0,
        };
      }

      dispatch({
        type: "timeline/setZoom",
        payload: { pxPerSec: nextPxPerSec },
        meta: { source: "user", transient: true, note },
      } as any);

      dispatch({
        type: "timeline/setViewport",
        payload: { startSec: nextStartSec, endSec: nextEndSec },
        meta: { source: "user", transient: true, note: `${note}:viewport` },
      } as any);

      requestAnimationFrame(() => {
        const el = scrollerRef.current;
        if (!el) return;
        el.scrollLeft = nextScrollLeftPx;
      });
    },
    [dispatch, norm.durationSec, viewportWidthPx],
  );

  const zoomToSelectedRangeOrMarker = useCallback(() => {
    if (selectedRangeWindow) {
      const spanSec = Math.max(0, selectedRangeWindow.endSec - selectedRangeWindow.startSec);
      const padSec = Math.max(0.35, spanSec * 0.18);

      zoomViewportToWindow(
        selectedRangeWindow.startSec - padSec,
        selectedRangeWindow.endSec + padSec,
        "keyboard focus selected range",
      );
      return;
    }

    if (!filteredMarkerJumpTargets.length) return;

    const playheadSec = norm.playheadSec;
    const activeTarget = activeMarkerTargetIndex >= 0 ? filteredMarkerJumpTargets[activeMarkerTargetIndex] : null;

    let nearest = activeTarget ?? filteredMarkerJumpTargets[0];
    if (!activeTarget) {
      let bestDelta = Math.abs(nearest.timeSec - playheadSec);
      for (let i = 1; i < filteredMarkerJumpTargets.length; i += 1) {
        const candidate = filteredMarkerJumpTargets[i];
        const delta = Math.abs(candidate.timeSec - playheadSec);
        if (delta < bestDelta) {
          nearest = candidate;
          bestDelta = delta;
        }
      }
    }

    const currentSpanSec = Math.max(1e-3, norm.viewportEndSec - norm.viewportStartSec);
    const halfSpanSec = clamp(currentSpanSec * 0.28, 1.25, 4.5);
    zoomViewportToWindow(
      nearest.timeSec - halfSpanSec,
      nearest.timeSec + halfSpanSec,
      "keyboard focus marker neighborhood",
    );

    setActiveMarker(nearest.id, "keyboard/shift+z");

    dispatch({
      type: "timeline/setPlayhead",
      payload: { playheadSec: nearest.timeSec },
      meta: { source: "user", note: "keyboard focus nearest marker" },
    } as any);
  }, [
    activeMarkerTargetIndex,
    dispatch,
    filteredMarkerJumpTargets,
    norm.playheadSec,
    norm.viewportEndSec,
    norm.viewportStartSec,
    selectedRangeWindow,
    setActiveMarker,
    zoomViewportToWindow,
  ]);

  const focusCameraOnClipStart = useCallback(
    (startSec: number, durationSec: number, source: string) => {
      const D = finiteNum(durationSec) && durationSec > 0 ? durationSec : 120;

      followPlayheadRef.current = false;
      setFollowPlayhead(false);

      const t = clamp(startSec, 0, D);

      dispatch({
        type: "timeline/setPlayhead",
        payload: { playheadSec: t },
        meta: { source, note: "focus camera: playhead" },
      } as any);

      const span = Math.max(5, Math.min(20, D || 15));
      const nextViewportStart = Math.max(0, t - span * 0.25);
      const nextViewportEnd = Math.min(D, t + span * 0.75);

      dispatch({
        type: "timeline/setViewport",
        payload: { startSec: nextViewportStart, endSec: nextViewportEnd },
        meta: { source, note: "focus camera: viewport" },
      } as any);

      requestAnimationFrame(() => {
        reframeScrollToStartSec(nextViewportStart);
      });
    },
    [dispatch, reframeScrollToStartSec],
  );

  const requestMediaPanelMode = useCallback((detail: MediaPanelAuthorityRequest) => {
    const requestedMode = detail.mode ?? "closed";
    const nextMode = detail.source === "inlineWitness" && requestedMode === "overlay" ? "focus" : requestedMode;
    const requestSource = detail.source ?? "system";
    const isLowPrioritySource = requestSource === "system";
    const requestKind = detail.kind ?? mediaPanelIntent?.kind ?? "unknown";
    const shouldUseAudioWitness =
      (requestKind === "audio" || (requestKind === "unknown" && detail.hasLinkedAudio === true)) &&
      (nextMode === "focus" || nextMode === "overlay");
    const shouldUseVisualWitness =
      (requestKind === "video" || requestKind === "image") &&
      (nextMode === "focus" || nextMode === "overlay");

    if (mediaPanelPinnedRef.current && isLowPrioritySource && !detail.force) {
      return;
    }

    const hasIntentPayload =
      typeof detail.clipId !== "undefined" ||
      typeof detail.trackId !== "undefined" ||
      typeof detail.kind !== "undefined" ||
      typeof detail.mediaId !== "undefined" ||
      typeof detail.mediaUrl !== "undefined" ||
      typeof detail.label !== "undefined" ||
      typeof detail.clipLabel !== "undefined" ||
      typeof detail.transitionToken !== "undefined" ||
      typeof detail.source !== "undefined";

    if (hasIntentPayload) {
      setMediaPanelIntent((prev) => {
        const nextIntent: MediaPanelIntent = {
          clipId: detail.clipId ?? prev?.clipId,
          trackId: detail.trackId ?? prev?.trackId,
          kind: detail.kind ?? prev?.kind ?? "unknown",
          mediaId: detail.mediaId ?? prev?.mediaId,
          mediaUrl: detail.mediaUrl ?? prev?.mediaUrl,
          label: detail.label ?? prev?.label,
          clipLabel: detail.clipLabel ?? prev?.clipLabel,
          hasLinkedAudio: detail.hasLinkedAudio ?? prev?.hasLinkedAudio,
          transitionToken: detail.transitionToken ?? prev?.transitionToken,
          source: detail.source ?? prev?.source,
        };

        if (
          prev &&
          prev.clipId === nextIntent.clipId &&
          prev.trackId === nextIntent.trackId &&
          prev.kind === nextIntent.kind &&
          prev.mediaId === nextIntent.mediaId &&
          prev.mediaUrl === nextIntent.mediaUrl &&
          prev.label === nextIntent.label &&
          prev.clipLabel === nextIntent.clipLabel &&
          prev.hasLinkedAudio === nextIntent.hasLinkedAudio &&
          prev.transitionToken === nextIntent.transitionToken &&
          prev.source === nextIntent.source
        ) {
          mediaPanelIntentRef.current = prev;
          return prev;
        }

        mediaPanelIntentRef.current = nextIntent;
        return nextIntent;
      });
    }

    if (nextMode === "closed") {
      mediaPanelModeRef.current = "closed";
      setMediaPanelMode("closed");
      mediaPanelPinnedRef.current = false;
      setMediaPanelPinned(false);
      if (detail.preserveWitnessBar) {
        if (detail.source === "inlineWitness") {
          setAudioWitnessUi((prev) => ({
            ...prev,
            activeCapsuleId: undefined,
            activeLensId: null,
            pinnedMiniLensId: null,
          }));
        }
        return;
      }
      setAudioWitnessUi((prev) => ({
        ...prev,
        witnessBarVisible: false,
        activeLensId: null,
        pinnedMiniLensId: null,
      }));
      return;
    }

    if (nextMode === "ingest") {
      mediaPanelModeRef.current = "ingest";
      setMediaPanelMode("ingest");
      mediaPanelPinnedRef.current = false;
      setMediaPanelPinned(false);
      setAudioWitnessUi((prev) => ({
        ...prev,
        witnessBarVisible: false,
        activeLensId: null,
        pinnedMiniLensId: null,
      }));
      return;
    }

    if (shouldUseAudioWitness || shouldUseVisualWitness) {
      setWitnessAuditionMode(detail.clipId ? "clip" : "source");
      setAudioWitnessUi((prev) => ({
        ...prev,
        activeCapsuleId: detail.clipId ?? prev.activeCapsuleId,
        witnessBarVisible: true,
        focusScope: detail.clipId ? "clip" : prev.focusScope,
      }));
    } else {
      setAudioWitnessUi((prev) => ({
        ...prev,
        witnessBarVisible: false,
        activeLensId: null,
        pinnedMiniLensId: null,
      }));
    }

    if (nextMode === "focus") {
      const nextPinned = typeof detail.pinned === "boolean" ? detail.pinned : mediaPanelPinnedRef.current;
      mediaPanelModeRef.current = "focus";
      mediaPanelPinnedRef.current = nextPinned;
      setMediaPanelMode("focus");
      setMediaPanelPinned(nextPinned);
      return;
    }

    if (nextMode === "overlay") {
      const nextPinned = typeof detail.pinned === "boolean" ? detail.pinned : mediaPanelPinnedRef.current;
      mediaPanelModeRef.current = "overlay";
      mediaPanelPinnedRef.current = nextPinned;
      setMediaPanelMode("overlay");
      setMediaPanelPinned(nextPinned);
    }
  }, [mediaPanelIntent?.kind, setWitnessAuditionMode]);

  const inspectFocusClipMember = useCallback((member: MediaMember, trackKind: "audio" | "video") => {
    const focusClipId = String(bindingEditorState.focusClipId ?? playbackProjection.focusClipId ?? selectedClipId ?? "").trim();
    const selectedMetadata = focusClipId ? clipMediaMetadataByClipId[focusClipId] ?? null : null;

    requestMediaPanelMode({
      mode: "focus",
      pinned: true,
      force: true,
      preserveWitnessBar: true,
      clipId: focusClipId || undefined,
      kind: trackKind,
      mediaId: member.sourceAssetId || undefined,
      mediaUrl: member.mediaUrl ?? selectedMetadata?.mediaUrl ?? undefined,
      label: member.label,
      clipLabel: selectedMetadata?.sourceLabel ?? contextClipTitle,
      hasLinkedAudio: playbackProjection.hasLinkedAudio,
      source: "inlineWitness",
    });
    setWitnessAuditionMode("clip");
  }, [bindingEditorState.focusClipId, clipMediaMetadataByClipId, contextClipTitle, playbackProjection.focusClipId, playbackProjection.hasLinkedAudio, requestMediaPanelMode, selectedClipId, setWitnessAuditionMode]);

  const removeAudioClipById = useCallback((
    clipId: string,
    source: string = "audioWitness",
    options?: { closePanel?: boolean },
  ) => {
    const resolvedClipId = String(clipId ?? "").trim();
    if (!resolvedClipId) return;
    const closePanel = options?.closePanel ?? source === "inlineWitness";

    discardAuthoredSonicsForClip({
      clipId: resolvedClipId,
      emotionalIntentSegments: authoredEmotionalIntentSegmentsRef.current,
      fxIntentNodes: authoredFxIntentNodesRef.current,
      discardEmotionalIntentSegment: sonics.discardEmotionalIntentSegment,
      discardFxIntentNode: sonics.discardFxIntentNode,
    });

    dispatch({
      type: "clip/remove",
      payload: { clipId: resolvedClipId },
      meta: { source, note: "audio-witness:remove-clip" },
    } as any);

    dispatch({
      type: "clip/select",
      payload: { clipId: null },
      meta: { source, note: "audio-witness:clear-selection-after-remove" },
    } as any);

    if (closePanel) {
      requestMediaPanelMode({ mode: "closed", kind: "audio", source: "mediaPanel", force: true });
      return;
    }

    mediaPanelIntentRef.current = null;
    setMediaPanelIntent(null);
    mediaPanelModeRef.current = "focus";
    setMediaPanelMode("focus");
    mediaPanelPinnedRef.current = false;
    setMediaPanelPinned(false);
    setAudioWitnessUi((prev) => ({
      ...prev,
      activeCapsuleId: undefined,
      witnessBarVisible: false,
      activeLensId: null,
      pinnedMiniLensId: null,
    }));
  }, [dispatch, requestMediaPanelMode, sonics]);

  const removeActiveAudioClip = useCallback(() => {
    const clipId = String(activeAudioClipId ?? "").trim();
    if (!clipId) return;
    removeAudioClipById(clipId, "audioWitness", { closePanel: false });
  }, [activeAudioClipId, removeAudioClipById]);

  const authorEmotionalIntentSegment = useCallback((segment: EmotionalIntentSegment) => {
    const result = sonics.authorEmotionalIntentSegment(segment);
    showTimelineWhisper({
      type: "arc/upserted",
      operation: result.operation,
    });
  }, [showTimelineWhisper, sonics]);

  const discardEmotionalIntentSegment = useCallback((segmentId: string) => {
    sonics.discardEmotionalIntentSegment(segmentId);
  }, [sonics]);

  const authorFxIntentNode = useCallback((node: FxIntentNode) => {
    const result = sonics.authorFxIntentNode(node);
    showTimelineWhisper({
      type: "fx/upserted",
      operation: result.operation,
      nodeType: node.type,
    });
  }, [showTimelineWhisper, sonics]);

  const discardFxIntentNode = useCallback((nodeId: string) => {
    sonics.discardFxIntentNode(nodeId);
  }, [sonics]);

  const emitSurfaceIntent = useMemo(() => createSurfaceIntentEmitter({ hz: 8, source: "timeline" }), []);

  /* ---------------------------------------------------------- */
  /* Mount proof marker                                          */
  /* ---------------------------------------------------------- */

  useEffect(() => {
    document.documentElement.setAttribute("data-oc-timeline-mounted", "1");
    return () => {
      document.documentElement.removeAttribute("data-oc-timeline-mounted");
    };
  }, []);

  /* ---------------------------------------------------------- */
  /* Debug surface                                               */
  /* ---------------------------------------------------------- */

  useEffect(() => {
    const w = window as any;
    w.__oc_debug = w.__oc_debug || {};
    const laneModelDebugSnapshot = {
      laneCount: initialProductLaneModel.laneDefinitions.length,
      relationCount: initialProductLaneModel.dependencyRelations.length,
      capsuleCount: initialProductLaneModel.capsules.length,
      couplingCount: initialProductLaneModel.crossLaneCouplings.length,
      lanes: initialProductLaneModel.laneDefinitions.map((lane) => ({
        id: lane.id,
        title: lane.title,
        syncHealth: lane.syncHealth,
        couplingIds: Array.isArray((lane.metadata as Record<string, unknown> | undefined)?.crossLaneCouplings)
          ? ((lane.metadata as Record<string, unknown>).crossLaneCouplings as Array<{ id?: string }>).map((coupling) => coupling.id).filter(Boolean)
          : [],
      })),
      couplings: initialProductLaneModel.crossLaneCouplings.slice(),
      capsules: initialProductLaneModel.capsules.map((capsule) => ({
        id: capsule.id,
        title: capsule.title,
        laneIds: capsule.laneIds.slice(),
        couplingIds: Array.isArray((capsule.metadata as Record<string, unknown> | undefined)?.crossLaneCouplings)
          ? ((capsule.metadata as Record<string, unknown>).crossLaneCouplings as Array<{ id?: string }>).map((coupling) => coupling.id).filter(Boolean)
          : [],
      })),
    };
    w.__oc_debug.getLaneModelDebug = () => laneModelDebugSnapshot;
    w.__oc_debug.lastLaneModelDebug = laneModelDebugSnapshot;
    window.dispatchEvent(new CustomEvent(LANE_MODEL_DEBUG_EVENT, { detail: laneModelDebugSnapshot }));
    w.__oc_debug.getPublishStrip = () => {
      const st = stateRef.current as any;
      const annotations = Array.isArray(st?.annotations) ? st.annotations : [];
      const markers = Array.isArray(st?.markers) ? st.markers : [];
      const playheadSec = finiteNum(st?.playheadSec) ? Number(st.playheadSec) : 0;
      const durationSec = finiteNum(st?.durationSec) ? Number(st.durationSec) : 0;
      const signals = buildPublishStripSignals({
        npcRuntime: latestNpcRuntimeRef.current,
        surfaceIntent: latestSurfaceIntentRef.current,
        phaseRuntime: latestPhaseRuntimeRef.current,
        playheadSec,
      });

      const lastImportedContext = lastImportedPublishContextRef.current;
      const selectedOrFocusedClipId = String(
        st?.laneFocus?.sourceClipId ?? st?.selectedClipId ?? lastImportedContext?.clipId ?? "",
      ).trim();
      const selectedOrFocusedClip = selectedOrFocusedClipId
        ? pickFirstClipsContainer(st).find(
            (clip: any) => String(clip?.id ?? clip?.clipId ?? "").trim() === selectedOrFocusedClipId,
          ) ?? null
        : null;
      const importContext = lastImportedContext?.trackId
        ? lastImportedContext
        : selectedOrFocusedClip && String(selectedOrFocusedClip?.trackId ?? "").trim()
          ? {
              importedKind: normalizeImportedKind(selectedOrFocusedClip?.kind),
              clipId: selectedOrFocusedClipId,
              trackId: String(selectedOrFocusedClip?.trackId ?? "").trim(),
              sourceLabel: String(
                selectedOrFocusedClip?.label ?? selectedOrFocusedClip?.fileName ?? selectedOrFocusedClip?.name ?? "",
              ).trim() || null,
            }
          : null;

      return compileTimelinePublishStrip({
        durationSec,
        annotations,
        markers,
        signals,
        importContext,
      });
    };

    w.__oc_debug.timelineScrollEl = scrollerRef.current ?? null;

    w.__oc_debug.setFollowPlayhead = (enabled: unknown) => {
      const v = !!enabled;
      followPlayheadRef.current = v;
      setFollowPlayhead(v);
    };
    w.__oc_debug.getFollowPlayhead = () => !!followPlayheadRef.current;

    w.__oc_debug.getTransportPlaying = () => !!isPlayingRef.current;
    w.__oc_debug.getRulerZoomDebug = () => ({
      enabled: !!rulerZoomModeEnabledRef.current,
      localPxPerSec: rulerOnlyPxPerSecRef.current,
      localViewportStartSec: rulerOnlyViewportStartSecRef.current,
      statePxPerSec: readPxPerSec(stateRef.current),
    });
    const debugHost = window.location.hostname.toLowerCase();
    const allowLocalDebug =
      !!(import.meta as any)?.env?.DEV ||
      debugHost === "localhost" ||
      debugHost === "127.0.0.1" ||
      debugHost === "::1";
    if (allowLocalDebug) {
      w.__oc_debug.getSemanticMarkerDebug = () => ({
        activeMarkerId: activeMarkerIdRef.current,
        visibleSemanticMarkerCount,
        toggles: {
          events: showRulerEventMarkers,
          beats: showRulerBeatMarkers,
        },
        lastSemanticNavigationCommand: semanticLastNavCommandRef.current,
      });
    } else {
      delete w.__oc_debug.getSemanticMarkerDebug;
    }

    if (allowLocalDebug) {
      const buildMediaPanelDebugRequest = (
        detail?: Partial<MediaPanelAuthorityRequest>,
      ): MediaPanelAuthorityRequest => {
        const currentMediaPanelIntent = mediaPanelIntentRef.current;
        const requestedClipId =
          typeof detail?.clipId === "string" && detail.clipId.trim()
            ? detail.clipId.trim()
            : currentMediaPanelIntent?.clipId ?? selectedClipId ?? activeAudioClipId ?? undefined;
        const requestedClip = requestedClipId ? clipById.get(requestedClipId) ?? null : null;
        const requestedMetadata = requestedClipId ? clipMediaMetadataByClipId[requestedClipId] ?? null : null;
        const requestedKind =
          detail?.kind ??
          resolveClipMediaKind(
            requestedClip,
            requestedMetadata?.kind ?? currentMediaPanelIntent?.kind ?? activeWitnessMediaType,
          );
        const requestedMediaUrl =
          detail?.mediaUrl ??
          readClipMediaUrl(requestedClip) ??
          requestedMetadata?.mediaUrl ??
          currentMediaPanelIntent?.mediaUrl;
        const requestedLabel =
          detail?.label ??
          resolveTimelineClipTitle(
            requestedClip,
            requestedMetadata?.sourceLabel ?? currentMediaPanelIntent?.clipLabel ?? currentMediaPanelIntent?.label ?? null,
            requestedKind,
          );

        return {
          ...detail,
          clipId: requestedClipId,
            trackId:
              detail?.trackId ??
              (String(requestedClip?.trackId ?? currentMediaPanelIntent?.trackId ?? "").trim() || undefined),
          kind: requestedKind,
          mediaId:
            detail?.mediaId ??
              (String(requestedClip?.mediaId ?? requestedClip?.media?.id ?? currentMediaPanelIntent?.mediaId ?? "").trim() ||
                undefined),
          mediaUrl: requestedMediaUrl,
          label: requestedLabel,
          clipLabel: detail?.clipLabel ?? requestedLabel,
          hasLinkedAudio:
            typeof detail?.hasLinkedAudio === "boolean"
              ? detail.hasLinkedAudio
              : typeof requestedMetadata?.hasPlayableAudio === "boolean"
                ? requestedMetadata.hasPlayableAudio
                : currentMediaPanelIntent?.hasLinkedAudio,
          transitionToken: detail?.transitionToken ?? currentMediaPanelIntent?.transitionToken,
          source: detail?.source ?? "mediaPanel",
        };
      };

      const readMediaPanelDebugState = () => ({
        mode: mediaPanelModeRef.current,
        pinned: mediaPanelPinnedRef.current,
        intent: mediaPanelIntentRef.current ?? null,
        selectedClipId: selectedClipId ?? null,
        activeClipId: activeAudioClipId ?? null,
        activeKind: activeWitnessMediaType,
      });

      const invokeMediaPanelDebug = (mode: MediaPanelMode, detail?: Partial<MediaPanelAuthorityRequest>) => {
        const request = buildMediaPanelDebugRequest({ ...detail, mode });
        const hasResolvedTarget = !!(
          request.clipId ||
          request.mediaId ||
          request.mediaUrl ||
          mediaPanelIntentRef.current?.clipId
        );

        if (mode !== "closed" && !hasResolvedTarget) {
          return { error: "No media panel target resolved", request, state: readMediaPanelDebugState() };
        }

        requestMediaPanelMode(request);
        return { request, state: readMediaPanelDebugState() };
      };

      const closeMediaPanelDebug = (
        detail?: Partial<MediaPanelAuthorityRequest>,
        options?: { force?: boolean },
      ) => {
        const forceClose = options?.force === true || detail?.force === true;
        const request = buildMediaPanelDebugRequest({ ...detail, mode: "closed", force: forceClose });

        if (mediaPanelPinnedRef.current && !forceClose) {
          return {
            error: "Media dock is pinned; use forceClose() to bypass pin",
            request,
            state: readMediaPanelDebugState(),
          };
        }

        requestMediaPanelMode(request);
        return { request, state: readMediaPanelDebugState() };
      };

      const setPinnedMediaPanelDebug = (pinned: boolean, detail?: Partial<MediaPanelAuthorityRequest>) =>
        invokeMediaPanelDebug(mediaPanelModeRef.current === "overlay" ? "overlay" : "focus", {
          ...detail,
          pinned,
          force: detail?.force ?? true,
        });

      const mediaDockDebug = {
        getState: () => ({
          mode: mediaPanelModeRef.current,
          isPinned: mediaPanelPinnedRef.current,
          focusIntent: mediaPanelIntentRef.current ?? null,
        }),
        openFocus: (detail?: Partial<MediaPanelAuthorityRequest>) =>
          invokeMediaPanelDebug("focus", { ...detail, pinned: detail?.pinned ?? false }),
        openOverlay: (detail?: Partial<MediaPanelAuthorityRequest>) => invokeMediaPanelDebug("overlay", detail),
        close: (detail?: Partial<MediaPanelAuthorityRequest>) => closeMediaPanelDebug(detail),
        forceClose: (detail?: Partial<MediaPanelAuthorityRequest>) =>
          closeMediaPanelDebug({ ...detail, force: true }, { force: true }),
        pin: (detail?: Partial<MediaPanelAuthorityRequest>) => setPinnedMediaPanelDebug(true, detail),
        unpin: (detail?: Partial<MediaPanelAuthorityRequest>) => setPinnedMediaPanelDebug(false, detail),
        togglePin: (detail?: Partial<MediaPanelAuthorityRequest>) =>
          setPinnedMediaPanelDebug(!mediaPanelPinnedRef.current, detail),
      };

      w.__oc_debug.getMediaPanelDebug = readMediaPanelDebugState;
      w.__oc_debug.mediaDock = mediaDockDebug;
      w.__oc_debug.mediaPanelRequest = (detail?: Partial<MediaPanelAuthorityRequest>) => {
        const request = buildMediaPanelDebugRequest(detail);
        const hasResolvedTarget =
          request.mode === "closed" ||
          !!(request.clipId || request.mediaId || request.mediaUrl || mediaPanelIntentRef.current?.clipId);

        if (!hasResolvedTarget) {
          return { error: "No media panel target resolved", request, state: readMediaPanelDebugState() };
        }

        requestMediaPanelMode(request);
        return { request, state: readMediaPanelDebugState() };
      };
      w.__oc_debug.focusMediaDock = (detail?: Partial<MediaPanelAuthorityRequest>) =>
        invokeMediaPanelDebug("focus", { ...detail, pinned: detail?.pinned ?? false });
      w.__oc_debug.overlayMediaDock = (detail?: Partial<MediaPanelAuthorityRequest>) =>
        invokeMediaPanelDebug("overlay", detail);
      w.__oc_debug.pinMediaDock = (detail?: Partial<MediaPanelAuthorityRequest>) =>
        mediaDockDebug.pin(detail);
      w.__oc_debug.unpinMediaDock = (detail?: Partial<MediaPanelAuthorityRequest>) =>
        mediaDockDebug.unpin(detail);
      w.__oc_debug.closeMediaDock = (detail?: Partial<MediaPanelAuthorityRequest>) =>
        mediaDockDebug.close(detail);
      w.__oc_debug.forceCloseMediaDock = (detail?: Partial<MediaPanelAuthorityRequest>) =>
        mediaDockDebug.forceClose(detail);
    } else {
      delete w.__oc_debug.getMediaPanelDebug;
      delete w.__oc_debug.mediaDock;
      delete w.__oc_debug.mediaPanelRequest;
      delete w.__oc_debug.focusMediaDock;
      delete w.__oc_debug.overlayMediaDock;
      delete w.__oc_debug.pinMediaDock;
      delete w.__oc_debug.unpinMediaDock;
      delete w.__oc_debug.closeMediaDock;
      delete w.__oc_debug.forceCloseMediaDock;
    }

    w.__oc_debug.__attached = "TimelineShell";
    w.__oc_debug.version = "omnicine.timeline.shell.v40.r12RulerBasePivot";

    w.__oc_debug_state = w.__oc_debug_state || (() => stateRef.current);
    w.__oc_debug_clips =
      w.__oc_debug_clips ||
      (() => {
        const st: any = stateRef.current;
        return Array.isArray(st?.clips) ? st.clips : [];
      });

    w.__oc_debug.openHelp = (detail?: unknown) => {
      emitTimelineHelpOpen(detail as { firstRun?: boolean; key?: string; focus?: string } | undefined);
    };
    w.__oc_debug.closeHelp = () => {
      window.dispatchEvent(new CustomEvent(OMNICINE_TIMELINE_HELP_CLOSE_EVENT));
    };
    w.__oc_debug.toggleHelp = () => {
      window.dispatchEvent(new CustomEvent(OMNICINE_TIMELINE_HELP_TOGGLE_EVENT));
    };
  }, [
    activeAudioClipId,
    activeWitnessMediaType,
    clipById,
    clipMediaMetadataByClipId,
    dispatch,
    initialProductLaneModel,
    mediaPanelIntent,
    mediaPanelMode,
    requestMediaPanelMode,
    selectedClipId,
    showRulerEventMarkers,
    showRulerBeatMarkers,
    visibleSemanticMarkerCount,
  ]);

  /* ---------------------------------------------------------- */
  /* Transport playing event                                     */
  /* ---------------------------------------------------------- */

  useEffect(() => {
    return onTransportPlaying((detail) => {
      const on = !!detail?.enabled;
      isPlayingRef.current = on;
      setIsPlaying(on);
    });
  }, []);

  /* ---------------------------------------------------------- */
  /* Follow Playhead control                                     */
  /* ---------------------------------------------------------- */

  useEffect(() => {
    const offSet = onFollowPlayheadSet((detail) => {
      if (typeof detail?.enabled === "undefined") return;
      const v = !!detail.enabled;
      followPlayheadRef.current = v;
      setFollowPlayhead(v);
    });

    const offToggle = onFollowPlayheadToggle(() => {
      setFollowPlayhead((prev) => {
        const v = !prev;
        followPlayheadRef.current = v;
        return v;
      });
    });

    return () => {
      offSet();
      offToggle();
    };
  }, []);

  /* ---------------------------------------------------------- */
  /* AtomicRuns update bridge                                    */
  /* ---------------------------------------------------------- */

  useEffect(() => {
    return onAtomicRunsUpdate((detail) => {
      const runs = Array.isArray(detail?.runs) ? detail!.runs : [];
      const src = normalizeSource(detail?.source);

      dispatch({
        type: "atomicRuns/setAll",
        payload: { sessionId: detail?.sessionId, runs },
        meta: { source: src, note: String(detail?.note ?? "atomicRuns:update->setAll") },
      } as any);
    });
  }, [dispatch]);

  /* ---------------------------------------------------------- */
  /* AtomicRuns open intent                                      */
  /* ---------------------------------------------------------- */

  useEffect(() => {
    return onAtomicRunsOpen((detail) => {
      const s = Number(detail?.startSec ?? 0);
      if (!Number.isFinite(s)) return;

      dispatch({
        type: "timeline/setPlayhead",
        payload: { playheadSec: s },
        meta: { source: "user", note: `atomicRuns:open:${detail?.runId ?? ""}` },
      } as any);

      if (typeof detail?.runId === "string" && detail.runId.trim()) {
        dispatch({
          type: "atomicRuns/select",
          payload: { runId: detail.runId.trim() },
          meta: { source: "user", note: `atomicRuns:select:${detail.runId.trim()}` },
        } as any);
      }
    });
  }, [dispatch]);

  /* ---------------------------------------------------------- */
  /* Help open/close                                             */
  /* ---------------------------------------------------------- */

  const [helpOpen, setHelpOpen] = useState(false);
  const firstRunRef = useRef(false);

  const openHelp = useCallback((detail?: unknown) => {
    const d = detail as { firstRun?: boolean } | undefined;
    if (d?.firstRun) firstRunRef.current = true;
    setHelpOpen(true);
  }, []);

  const closeHelp = useCallback(() => {
    setHelpOpen(false);

    if (firstRunRef.current) {
      try {
        window.localStorage.setItem(TOUR_KEY, "done");
      } catch {
        // ignore
      }
      firstRunRef.current = false;
    }
  }, []);

  useEffect(() => {
    const c1 = onTimelineHelpOpen((detail) => openHelp(detail));
    const c2 = onTimelineHelpToggle(() => setHelpOpen((v) => !v));
    const c3 = onTimelineHelpClose(() => closeHelp());

    return () => {
      c1();
      c2();
      c3();
    };
  }, [openHelp, closeHelp]);

  useEffect(() => {
    if (!helpOpen) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeHelp();
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [helpOpen, closeHelp]);

  /* ---------------------------------------------------------- */
  /* First-run help trigger                                      */
  /* ---------------------------------------------------------- */

  useEffect(() => {
    let raf = 0;

    try {
      const done = window.localStorage.getItem(TOUR_KEY) === "done";
      if (done) return;

      raf = window.requestAnimationFrame(() => {
        emitTimelineHelpOpen({ firstRun: true, key: TOUR_KEY, focus: "quickstart" });
      });
    } catch {
      // ignore
    }

    return () => window.cancelAnimationFrame(raf);
  }, []);

  /* ---------------------------------------------------------- */
  /* Envelope AutoGen producer                                   */
  /* ---------------------------------------------------------- */

  useEffect(() => {
    return installSingletonService("envelopeAutoGen", () => {
      const w = window as any;
      const salt = w?.__oc_debug?.sessionId ?? w?.__oc_debug?.projectId ?? "omnicine";

      return startEnvelopeAutoGen({
        getState: () => stateRef.current as any,
        salt: String(salt),
        npcTrackId: "n1",
        cadenceMs: 250,
        source: "ai-assist",
      });
    });
  }, []);

  /* ---------------------------------------------------------- */
  /* NPC runtime loop                                            */
  /* ---------------------------------------------------------- */

  useEffect(() => {
    return installSingletonService("npcRuntimeLoop", () => {
      return startNpcRuntimeLoop({
        getState: () => stateRef.current as any,
        npcTrackId: "n1",
        cadenceMs: 120,
        source: "system",
      });
    });
  }, []);

  /* ---------------------------------------------------------- */
  /* Add Phase Ranges                                            */
  /* ---------------------------------------------------------- */

  const ensureDefaultPhaseRanges = useCallback(() => {
    const st = stateRef.current as any;

    const durationSec = finiteNum(st?.durationSec) ? Number(st.durationSec) : 0;
    if (!finiteNum(durationSec) || durationSec <= 0) return;

    const rangesNow = Array.isArray(st?.ranges) ? st.ranges : [];
    const phaseMap = findPhaseRangesByLabel(rangesNow);

    const seg = durationSec / 4;
    const bands = [
      { label: "phase:setup", start: 0, end: seg },
      { label: "phase:rising", start: seg, end: seg * 2 },
      { label: "phase:peak", start: seg * 2, end: seg * 3 },
      { label: "phase:resolve", start: seg * 3, end: durationSec },
    ];

    for (const b of bands) {
      const a = clamp(b.start, 0, durationSec);
      const c = clamp(b.end, 0, durationSec);
      const lo = Math.min(a, c);
      const hi = Math.max(a, c);

      const existing = phaseMap.get(b.label);

      if (existing?.id != null) {
        dispatch({
          type: "range/update",
          payload: { rangeId: String(existing.id), startSec: lo, endSec: hi },
          meta: { source: "user", note: `phaseRanges:update:${b.label}` },
        } as any);
      } else {
        dispatch({
          type: "range/add",
          payload: {
            range: {
              id: makeId(),
              startSec: lo,
              endSec: hi,
              kind: "selection",
              label: b.label,
              source: "user",
            },
          },
          meta: { source: "user", note: `phaseRanges:add:${b.label}` },
        } as any);
      }
    }
  }, [dispatch]);

  useEffect(() => {
    const cleanups = ADD_PHASE_RANGES_LEGACY_EVENTS.map((type, idx) => {
      const handler: EventListener = () => ensureDefaultPhaseRanges();
      return installSingletonListener(`addPhaseRanges_${idx}`, type, handler);
    });

    const offShared = onAddPhaseRanges(() => ensureDefaultPhaseRanges());

    return () => {
      for (const c of cleanups) c();
      offShared();
    };
  }, [ensureDefaultPhaseRanges]);

  /* ---------------------------------------------------------- */
  /* OmniCast phase emitter                                      */
  /* ---------------------------------------------------------- */

  useEffect(() => {
    const emitOmniCastPhase = (args: {
      sessionId?: string;
      phase: NarrativePhase;
      intensity01: number;
      playheadSec: number;
      durationSec: number;
    }) => {
      const detail = {
        sessionId: args.sessionId,
        phase: args.phase,
        intensity: args.intensity01,
        ts: Date.now(),
        source: "timeline" as const,
        playheadSec: args.playheadSec,
        durationSec: args.durationSec,
      };

      latestPhaseRuntimeRef.current = detail;

      window.dispatchEvent(new CustomEvent(OMNICAST_PHASE_EVENT, { detail }));

      const w = window as any;
      w.__oc_phase_runtime = detail;
    };

    return installSingletonService("omnicastPhaseEmitterFromRanges", () => {
      const tick = () => {
        const st = stateRef.current as any;

        const playheadSec = finiteNum(st?.playheadSec) ? Number(st.playheadSec) : 0;
        const ranges = (Array.isArray(st?.ranges) ? st.ranges : []) as Array<{
          startSec: number;
          endSec: number;
          label?: string | null;
          kind?: unknown;
        }>;

        const computed = computePhaseFromRanges(ranges, playheadSec);
        if (!computed) return;

        const intensityB = bucket01(computed.intensity01, 20);
        const sig = `${computed.phase}|${intensityB}`;

        const now = Date.now();
        const prev = lastPhaseRef.current;
        const minDt = 125;
        const okTime = !prev || now - prev.ts >= minDt;

        if (!prev || (sig !== prev.sig && okTime)) {
          lastPhaseRef.current = { sig, ts: now };

          const sessionId = getBoundSessionId() ?? undefined;

          emitOmniCastPhase({
            sessionId,
            phase: computed.phase,
            intensity01: intensityB,
            playheadSec,
            durationSec: computed.durationSec,
          });
        }
      };

      const id = window.setInterval(tick, 60);
      tick();

      return () => window.clearInterval(id);
    });
  }, []);

  /* ---------------------------------------------------------- */
  /* Fan-out bridge                                              */
  /* ---------------------------------------------------------- */

  const npc01Ref = useRef<{ attention: number; emotion: number; overload: number; safety: number } | null>(null);
  const npcTrackAnchor = useMemo(() => resolveTrackAnchorRange(norm.renderTracks, norm.ranges, "npc"), [norm.ranges, norm.renderTracks]);

  useEffect(() => {
    const onNpcRuntime: EventListener = (e) => {
      const raw = (e as CustomEvent).detail ?? {};
      const ts = finiteNum((raw as any)?.ts) ? Number((raw as any).ts) : Date.now();

      const probs = (raw as any)?.probs ?? {};
      const npc01 = {
        attention: safe01(probs?.attention, 0.5),
        emotion: safe01(probs?.emotion, 0.5),
        overload: safe01(probs?.overload, 0.0),
        safety: safe01(probs?.safety, 1.0),
      };
      npc01Ref.current = npc01;

      const sessionId = ((raw as any)?.sessionId ? String((raw as any).sessionId) : getBoundSessionId()) ?? undefined;
      const detail = {
        ...(raw ?? {}),
        sessionId,
        ts,
        anchorRange: npcTrackAnchor
          ? {
              rangeId: npcTrackAnchor.rangeId,
              label: npcTrackAnchor.label,
              kind: npcTrackAnchor.kind,
              startSec: npcTrackAnchor.startSec,
              endSec: npcTrackAnchor.endSec,
              followUpdates: npcTrackAnchor.followUpdates,
            }
          : null,
      };
      latestNpcRuntimeRef.current = detail;

      const npcLaneSig = [
        String((detail as any)?.mode ?? ""),
        Math.round((finiteNum((detail as any)?.playheadSec) ? Number((detail as any).playheadSec) : 0) * 2) / 2,
        Math.round(npc01.attention * 20),
        Math.round(npc01.emotion * 20),
        Math.round(npc01.overload * 20),
        Math.round(npc01.safety * 20),
        String(npcTrackAnchor?.rangeId ?? ""),
      ].join("|");
      if (latestNpcLaneSigRef.current !== npcLaneSig) {
        latestNpcLaneSigRef.current = npcLaneSig;
        setLatestNpcLaneRuntime(detail);
      }

      window.dispatchEvent(new CustomEvent(OMNICAST_NPC_RUNTIME_EVENT, { detail }));

      window.dispatchEvent(
        new CustomEvent(LUMINA_VIBE_SCORE_EVENT, {
          detail: {
            sessionId,
            mode: (detail as any)?.mode,
            probs: (detail as any)?.probs,
            ts: (detail as any)?.ts,
          },
        }),
      );

      window.dispatchEvent(new CustomEvent(OMNICINE_HUD_NPC_EVENT, { detail }));
    };

    return installSingletonListener("npcRuntimeFanout", OMNICINE_NPC_RUNTIME_EVENT, onNpcRuntime);
  }, [npcTrackAnchor]);

  /* ---------------------------------------------------------- */
  /* SurfaceIntent authority loop                                */
  /* ---------------------------------------------------------- */

  useEffect(() => {
    return installSingletonService("surfaceIntentV1AuthorityLoop", () => {
      const tick = () => {
        const st = stateRef.current as any;

        const playheadSec = finiteNum(st?.playheadSec) ? Number(st.playheadSec) : 0;
        const ranges = (Array.isArray(st?.ranges) ? st.ranges : []) as Array<{
          startSec: number;
          endSec: number;
          label?: string | null;
          kind?: unknown;
        }>;

        const computed = computePhaseFromRanges(ranges, playheadSec);
        const phase: NarrativePhase = computed?.phase ?? "setup";
        const phase01Raw = computed ? clamp01(computed.intensity01) : 0;

        const npc01Raw = npc01Ref.current ?? { attention: 0.5, emotion: 0.5, overload: 0.0, safety: 1.0 };

        const sessionId = getBoundSessionId() ?? null;

        const phase01 = bucket01(phase01Raw, 20);
        const npc01 = {
          attention: bucket01(clamp01(npc01Raw.attention), 20),
          emotion: bucket01(clamp01(npc01Raw.emotion), 20),
          overload: bucket01(clamp01(npc01Raw.overload), 20),
          safety: bucket01(clamp01(npc01Raw.safety), 20),
        };

        const suggestMode: "focus" | "balanced" | "ambient" =
          npc01.overload > 0.75 || npc01.safety < 0.5
            ? "focus"
            : phase === "resolve" && phase01 < 0.2
              ? "ambient"
              : "balanced";

        emitSurfaceIntent({ sessionId, phase, phase01, npc01, suggestMode });
      };

      const id = window.setInterval(tick, 50);
      tick();

      return () => window.clearInterval(id);
    });
  }, [emitSurfaceIntent]);

  useEffect(() => {
    const onSurfaceIntent: EventListener = (event) => {
      latestSurfaceIntentRef.current = ((event as CustomEvent).detail ?? null) as SurfaceIntentV1 | null;
    };

    return installSingletonListener("surfaceIntentDebugSnapshot", OMNICAST_SURFACE_INTENT_EVENT, onSurfaceIntent);
  }, []);

  /* ---------------------------------------------------------- */
  /* Ruler pan / zoom intents                                    */
  /* ---------------------------------------------------------- */

  const requestRulerPanByPx = useCallback(
    (deltaPx: number) => {
      const el = scrollerRef.current;
      if (!el) return;
      if (!Number.isFinite(deltaPx) || Math.abs(deltaPx) < 0.01) return;

      const anim = rulerPanAnimRef.current;
      anim.velocityPx += deltaPx;

      if (anim.rafId != null) return;

      const step = () => {
        const node = scrollerRef.current;
        if (!node) {
          anim.rafId = null;
          anim.velocityPx = 0;
          return;
        }

        const maxScrollLeftPx = Math.max(0, laneWidthPx - viewportWidthPx);
        const desiredScrollLeftPx = clamp(node.scrollLeft + anim.velocityPx, 0, maxScrollLeftPx);
        const appliedDeltaPx = desiredScrollLeftPx - node.scrollLeft;

        if (Math.abs(appliedDeltaPx) > 0.01) {
          node.scrollLeft = desiredScrollLeftPx;
        }

        // Kinetic damping keeps touchpad pan fluid without reducer spam.
        anim.velocityPx *= 0.74;

        const blockedAtEdge = Math.abs(appliedDeltaPx) < 0.01 && Math.abs(anim.velocityPx) > 0.2;
        if (blockedAtEdge) anim.velocityPx *= 0.5;

        if (Math.abs(anim.velocityPx) < 0.2) {
          anim.velocityPx = 0;
          anim.rafId = null;
          return;
        }

        anim.rafId = requestAnimationFrame(step);
      };

      anim.rafId = requestAnimationFrame(step);
    },
    [laneWidthPx, viewportWidthPx],
  );

  const requestRulerZoomAtViewportX = useCallback(
    (localViewportX: number, wheelDeltaY: number) => {
      const el = scrollerRef.current;
      if (!el) return;
      if (!Number.isFinite(wheelDeltaY) || Math.abs(wheelDeltaY) < 0.01) return;

      const nowTs = typeof performance !== "undefined" ? performance.now() : Date.now();
      if (nowTs - rulerZoomLastEventTsRef.current > RULER_CTRL_ZOOM_IDLE_RESET_MS) {
        rulerZoomDeltaSmoothRef.current = 0;
      }
      rulerZoomLastEventTsRef.current = nowTs;

      const panAnim = rulerPanAnimRef.current;
      if (panAnim.rafId != null) {
        cancelAnimationFrame(panAnim.rafId);
        panAnim.rafId = null;
      }
      panAnim.velocityPx = 0;

      const st = stateRef.current as any;
      const curPx = readPxPerSec(st);
      const durationSec = finiteNum(st?.durationSec) ? Number(st.durationSec) : 120;
      const currentViewportWidthPx = Math.max(1, viewportWidthPx);
      const zoomModeEnabled = rulerZoomModeEnabledRef.current;

      const clampedLocalX = clamp(localViewportX, 0, currentViewportWidthPx);
      const clampedWheelDeltaY = clamp(wheelDeltaY, -42, 42);
      const prevSmoothedWheelDeltaY = rulerZoomDeltaSmoothRef.current;
      const smoothedWheelDeltaY =
        prevSmoothedWheelDeltaY +
        (clampedWheelDeltaY - prevSmoothedWheelDeltaY) * RULER_CTRL_ZOOM_SMOOTHING_ALPHA;
      rulerZoomDeltaSmoothRef.current = smoothedWheelDeltaY;

      if (Math.abs(smoothedWheelDeltaY) < RULER_CTRL_ZOOM_DEADZONE_PX) return;

      const zoomFactor = Math.exp(-smoothedWheelDeltaY * RULER_CTRL_ZOOM_GAIN);
      const nextPx = clamp(curPx * zoomFactor, 20, 400);

      if (Math.abs(nextPx - curPx) < 1e-6) return;

      if (zoomModeEnabled) {
        const localCurPx = finiteNum(rulerOnlyPxPerSecRef.current)
          ? Number(rulerOnlyPxPerSecRef.current)
          : curPx;
        const localNextPx = clamp(localCurPx * zoomFactor, 20, 400);

        if (Math.abs(localNextPx - localCurPx) < 1e-6) return;

        const localStartSec = finiteNum(rulerOnlyViewportStartSecRef.current)
          ? Number(rulerOnlyViewportStartSecRef.current)
          : readViewportStartSec(st);

        const cursorTimeSec = localStartSec + clampedLocalX / Math.max(1e-6, localCurPx);
        const localMaxStartSec = Math.max(
          0,
          durationSec - currentViewportWidthPx / Math.max(1e-6, localNextPx),
        );
        const localNextStartSec = clamp(
          cursorTimeSec - clampedLocalX / Math.max(1e-6, localNextPx),
          0,
          localMaxStartSec,
        );

        // Keep refs in lockstep with state to avoid event-to-event lag jitter during fast pinch streams.
        rulerOnlyPxPerSecRef.current = localNextPx;
        rulerOnlyViewportStartSecRef.current = localNextStartSec;
        setRulerOnlyPxPerSec(localNextPx);
        setRulerOnlyViewportStartSec(localNextStartSec);
        return;
      }

      const cursorTimeSec = (el.scrollLeft + clampedLocalX) / Math.max(1e-6, curPx);
      const maxScrollLeftPx = Math.max(0, durationSec * nextPx - currentViewportWidthPx);
      const nextScrollLeftPx = clamp(cursorTimeSec * nextPx - clampedLocalX, 0, maxScrollLeftPx);

      const nextViewportStartSec = nextScrollLeftPx / Math.max(1e-6, nextPx);
      const nextViewportEndSec = Math.min(durationSec, nextViewportStartSec + currentViewportWidthPx / nextPx);

      const now = typeof performance !== "undefined" ? performance.now() : Date.now();
      suppressViewportSyncUntilRef.current = now + 180;

      lastViewportSyncRef.current = {
        viewportStartSec: nextViewportStartSec,
        viewportEndSec: nextViewportEndSec,
        scrollLeftPx: nextScrollLeftPx,
        scrollTopPx: el.scrollTop || 0,
      };

      dispatch({
        type: "timeline/setZoom",
        payload: { pxPerSec: nextPx },
        meta: { source: "user", transient: true, note: "ruler zoom (cursor-centered)" },
      } as any);

      dispatch({
        type: "timeline/setViewport",
        payload: { startSec: nextViewportStartSec, endSec: nextViewportEndSec },
        meta: { source: "user", transient: true, note: "ruler zoom viewport" },
      } as any);

      requestAnimationFrame(() => {
        el.scrollLeft = nextScrollLeftPx;

        // Drift guard: keep the cursor-anchored time stable under fast gesture bursts.
        const postCursorTimeSec = (el.scrollLeft + clampedLocalX) / Math.max(1e-6, nextPx);
        const driftSec = cursorTimeSec - postCursorTimeSec;
        if (Math.abs(driftSec) > 1e-4) {
          const correctedScrollLeftPx = clamp(el.scrollLeft + driftSec * nextPx, 0, maxScrollLeftPx);
          el.scrollLeft = correctedScrollLeftPx;
        }
      });
    },
    [dispatch, viewportWidthPx],
  );

  const onRulerZoomStep = useCallback(
    (direction: "in" | "out") => {
      if (!rulerZoomModeEnabledRef.current) return;

      // Step-based explicit zoom is deterministic and avoids touchpad pinch jitter.
      rulerZoomDeltaSmoothRef.current = 0;
      rulerZoomLastEventTsRef.current = 0;

      const centerX = Math.max(1, viewportWidthPx) * 0.5;
      const stepDeltaY = direction === "in" ? -28 : 28;
      requestRulerZoomAtViewportX(centerX, stepDeltaY);
    },
    [requestRulerZoomAtViewportX, viewportWidthPx],
  );

  const ignoreRulerWheelZoom = useCallback((_localViewportX: number, _wheelDeltaY: number) => {
    // Option 1: explicit controls only; wheel/pinch zoom is intentionally disabled.
  }, []);

  useEffect(() => {
    const onNativeWheelCapture = (e: WheelEvent) => {
      if (!e.ctrlKey) return;

      const node = scrollerRef.current;
      if (!node) return;

      const targetNode = e.target as Node | null;
      if (!targetNode || !node.contains(targetNode)) return;

      if (typeof e.preventDefault === "function" && e.cancelable) {
        e.preventDefault();
      }

      if (typeof e.stopPropagation === "function") {
        e.stopPropagation();
      }

      // Option 1: swallow ctrl+wheel/pinch over timeline to keep UI fully stable.
      return;
    };

    window.addEventListener("wheel", onNativeWheelCapture, {
      capture: true,
      passive: false,
    });

    return () => {
      window.removeEventListener("wheel", onNativeWheelCapture, {
        capture: true,
      } as EventListenerOptions);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (edgeScrubRafRef.current != null) {
        cancelAnimationFrame(edgeScrubRafRef.current);
        edgeScrubRafRef.current = null;
      }

      const anim = rulerPanAnimRef.current;
      if (anim.rafId != null) {
        cancelAnimationFrame(anim.rafId);
        anim.rafId = null;
      }
      anim.velocityPx = 0;
    };
  }, []);

  const stopEmptySpacePan = useCallback(() => {
    emptyPanRef.current = null;
    setIsEmptySpacePanning(false);
  }, []);

  const onScrollerPointerDownCapture = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;

      const target = e.target as HTMLElement | null;
      if (!target) return;

      const blockedByInteractive = !!target.closest(
        [
          "button",
          "input",
          "textarea",
          "select",
          "a",
          "[contenteditable='true']",
          "[data-oc-ruler-base-rail='1']",
          "[data-oc-inline-witness-rail]",
          "[data-oc-media-panel-focus-dock]",
          "[data-omnicine-track='1']",
        ].join(","),
      );
      if (blockedByInteractive) return;

      const node = scrollerRef.current;
      if (!node) return;

      e.preventDefault();
      e.stopPropagation();

      emptyPanRef.current = {
        pointerId: e.pointerId,
        lastClientX: e.clientX,
      };

      setIsEmptySpacePanning(true);

      try {
        node.setPointerCapture(e.pointerId);
      } catch {
        // ignore capture failures
      }
    },
    [],
  );

  const onScrollerPointerMoveCapture = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const pan = emptyPanRef.current;
    if (!pan || pan.pointerId !== e.pointerId) return;

    const node = scrollerRef.current;
    if (!node) return;

    e.preventDefault();
    e.stopPropagation();

    const dx = e.clientX - pan.lastClientX;
    pan.lastClientX = e.clientX;

    const maxScrollLeftPx = Math.max(0, laneWidthPx - Math.max(1, viewportWidthPxRef.current));
    node.scrollLeft = clamp(node.scrollLeft - dx, 0, maxScrollLeftPx);
  }, [laneWidthPx]);

  const onScrollerPointerUpCapture = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const pan = emptyPanRef.current;
    if (!pan || pan.pointerId !== e.pointerId) return;

    const node = scrollerRef.current;
    if (node) {
      try {
        node.releasePointerCapture(e.pointerId);
      } catch {
        // ignore capture release failures
      }
    }

    stopEmptySpacePan();
  }, [stopEmptySpacePan]);

  const onScrollerPointerCancelCapture = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const pan = emptyPanRef.current;
    if (!pan || pan.pointerId !== e.pointerId) return;
    stopEmptySpacePan();
  }, [stopEmptySpacePan]);

  useEffect(() => {
    if (rulerZoomModeEnabled || !finiteNum(rulerScrubViewportX)) {
      if (edgeScrubRafRef.current != null) {
        cancelAnimationFrame(edgeScrubRafRef.current);
        edgeScrubRafRef.current = null;
      }
      return;
    }

    const step = () => {
      const node = scrollerRef.current;
      const scrubX = rulerScrubViewportXRef.current;

      if (!node || !finiteNum(scrubX) || rulerZoomModeEnabledRef.current) {
        edgeScrubRafRef.current = null;
        return;
      }

      const width = Math.max(1, viewportWidthPxRef.current);
      const x = clamp(Number(scrubX), 0, width);

      let direction = 0;
      let intensity = 0;

      if (x <= RULER_EDGE_SCRUB_ZONE_PX) {
        direction = -1;
        intensity = (RULER_EDGE_SCRUB_ZONE_PX - x) / RULER_EDGE_SCRUB_ZONE_PX;
      } else if (x >= width - RULER_EDGE_SCRUB_ZONE_PX) {
        direction = 1;
        intensity = (x - (width - RULER_EDGE_SCRUB_ZONE_PX)) / RULER_EDGE_SCRUB_ZONE_PX;
      }

      if (direction === 0 || intensity <= 0.001) {
        edgeScrubRafRef.current = null;
        return;
      }

      const maxScrollLeftPx = Math.max(0, laneWidthPx - width);
      const pxPerFrame = 2 + intensity * RULER_EDGE_SCRUB_MAX_SCROLL_PX_PER_FRAME;
      const nextScrollLeft = clamp(node.scrollLeft + direction * pxPerFrame, 0, maxScrollLeftPx);

      if (Math.abs(nextScrollLeft - node.scrollLeft) > 0.01) {
        node.scrollLeft = nextScrollLeft;

        const playheadSec = clamp(
          (nextScrollLeft + x) / Math.max(1e-6, pxPerSecRef.current),
          0,
          Math.max(0, norm.durationSec),
        );

        dispatch({
          type: "timeline/setPlayhead",
          payload: { playheadSec },
          meta: { source: "user", transient: true, note: "ruler edge scrub" },
        } as any);
      }

      edgeScrubRafRef.current = requestAnimationFrame(step);
    };

    edgeScrubRafRef.current = requestAnimationFrame(step);

    return () => {
      if (edgeScrubRafRef.current != null) {
        cancelAnimationFrame(edgeScrubRafRef.current);
        edgeScrubRafRef.current = null;
      }
    };
  }, [rulerScrubViewportX, rulerZoomModeEnabled, laneWidthPx, dispatch, norm.durationSec]);

  /* ---------------------------------------------------------- */
  /* Sync viewport (scroll + resize)                             */
  /* ---------------------------------------------------------- */

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const nearlyEqual = (a: number, b: number, eps = 1e-3) => Math.abs(a - b) <= eps;

    const doSync = () => {
      const now = typeof performance !== "undefined" ? performance.now() : Date.now();
      if (now < suppressViewportSyncUntilRef.current) return;

      try {
        if (typeof performance !== "undefined" && performance.mark) performance.mark("tshell:sync:start");
      } catch {}

      const st = stateRef.current as any;
      const px = readPxPerSec(st);
      const durationSec = finiteNum(st?.durationSec) ? Number(st.durationSec) : 120;

      const scrollLeftPx = el.scrollLeft || 0;
      const scrollTopPx = el.scrollTop || 0;
      const clientWidthPx = readScrollerViewportWidthPx(el);

      setViewportWidthPx((prev) => (prev === clientWidthPx ? prev : clientWidthPx));

      const viewportStartSec = scrollLeftPx / Math.max(1e-6, px);
      const viewportEndSec = viewportStartSec + clientWidthPx / Math.max(1e-6, px);

      const prev = lastViewportSyncRef.current;
      const same =
        prev &&
        nearlyEqual(prev.viewportStartSec, viewportStartSec) &&
        nearlyEqual(prev.viewportEndSec, viewportEndSec) &&
        prev.scrollLeftPx === scrollLeftPx &&
        prev.scrollTopPx === scrollTopPx;

      if (same) {
        if (__OC_PERF) __OC_PERF.scrollSkipped++;
        try {
          if (typeof performance !== "undefined" && performance.mark) {
            performance.mark("tshell:sync:end");
            performance.measure("tshell:sync", "tshell:sync:start", "tshell:sync:end");
          }
        } catch {}
        return;
      }

      lastViewportSyncRef.current = {
        viewportStartSec,
        viewportEndSec,
        scrollLeftPx,
        scrollTopPx,
      };

      if (__OC_PERF) __OC_PERF.scrollDispatched++;

      dispatch({
        type: "timeline/setViewport",
        payload: {
          startSec: Math.max(0, viewportStartSec),
          endSec: Math.min(durationSec, viewportEndSec),
        },
        meta: { transient: true, source: "user", note: "scroll viewport" },
      } as any);

      try {
        if (typeof performance !== "undefined" && performance.mark) {
          performance.mark("tshell:sync:end");
          performance.measure("tshell:sync", "tshell:sync:start", "tshell:sync:end");

          try {
            const measures = performance.getEntriesByName("tshell:sync");
            const last = measures.length ? measures[measures.length - 1] : null;
            const w = window as any;
            w.__oc_perf_measures = w.__oc_perf_measures || [];
            if (last) {
              w.__oc_perf_measures.push({ ts: Date.now(), duration: last.duration });
              if (w.__oc_perf_measures.length > 200) w.__oc_perf_measures.shift();
              if (last.duration > 8) console.warn("[perf] TimelineShell sync slow:", last.duration.toFixed(2), "ms");
            }
          } catch {
            // ignore
          }
        }
      } catch {}
    };

    const onScroll = () => {
      if (__OC_PERF) __OC_PERF.scrollHandled++;
      if (scrollRafRef.current != null) return;
      scrollRafRef.current = requestAnimationFrame(() => {
        scrollRafRef.current = null;
        doSync();
      });
    };

    const onResize = () => {
      if (scrollRafRef.current != null) return;
      scrollRafRef.current = requestAnimationFrame(() => {
        scrollRafRef.current = null;
        doSync();
      });
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);

    doSync();

    return () => {
      el.removeEventListener("scroll", onScroll as any);
      window.removeEventListener("resize", onResize);
      if (scrollRafRef.current != null) cancelAnimationFrame(scrollRafRef.current);
      scrollRafRef.current = null;
    };
  }, [dispatch]);

  /* ---------------------------------------------------------- */
  /* Follow playhead                                             */
  /* ---------------------------------------------------------- */

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    if (!isPlaying || !followPlayheadRef.current) {
      autoScrollTargetRef.current = null;
      if (autoScrollRafRef.current != null) {
        cancelAnimationFrame(autoScrollRafRef.current);
        autoScrollRafRef.current = null;
      }
      return;
    }

    const x = norm.playheadSec * pxPerSec;
    const visibleViewportWidthPx = Math.max(1, viewportWidthPx);
    const maxScrollLeftPx = Math.max(0, laneWidthPx - visibleViewportWidthPx);
    const pad = Math.min(AUTO_SCROLL_EDGE_PAD_PX, Math.max(28, visibleViewportWidthPx * 0.22));

    let targetScrollLeftPx: number | null = null;

    if (x < el.scrollLeft + pad) targetScrollLeftPx = clamp(x - pad, 0, maxScrollLeftPx);
    else if (x > el.scrollLeft + visibleViewportWidthPx - pad) {
      targetScrollLeftPx = clamp(x - visibleViewportWidthPx + pad, 0, maxScrollLeftPx);
    }

    if (targetScrollLeftPx == null || Math.abs(targetScrollLeftPx - el.scrollLeft) <= 0.5) {
      autoScrollTargetRef.current = null;
      if (autoScrollRafRef.current != null) {
        cancelAnimationFrame(autoScrollRafRef.current);
        autoScrollRafRef.current = null;
      }
      return;
    }

    autoScrollTargetRef.current = targetScrollLeftPx;

    if (autoScrollRafRef.current != null) return;

    const step = () => {
      const node = scrollerRef.current;
      if (!node || !isPlayingRef.current || !followPlayheadRef.current) {
        autoScrollRafRef.current = null;
        autoScrollTargetRef.current = null;
        return;
      }

      const target = autoScrollTargetRef.current;
      if (target == null) {
        autoScrollRafRef.current = null;
        return;
      }

      const diff = target - node.scrollLeft;
      if (Math.abs(diff) <= 0.5) {
        node.scrollLeft = target;
        autoScrollRafRef.current = null;
        autoScrollTargetRef.current = null;
        return;
      }

      const now = typeof performance !== "undefined" ? performance.now() : Date.now();
      suppressViewportSyncUntilRef.current = now + 120;

      const maxScroll = Math.max(0, laneWidthPx - Math.max(1, viewportWidthPx));
      node.scrollLeft = clamp(node.scrollLeft + diff * AUTO_SCROLL_LERP, 0, maxScroll);
      autoScrollRafRef.current = requestAnimationFrame(step);
    };

    autoScrollRafRef.current = requestAnimationFrame(step);
  }, [norm.playheadSec, pxPerSec, isPlaying, viewportWidthPx, laneWidthPx]);

  useEffect(() => {
    return () => {
      if (autoScrollRafRef.current != null) {
        cancelAnimationFrame(autoScrollRafRef.current);
        autoScrollRafRef.current = null;
      }
      autoScrollTargetRef.current = null;
    };
  }, []);

  /* ---------------------------------------------------------- */
  /* Preview → Timeline intent bridge                            */
  /* ---------------------------------------------------------- */

  useEffect(() => {
    const w = window as any;
    w.__oc_ridToClipIdRef = w.__oc_ridToClipIdRef || { current: new Map<string, string>() };
    const ridToClipIdRef = w.__oc_ridToClipIdRef as { current: Map<string, string> };

    const emitClipSelected = (clipId: string, source: string) => {
      emitOmniCineClipSelected(clipId, source);
    };

    const onTimelineAdd = (detail: TimelineAddDetail | undefined) => {
      if (!detail || typeof detail !== "object") return;

      const st = stateRef.current as any;
      const maxDur = finiteNum(st?.durationSec) ? Number(st.durationSec) : norm.durationSec;

      const source = normalizeSource(detail.source);
      const noteStr = String(detail.note ?? "");
      const sourceStr = String(detail.source ?? "");

      const rid =
        typeof detail.requestId === "string" && detail.requestId.trim() ? detail.requestId.trim() : null;

      const mediaId =
        typeof detail.mediaId === "string" && detail.mediaId.trim() ? detail.mediaId.trim() : null;
      const mediaLabel =
        typeof detail.label === "string" && detail.label.trim()
          ? detail.label.trim()
          : typeof detail.fileName === "string" && detail.fileName.trim()
            ? detail.fileName.trim()
            : null;

      const tracksFromState = Array.isArray(st?.tracks) ? st.tracks : [];
      const tracksForChoice = tracksFromState.length > 0 ? tracksFromState : FALLBACK_TRACKS;

      const kind = (detail.kind ?? "video") as "video" | "audio" | "image" | "npc";

      const preferredTrackId =
        typeof detail.trackId === "string" && detail.trackId.trim() ? detail.trackId.trim() : undefined;

      const trackId =
        (preferredTrackId &&
          tracksForChoice.some((t: any) => String(t?.id ?? "") === preferredTrackId) &&
          preferredTrackId) ||
        tracksForChoice.find((t: any) => t?.kind === kind)?.id ||
        tracksForChoice.find((t: any) => t?.kind === "video")?.id ||
        (kind === "audio" ? "a1" : "v1");

      const startParsed = parseMaybeNumber(detail.startSec);
      const endParsed = parseMaybeNumber(detail.endSec);
      const durParsed = parseMaybeNumber(detail.durSec);

      const start0 =
        startParsed != null
          ? startParsed
          : finiteNum(st?.playheadSec)
            ? Number(st.playheadSec)
            : norm.playheadSec;

      const end0 = endParsed != null ? endParsed : null;
      const dur0 = durParsed != null ? durParsed : end0 != null ? Math.max(0.05, end0 - start0) : 3;
      const durSec = Math.max(0.05, dur0);

      const startSec = clamp(start0, 0, Math.max(0, maxDur - durSec));
      const endSec = clamp(startSec + durSec, startSec + 0.05, maxDur);

      const directUrl =
        typeof detail.url === "string" && detail.url.trim()
          ? detail.url.trim()
          : typeof detail.previewUrl === "string" && detail.previewUrl.trim()
            ? detail.previewUrl.trim()
            : typeof detail.mediaUrl === "string" && detail.mediaUrl.trim()
              ? detail.mediaUrl.trim()
              : typeof detail.src === "string" && detail.src.trim()
                ? detail.src.trim()
                : "";

      const isImportEvent =
        String(detail.ui?.intent ?? "").toLowerCase() === "import" ||
        sourceStr.toLowerCase().includes("import") ||
        noteStr.toLowerCase().includes("import") ||
        (!!rid && (kind === "video" || kind === "audio") && (!!directUrl || !!mediaId));

      let clipId: string | null = rid ? ridToClipIdRef.current.get(rid) ?? null : null;

      if (!clipId) {
        clipId = findExistingClipIdByRequestOrMedia(rid, mediaId, {
          // Media-id recovery is only safe for request-based upgrade flows.
          // Plain "add this asset again" actions must create a distinct clip.
          allowMediaIdMatch: !!rid,
        });
        if (clipId && rid) ridToClipIdRef.current.set(rid, clipId);
      }

      if (!clipId) clipId = makeId();

      if (clipId) {
        const list = pickFirstClipsContainer(
          (window as any).__oc_debug?.getState?.() ?? (window as any).__oc_debug_state?.() ?? st,
        );

        const exists = Array.isArray(list) ? list.some((c: any) => String(c?.id) === String(clipId)) : false;

        if (exists) {
          const patch = buildTimelineAddClipPatch({
            startSec,
            endSec,
            durSec,
            mediaId,
            mediaLabel,
            requestId: rid,
            source,
            directUrl,
          });

          dispatch({
            type: "clip/patch",
            payload: { clipId, patch },
            meta: { source, note: "timeline:add upgrade (patch existing)" },
          } as any);

          dispatch({
            type: "clip/select",
            payload: { clipId },
            meta: { source, note: "select after upgrade patch" },
          } as any);

          recordClipMediaMetadata(buildTimelineAddClipMediaMetadata({
            clipId,
            detail,
            durSec,
            directUrl,
            kind,
            mediaId,
            requestId: rid,
            mediaLabel,
          }));

          emitClipSelected(String(clipId), source);

          if (isImportEvent) {
            applyImportedLaneFocus({
              importedKind: kind,
              clipId: String(clipId),
              includeAudioCompanion: kind === "video" && detail.hasPlayableAudio !== false,
              source,
            });
          }

          if (rid) ridToClipIdRef.current.set(rid, String(clipId));

          if (isImportEvent) {
            focusCameraOnClipStart(startSec, maxDur, source);
          }

          return;
        }
      }

      const sig = rid
        ? `rid:${rid}`
        : JSON.stringify({
            trackId: String(trackId ?? ""),
            kind: kind ?? "",
            durSec: durSec ?? "",
            startSec: startParsed ?? "",
            endSec: endParsed ?? "",
            mediaId: mediaId ?? "",
            url: directUrl ?? "",
          });

      const now = typeof performance !== "undefined" ? performance.now() : Date.now();
      const prev = lastAddSigRef.current;

      if (prev && prev.sig === sig && now - prev.t < 250) return;
      lastAddSigRef.current = { sig, t: now };

      const clip = buildTimelineAddClip({
        clipId,
        trackId: String(trackId),
        kind,
        startSec,
        endSec,
        durSec,
        mediaId,
        mediaLabel,
        requestId: rid,
        source,
        directUrl,
      });

      dispatch({
        type: "clip/add",
        payload: { clip },
        meta: { source, note: "timeline:add" },
      } as any);

      try {
        dispatch({
          type: "timeline/setPlayhead",
          payload: { playheadSec: Number(clip.startSec ?? 0) + 0.01 },
          meta: { source, note: "align playhead after add" },
        } as any);
      } catch {
        // best-effort
      }

      dispatch({
        type: "clip/select",
        payload: { clipId },
        meta: { source, note: "select after add" },
      } as any);

      recordClipMediaMetadata(buildTimelineAddClipMediaMetadata({
        clipId,
        detail,
        durSec,
        directUrl,
        kind,
        mediaId,
        requestId: rid,
        mediaLabel,
      }));

      emitClipSelected(String(clipId), source);

      if (isImportEvent) {
        applyImportedLaneFocus({
          importedKind: kind,
          clipId: String(clipId),
          includeAudioCompanion: kind === "video" && detail.hasPlayableAudio !== false,
          source,
        });
      }

      if (rid) ridToClipIdRef.current.set(rid, String(clipId));

      if (isImportEvent) {
        focusCameraOnClipStart(startSec, maxDur, source);
      }
    };

    return installSingletonTimelineAddEvent("timelineAdd", (detail) => {
      onTimelineAdd(detail);
    });
  }, [dispatch, focusCameraOnClipStart, norm.durationSec, norm.playheadSec, recordClipMediaMetadata, requestMediaPanelMode]);

  /* ---------------------------------------------------------- */
  /* Media Panel authority listeners                             */
  /* ---------------------------------------------------------- */

  useEffect(() => {
    const unsubscribeOpenGlass = onOmniCineInlineWitnessOpenGlass((detail) => {
      const request = detail as MediaPanelAuthorityRequest;

      if (mediaPanelPinnedRef.current && !request.force) return;

      requestMediaPanelMode({
        ...request,
        mode: "focus",
        pinned: false,
        source: "inlineWitness",
      });
    });

    return unsubscribeOpenGlass;
  }, [requestMediaPanelMode]);

  useEffect(() => {
    const unsubscribeRemoveClip = onOmniCineInlineWitnessRemoveClip((detail) => {
      const clipId = String(detail.clipId ?? "").trim();
      if (!clipId) return;
      removeAudioClipById(clipId, detail.source ?? "inlineWitness");
    });

    return unsubscribeRemoveClip;
  }, [removeAudioClipById]);

  useEffect(() => {
    const unsubscribeClipSelected = onOmniCineClipSelected((detail) => {
      const clipId = String(detail.clipId ?? "").trim();
      if (!clipId) return;

      const selectedClip = clipById.get(clipId) ?? null;
      const selectedClipMetadata = clipMediaMetadataByClipId[clipId];
      const selectedKind = resolveClipMediaKind(selectedClip, selectedClipMetadata?.kind ?? null);

      if (selectedKind === "video" || selectedKind === "image") {
        setWitnessAuditionMode("clip");
        setAudioWitnessUi((prev) => ({
          ...prev,
          activeCapsuleId: clipId,
          witnessBarVisible: true,
          focusScope: "clip",
        }));
        return;
      }

      const witnessClip = witnessBoundClips.find((clip) => String(clip?.id ?? clip?.clipId ?? "") === clipId);
      const canOpenAudioWitness =
        !!witnessClip ||
        isAudioClip(selectedClip) ||
        selectedClipMetadata?.kind === "audio";
      if (!canOpenAudioWitness) return;

      setWitnessAuditionMode("clip");

      setAudioWitnessUi((prev) => ({
        ...prev,
        activeCapsuleId: clipId,
        witnessBarVisible: true,
        focusScope: "clip",
      }));
    });

    return unsubscribeClipSelected;
  }, [clipById, clipMediaMetadataByClipId, setWitnessAuditionMode, witnessBoundClips]);

  useEffect(() => {
    if (witnessBoundClips.length > 0) {
      const currentActiveId = audioWitnessUi.activeCapsuleId;
      if (currentActiveId) {
        const stillExists = witnessBoundClips.some(
          (clip) => String(clip?.id ?? clip?.clipId ?? "") === String(currentActiveId),
        );
        if (stillExists) return;
      }

      const nextClipId = String(witnessBoundClips[0]?.id ?? witnessBoundClips[0]?.clipId ?? "").trim();
      if (!nextClipId) return;
      setAudioWitnessUi((prev) => ({ ...prev, activeCapsuleId: nextClipId }));
      return;
    }

    setAudioWitnessUi((prev) => {
      if (!prev.activeCapsuleId && !prev.witnessBarVisible && !prev.activeLensId && !prev.pinnedMiniLensId) {
        return prev;
      }
      return { ...defaultAudioWitnessUiState };
    });
    discardAuthoredSonicsByIds({
      segmentIds: authoredEmotionalIntentSegmentsRef.current.slice().map((segment) => segment.id),
      fxNodeIds: authoredFxIntentNodesRef.current.slice().map((node) => node.id),
      discardEmotionalIntentSegment: sonics.discardEmotionalIntentSegment,
      discardFxIntentNode: sonics.discardFxIntentNode,
    });
  }, [audioWitnessUi.activeCapsuleId, sonics, witnessBoundClips]);

  useEffect(() => {
    const unsubscribeSetMode = onMediaPanelSetMode((detail) => {
      requestMediaPanelMode(detail);
    });

    return unsubscribeSetMode;
  }, [requestMediaPanelMode]);

  useEffect(() => {
    const unsubscribePreviewSet = onOmniStudioCompilePreviewSet((detail) => {
      const preview = detail.preview;

      const compileId = String(preview?.compileId ?? "").trim();
      if (!compileId) return;

      dispatch({
        type: "timeline/narrativePreview/set",
        payload: {
          compileId,
          blocks: Array.isArray(preview?.blocks) ? (preview.blocks as any[]) : [],
          overlays: Array.isArray(preview?.overlays) ? (preview.overlays as any[]) : [],
        },
        meta: { source: "system", transient: true, note: "omnistudio preview set" },
      } as any);
    });

    const unsubscribePreviewClear = onOmniStudioCompilePreviewClear(() => {
      dispatch({
        type: "timeline/narrativePreview/clear",
        meta: { source: "system", transient: true, note: "omnistudio preview clear" },
      } as any);
    });

    return () => {
      unsubscribePreviewSet();
      unsubscribePreviewClear();
    };
  }, [dispatch]);

  useEffect(() => {
    const OMNICINE_ANNOTATION_SURFACE_OPEN_EVENT = "omnicine:annotationSurface:open";

    const onMarkerContextRequest = (detail: {
        markerId?: string;
        clientX?: number;
        clientY?: number;
        label?: string | null;
      }) => {

      openMarkerSurface(detail, "menu");
    };

    const onMarkerEditRequest = (detail: {
        markerId?: string;
        clientX?: number;
        clientY?: number;
        label?: string | null;
      }) => {
      const markerId = String(detail.markerId ?? "").trim();
      const marker = markerId ? markerById.get(markerId) : null;
      const annotationId = String(marker?.annotation?.annotationId ?? "").trim();

      if (marker?.annotation?.origin === "annotation_surface" && annotationId) {
        window.dispatchEvent(
          new CustomEvent(OMNICINE_ANNOTATION_SURFACE_OPEN_EVENT, {
            detail: { panel: "annotate", annotationId, source: "marker_edit" },
          }),
        );
        return;
      }

      openMarkerSurface(detail, "edit");
    };

    const onVideoAnnotateAddMarker = (detail: {
        timeSec?: unknown;
        label?: unknown;
        annotationType?: unknown;
        annotationSubtype?: unknown;
        annotationSeverity?: unknown;
        annotationSummary?: unknown;
        annotationSource?: unknown;
      annotationId?: unknown;
      nextAction?: unknown;
      markerVisible?: unknown;
      annotationProvenance?: unknown;
        assetLabel?: unknown;
        clipId?: unknown;
        linkedAudio?: unknown;
        frameIndex?: unknown;
        anchorTimecode?: unknown;
        createdAt?: unknown;
        annotationState?: unknown;
      }) => {

      const nextTimeSec = clamp(parseMaybeNumber(detail.timeSec) ?? norm.playheadSec, 0, norm.durationSec);
      const rawLabel = String(detail.label ?? "").trim();
      const markerType = String(detail.annotationType ?? "note").trim().toLowerCase() || "note";
      const markerNote = String(detail.annotationSummary ?? rawLabel).trim() || rawLabel || `Note ${nextTimeSec.toFixed(2)}s`;
      const markerVisible = typeof detail.markerVisible === "boolean" ? detail.markerVisible : true;
      const markerFps = Number(norm.snap?.fps ?? 30) || 30;
      const annotationId = String(detail.annotationId ?? "").trim() || null;
      const markerColor = resolveAnnotationMarkerDefaultColor(detail.annotationType, detail.annotationSeverity);
      const marker = {
        id: annotationId ? `marker-${annotationId}` : makeId(),
        timeSec: nextTimeSec,
        label: rawLabel || `Note ${nextTimeSec.toFixed(2)}s`,
        type: markerType,
        origin: "annotation_surface" as const,
        note: markerNote,
        description: markerNote,
        color: markerColor,
        frameIndex: parseMaybeNumber(detail.frameIndex) ?? Math.max(0, Math.round(nextTimeSec * markerFps)),
        visible: markerVisible,
        source: "user" as const,
        annotation: {
          origin: "annotation_surface" as const,
          annotationId,
          type: String(detail.annotationType ?? "creative") as any,
          subtype: String(detail.annotationSubtype ?? "").trim() || null,
          severity: String(detail.annotationSeverity ?? "").trim() || null,
          summary: String(detail.annotationSummary ?? rawLabel).trim() || rawLabel || null,
          state: detail.annotationState === "published" ? "published" : "marker_only",
          source: detail.annotationSource === "voice" ? "voice" : detail.annotationSource === "ai" ? "ai" : "manual",
          nextAction: String(detail.nextAction ?? "").trim() || null,
          markerVisible,
          provenance:
            detail.annotationProvenance && typeof detail.annotationProvenance === "object"
              ? (detail.annotationProvenance as any)
              : null,
          assetLabel: String(detail.assetLabel ?? "active clip").trim() || "active clip",
          clipId: String(detail.clipId ?? "").trim() || null,
          linkedAudio: !!detail.linkedAudio,
          frameIndex: parseMaybeNumber(detail.frameIndex),
          anchorTimecode: String(detail.anchorTimecode ?? "").trim() || null,
          rawNote: rawLabel || `Note ${nextTimeSec.toFixed(2)}s`,
          createdAt: parseMaybeNumber(detail.createdAt) ?? Date.now(),
        },
      };

      dispatch({ type: "marker/add", payload: { marker }, meta: { source: "user" } } as any);
      emitMarkerAdded({ ...marker, origin: OMNICINE_VIDEO_ANNOTATE_ADD_MARKER_EVENT });
    };

    const offContext = onTimelineMarkerContextRequest(onMarkerContextRequest);
    const offEdit = onTimelineMarkerEditRequest(onMarkerEditRequest);
    const offAnnotate = onTimelineVideoAnnotateAddMarker(onVideoAnnotateAddMarker);

    return () => {
      offContext();
      offEdit();
      offAnnotate();
    };
  }, [dispatch, markerById, norm.durationSec, norm.playheadSec, norm.snap?.fps, openMarkerSurface]);

  useEffect(() => {
    if (!markerSurface) return;

    const onWindowPointerDown = (e: PointerEvent) => {
      const root = markerSurfaceRef.current;
      if (!root) return;

      const target = e.target as Node | null;
      if (target && root.contains(target)) return;

      closeMarkerSurface();
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeMarkerSurface();
      }
    };

    window.addEventListener("pointerdown", onWindowPointerDown, { capture: true });
    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("pointerdown", onWindowPointerDown, { capture: true } as EventListenerOptions);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [markerSurface, closeMarkerSurface]);

  useEffect(() => {
    if (!markerBrowserOpen) return;

    const onWindowPointerDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest("[data-oc-marker-browser-root='1']")) return;
      closeMarkerBrowser();
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeMarkerBrowser();
      }
    };

    window.addEventListener("pointerdown", onWindowPointerDown, { capture: true });
    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("pointerdown", onWindowPointerDown, { capture: true } as EventListenerOptions);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [markerBrowserOpen, closeMarkerBrowser]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (isEditableKeyboardTarget(e.target)) return;

      const key = String(e.key || "");
      const isSemanticRulerKey =
        key === "." ||
        key === ">" ||
        key === "," ||
        key === "<" ||
        key === "[" ||
        key === "{" ||
        key === "]" ||
        key === "}" ||
        key === "+" ||
        key === "=" ||
        key === "-" ||
        key === "_" ||
        key === "\\" ||
        (e.shiftKey && key.toLowerCase() === "z");

      if (isSemanticRulerKey) {
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === "function") {
          e.stopImmediatePropagation();
        }
      }

      if (key === "+" || key === "=") {
        e.preventDefault();
        setTemporaryRulerZoomMode(true);
        onRulerZoomStep("in");
        return;
      }

      if (key === "-" || key === "_") {
        e.preventDefault();
        setTemporaryRulerZoomMode(true);
        onRulerZoomStep("out");
        return;
      }

      if (key === "\\") {
        e.preventDefault();
        zoomViewportToWindow(0, Math.max(0.25, norm.durationSec), "keyboard fit timeline");
        return;
      }

      if (e.shiftKey && key.toLowerCase() === "z") {
        e.preventDefault();
        zoomToSelectedRangeOrMarker();
        return;
      }

      if (e.shiftKey && key.toLowerCase() === "m") {
        e.preventDefault();
        openMarkerBrowser();
        return;
      }

      if (key === "[" || key === "{" || key === "]" || key === "}") {
        const frameDelta = key === "[" ? -1 : key === "]" ? 1 : key === "{" ? -5 : 5;
        if (!String(activeMarkerIdRef.current ?? "").trim()) return;
        e.preventDefault();
        nudgeActiveMarkerByFrames(frameDelta, "keyboard/nudge-marker");
        return;
      }

      if (key !== "." && key !== ">" && key !== "," && key !== "<") return;
      if (!filteredMarkerJumpTargets.length) return;

      e.preventDefault();
      const activeTargetIndex = activeMarkerTargetIndex;
      const playheadSec = norm.playheadSec;

      if (key === "." || key === ">") {
        if (activeTargetIndex >= 0) {
          const nextIndex = (activeTargetIndex + 1) % filteredMarkerJumpTargets.length;
          jumpToMarker(filteredMarkerJumpTargets[nextIndex].id, activeMarkerNavType ? "keyboard/next-marker:same-type" : "keyboard/next-marker");
          return;
        }

        const next = filteredMarkerJumpTargets.find((marker) => marker.timeSec > playheadSec + 1e-4) ?? filteredMarkerJumpTargets[0];
        jumpToMarker(next.id, activeMarkerNavType ? "keyboard/next-marker:same-type" : "keyboard/next-marker");
        return;
      }

      if (activeTargetIndex >= 0) {
        const prevIndex = (activeTargetIndex - 1 + filteredMarkerJumpTargets.length) % filteredMarkerJumpTargets.length;
        const source = prevIndex > activeTargetIndex
          ? activeMarkerNavType ? "keyboard/prev-marker-wrap:same-type" : "keyboard/prev-marker-wrap"
          : activeMarkerNavType ? "keyboard/prev-marker:same-type" : "keyboard/prev-marker";
        jumpToMarker(filteredMarkerJumpTargets[prevIndex].id, source);
        return;
      }

      for (let i = filteredMarkerJumpTargets.length - 1; i >= 0; i -= 1) {
        const marker = filteredMarkerJumpTargets[i];
        if (marker.timeSec < playheadSec - 1e-4) {
          jumpToMarker(marker.id, activeMarkerNavType ? "keyboard/prev-marker:same-type" : "keyboard/prev-marker");
          return;
        }
      }

      jumpToMarker(
        filteredMarkerJumpTargets[filteredMarkerJumpTargets.length - 1].id,
        activeMarkerNavType ? "keyboard/prev-marker-wrap:same-type" : "keyboard/prev-marker-wrap",
      );
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
    };
  }, [
    activeMarkerTargetIndex,
    activeMarkerNavType,
    filteredMarkerJumpTargets,
    jumpToMarker,
    nudgeActiveMarkerByFrames,
    norm.durationSec,
    norm.playheadSec,
    openMarkerBrowser,
    onRulerZoomStep,
    setTemporaryRulerZoomMode,
    zoomToSelectedRangeOrMarker,
    zoomViewportToWindow,
  ]);

  /* ---------------------------------------------------------- */
  /* Memoized lane tree                                           */
  /* ---------------------------------------------------------- */

  const lanesNode = useMemo(() => {
    return (
      <div className="relative z-10 space-y-3 pt-3">
        {!norm.hideAuxiliaryLanes ? (
          <Suspense fallback={null}>
            <LazyAudioSpectrumLane
              pxPerSec={pxPerSec}
              viewportStartSec={norm.viewportStartSec}
              durationSec={norm.durationSec}
              snap={(state as any).snap ?? DEFAULT_SNAP}
              markers={(state as any).markers ?? EMPTY_ARR}
            />
          </Suspense>
        ) : null}

        {!norm.hideAuxiliaryLanes ? (
          <div
            key="atomicRuns.v1"
            data-omnicine-track="1"
            data-track-id="atomicRuns.v1"
            data-track-kind="atomicRuns"
            className="relative"
          >
            <Suspense fallback={null}>
              <LazyAtomicRunsTrack pxPerSec={pxPerSec} />
            </Suspense>
          </div>
        ) : null}

        {!norm.hideAuxiliaryLanes && narrativePreview.active && Array.isArray(narrativePreview.blocks) && narrativePreview.blocks.length > 0 ? (
          <Suspense fallback={null}>
            <LazyNarrativePreviewLane
              blocks={narrativePreview.blocks}
              pxPerSec={pxPerSec}
              viewportStartSec={norm.viewportStartSec}
              viewportEndSec={norm.viewportEndSec}
              activeSourceSceneId={activeOmniStudioSceneId}
              onSelectBlock={(sceneId) => {
                emitOmniStudioSceneSelect(sceneId, "timeline-preview");
              }}
            />
          </Suspense>
        ) : null}

        <div className="space-y-2">
          {norm.renderTracks.map((track) => {
            const anchorBadge = anchoredTrackBadges.get(track.id) ?? null;
            if (track.kind === "video") {
              return (
                <div
                  key={track.id}
                  data-omnicine-track="1"
                  data-track-id={track.id}
                  data-track-kind={track.kind}
                  className="relative"
                >
                  {anchorBadge ? (
                    <div className="pointer-events-none absolute right-2 top-2 z-[80]">
                      <div className="rounded-full border border-emerald-300/24 bg-emerald-300/12 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-emerald-50 shadow-[0_10px_30px_rgba(0,0,0,0.22)]">
                        Lane Anchor · {anchorBadge.label}{anchorBadge.followUpdates ? " · follow" : ""}
                      </div>
                    </div>
                  ) : null}
                  <Suspense fallback={null}>
                    <LazyVideoTrack track={track} pxPerSec={pxPerSec} />
                  </Suspense>
                </div>
              );
            }

            if (track.kind === "audio") {
              return (
                <div
                  key={track.id}
                  data-omnicine-track="1"
                  data-track-id={track.id}
                  data-track-kind={track.kind}
                  className="relative"
                >
                  {anchorBadge ? (
                    <div className="pointer-events-none absolute right-2 top-2 z-[80]">
                      <div className="rounded-full border border-emerald-300/24 bg-emerald-300/12 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-emerald-50 shadow-[0_10px_30px_rgba(0,0,0,0.22)]">
                        Lane Anchor · {anchorBadge.label}{anchorBadge.followUpdates ? " · follow" : ""}
                      </div>
                    </div>
                  ) : null}
                  <Suspense fallback={null}>
                    <LazyAudioTrack track={track} pxPerSec={pxPerSec} />
                  </Suspense>
                </div>
              );
            }

            return (
              <div
                key={track.id}
                data-omnicine-track="1"
                data-track-id={track.id}
                data-track-kind={track.kind}
                className="relative"
              >
                {anchorBadge ? (
                  <div className="pointer-events-none absolute right-2 top-2 z-[80]">
                    <div className="rounded-full border border-emerald-300/24 bg-emerald-300/12 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-emerald-50 shadow-[0_10px_30px_rgba(0,0,0,0.22)]">
                      Lane Anchor · {anchorBadge.label}{anchorBadge.followUpdates ? " · follow" : ""}
                    </div>
                  </div>
                ) : null}
                <Suspense fallback={null}>
                  <LazyNPCTrack track={track} pxPerSec={pxPerSec} />
                </Suspense>
              </div>
            );
          })}
        </div>

        <Suspense fallback={null}>
          <LazyTimelineLaneVisualStack
            laneState={lanes.state}
            viewportStartSec={norm.viewportStartSec}
            viewportEndSec={norm.viewportEndSec}
            pxPerSec={pxPerSec}
            playheadSec={norm.playheadSec}
            durationSec={norm.durationSec}
          />
        </Suspense>

        {!norm.hasStateTracks && (
          <div className="mt-2 text-xs text-white/60">
            Tracks are currently not present in timeline state; rendering UI lane scaffolding to prevent blank
            timeline.
          </div>
        )}
      </div>
    );
  }, [
    pxPerSec,
    norm.viewportStartSec,
    norm.durationSec,
    norm.renderTracks,
    norm.hasStateTracks,
    norm.viewportEndSec,
    activeOmniStudioSceneId,
    lanes.state,
    narrativePreview.active,
    narrativePreview.blocks,
    state,
  ]);

  /* ---------------------------------------------------------- */
  /* Render                                                      */
  /* ---------------------------------------------------------- */

  return (
    <div className="relative flex h-full flex-col" data-omnicine-timeline-shell="1">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background: "linear-gradient(90deg, rgba(34,211,238,.06), rgba(168,85,247,.06))",
        }}
      />

      <XRRangeIntentBridge />
      <XRMarkerIntentBridge />
      <XRVideoIntentBridge />
      <TimelineHologramBridge />

      {helpOpen ? (
        <Suspense fallback={null}>
          <LazyTimelineHelpDrawer open={helpOpen} onClose={closeHelp} tourKey={TOUR_KEY} />
        </Suspense>
      ) : null}

      <div data-omnicine-timeline-main="1" className="relative flex h-full min-h-0 flex-col">
        <div
          ref={mediaPanelIngestDockRef}
          data-oc-media-panel-ingest-dock
          className="pointer-events-none absolute left-4 right-4 top-3 z-[2147483645]"
          style={{ height: 0 }}
        />

        <div className="relative z-[2147483646] px-4 pt-3 space-y-1.5">
          <EtchedContextLine event={whisperHudState.activeEvent} retainedText={retainedContextLine} />

          <div className="relative">
            <ClipFocusLensOverlay
              visible={audioWitnessBarVisible && !audioWitnessUi.activeLensId}
              activeLensId={audioWitnessUi.activeLensId}
              pinnedMiniLensId={audioWitnessUi.pinnedMiniLensId}
              focusScope={audioWitnessUi.focusScope}
              status={audioWitnessStatus}
              currentTimeSec={audioWitnessCurrentTimeSec}
              durationSec={activeAudioDurationSec}
              activeSourceId={activeAudioSourceId}
              activeClipId={activeAudioClipId}
              placement="aboveBar"
            />

            {audioWitnessBarVisible && audioWitnessUi.activeLensId ? (
              <Suspense fallback={null}>
                <LazyNarrativeSonicsLensHost
                  visible={audioWitnessBarVisible && !!audioWitnessUi.activeLensId}
                  activeLensId={audioWitnessUi.activeLensId}
                  pinnedMiniLensId={audioWitnessUi.pinnedMiniLensId}
                  focusScope={audioWitnessUi.focusScope}
                  context={narrativeSonicsContext}
                  arcSegments={authoredEmotionalIntentSegments}
                  fxNodes={authoredFxIntentNodes}
                  continuityDiagnostics={continuityDiagnostics}
                  placement="aboveBar"
                  onUpsertArcSegment={authorEmotionalIntentSegment}
                  onRemoveArcSegment={discardEmotionalIntentSegment}
                  onUpsertFxNode={authorFxIntentNode}
                  onRemoveFxNode={discardFxIntentNode}
                  onClosePrimaryLens={() => {
                    setAudioWitnessUi((prev) => ({ ...prev, activeLensId: null }));
                  }}
                  onUnpinMiniLens={() => {
                    setAudioWitnessUi((prev) => ({ ...prev, pinnedMiniLensId: null }));
                  }}
                />
              </Suspense>
            ) : null}

            <AudioWitnessBar
              visible={audioWitnessBarVisible}
              focusScope={audioWitnessUi.focusScope}
              title={audioWitnessTitle}
              subtitle={audioWitnessSubtitle}
              mediaType={activeWitnessMediaType}
              activeMediaUrl={activeWitnessMediaUrl}
              activeSourceId={activeAudioSourceId}
              activeSourceLabel={audioWitnessTitle}
              activeClipId={activeAudioClipId}
              activeClipLabel={audioWitnessTitle}
              activeSequenceLabel="Narrative Sonics Surface"
              status={audioWitnessStatus}
              currentTimeSec={audioWitnessCurrentTimeSec}
              durationSec={activeAudioDurationSec}
              auditionMode={auditionMode}
              bindingMode={playbackProjection.bindingMode}
              mediaFamilyLabel={activeAudioFamilyLabel}
              hasLinkedAudio={playbackProjection.hasLinkedAudio}
              hasLinkedVideo={playbackProjection.hasLinkedVideo}
              hasExplicitBinding={hasExplicitBinding}
              canBindCurrentFamily={canBindCurrentFamily}
              familyOptions={bindingEditorState.familyOptions}
              selectedFamilyId={bindingEditorState.selectedFamilyId}
              audioMembers={bindingEditorState.audioMembers}
              videoMembers={bindingEditorState.videoMembers}
              selectedAudioMemberId={bindingEditorState.preferredAudioMemberId}
              selectedVideoMemberId={bindingEditorState.preferredVideoMemberId}
              onSetAuditionMode={setWitnessAuditionMode}
              onBindCurrentFamily={bindFocusClipToCurrentFamily}
              onClearExplicitBinding={clearExplicitBindingForFocusClip}
              onSelectFamily={setFocusClipBindingFamily}
              onSelectAudioMember={setFocusClipPreferredAudioMemberId}
              onSelectVideoMember={setFocusClipPreferredVideoMemberId}
              onInspectMediaMember={inspectFocusClipMember}
              activeLensId={audioWitnessUi.activeLensId}
              pinnedMiniLensId={audioWitnessUi.pinnedMiniLensId}
              availableLensIds={AUDIO_WITNESS_LENS_IDS}
              onToggleLens={(lensId) => {
                setAudioWitnessUi((prev) => ({
                  ...prev,
                  activeLensId: prev.activeLensId === lensId ? null : lensId,
                }));
              }}
              onPinMiniLens={(lensId) => {
                setAudioWitnessUi((prev) => ({
                  ...prev,
                  pinnedMiniLensId: prev.pinnedMiniLensId === lensId ? null : lensId,
                }));
              }}
              onRemoveActiveClip={removeActiveAudioClip}
              onClose={() => {
                requestMediaPanelMode({ mode: "closed", kind: activeWitnessMediaType, source: "mediaPanel", force: true });
              }}
            />

            <div
              ref={mediaPanelFocusDockRef}
              data-oc-media-panel-focus-dock
              className="pointer-events-none absolute inset-x-0 bottom-0 z-[55] mx-auto w-full transition-transform duration-150 ease-out"
              style={{
                height: 0,
                transform:
                  mediaPanelMode === "focus" && (!isAudioWitnessFocusMode || shouldRenderInlineAudioFocusPanel)
                    ? `translateY(${mediaPanelIntent?.kind === "audio" ? MEDIA_PANEL_AUDIO_FOCUS_TOP_INSET_PX : MEDIA_PANEL_VISUAL_FOCUS_TOP_INSET_PX}px)`
                    : "translateY(0)",
              }}
            />
          </div>

          <div data-oc-transport-action-rail-shell="1" className="relative px-0 pt-0.5 opacity-95">
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <TransportBar />
              </div>
              <div className="flex-none" />
            </div>
          </div>
        </div>

        <div className="px-4 pt-0.5">
          <div
            data-oc-inline-witness-rail
            className="relative z-50 mx-auto w-full overflow-hidden rounded-full pointer-events-auto"
            style={{
              height: 34,
              marginTop: 2,
              marginBottom: 4,
              background: "linear-gradient(180deg, rgba(255,255,255,0.025), rgba(255,255,255,0.01))",
              border: "1px solid rgba(255,255,255,0.04)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
            }}
          />
          {audioWitnessUi.activeCapsuleId ? (
            <Suspense fallback={null}>
              <LazyInlineWitness activeClipId={audioWitnessUi.activeCapsuleId} />
            </Suspense>
          ) : null}
        </div>
        <div className="relative flex-1 min-h-0" style={{ minHeight: MIN_TIMELINE_SCROLLER_HEIGHT_PX }}>
          <div
            ref={scrollerRef}
            data-oc-scroller="1"
            data-oc-scroll-authority="1"
            className={`h-full overflow-x-auto overflow-y-auto px-4 pb-4 pt-3 ${isEmptySpacePanning ? "cursor-grabbing" : ""}`}
            style={{ paddingBottom: RULER_BASE_CONTENT_CLEARANCE_PX }}
            onPointerDownCapture={onScrollerPointerDownCapture}
            onPointerMoveCapture={onScrollerPointerMoveCapture}
            onPointerUpCapture={onScrollerPointerUpCapture}
            onPointerCancelCapture={onScrollerPointerCancelCapture}
          >
            <div className="relative" style={{ width: laneWidthPx }}>
              <div className="pointer-events-none absolute inset-0 z-[32]" aria-hidden>
                <div
                  className="absolute inset-y-0"
                  style={{
                    left: playheadTimelineX,
                    width: 2,
                    transform: "translateX(-1px)",
                    background:
                      "linear-gradient(180deg, rgba(221,214,254,0.95), rgba(196,181,253,0.58), rgba(221,214,254,0.22))",
                    boxShadow: "0 0 14px rgba(196,181,253,0.35)",
                  }}
                />

                {rulerHoverTimelineX != null && (
                  <div
                    className="absolute inset-y-0"
                    style={{
                      left: rulerHoverTimelineX,
                      width: 42,
                      transform: "translateX(-50%)",
                      background:
                        "linear-gradient(90deg, rgba(34,211,238,0.00), rgba(34,211,238,0.11), rgba(34,211,238,0.00))",
                      filter: "blur(7px)",
                      opacity: rulerScrubTimelineX != null ? 1 : 0.58,
                    }}
                  />
                )}

                {rulerScrubTimelineX != null && (
                  <div
                    className="absolute inset-y-0"
                    style={{
                      left: rulerScrubTimelineX,
                      width: 2,
                      transform: "translateX(-1px)",
                      background:
                        "linear-gradient(180deg, rgba(207,250,254,0.98), rgba(34,211,238,0.78), rgba(165,243,252,0.25))",
                      boxShadow: "0 0 22px rgba(34,211,238,0.55)",
                    }}
                  />
                )}
              </div>

              <div className="pointer-events-none absolute inset-0 z-[60]">
                <div className="pointer-events-auto">
                  {mediaPanelMode !== "closed" ? (
                    <Suspense fallback={null}>
                      <LazyMediaPanel
                        mode={shouldRenderTallMediaPanel ? mediaPanelMode : "closed"}
                        isPinned={mediaPanelPinned}
                        focusIntent={mediaPanelIntent}
                        onRequestMode={(nextMode, nextPinned, options) =>
                          requestMediaPanelMode({
                            mode: nextMode,
                            pinned: nextPinned,
                            preserveWitnessBar: options?.preserveWitnessBar,
                            source: "mediaPanel",
                          })
                        }
                      />
                    </Suspense>
                  ) : null}
                </div>
              </div>

              {selectedPromotedRange && (
                <div className="pointer-events-none fixed right-5 top-24 z-[2147483643] w-[320px]">
                  <div
                    className="pointer-events-auto rounded-2xl border border-white/14 bg-[rgba(4,8,16,0.9)] p-3 shadow-[0_24px_80px_rgba(0,0,0,0.42)]"
                    style={{
                      backdropFilter: "blur(14px)",
                      WebkitBackdropFilter: "blur(14px)",
                    }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-emerald-100/86">Range Inspector</div>
                        <div className="mt-0.5 text-[11px] text-white/54">
                          {Math.max(0, Number(selectedPromotedRange.endSec ?? 0) - Number(selectedPromotedRange.startSec ?? 0)).toFixed(2)}s span · {selectedPromotedRange.markerSpan?.markerIds?.length ?? 0} anchors
                        </div>
                      </div>
                      <button
                        type="button"
                        className="rounded-md border border-white/14 bg-white/8 px-2 py-1 text-[11px] font-semibold text-white/80"
                        onClick={closeRangeInspector}
                      >
                        Close
                      </button>
                    </div>

                    <div className="mt-3 space-y-2">
                      <label className="block">
                        <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-white/62">Label</div>
                        <input
                          type="text"
                          value={rangeInspectorDraft.label}
                          onChange={(e) => setRangeInspectorDraft((prev) => ({ ...prev, label: e.currentTarget.value }))}
                          className="w-full rounded-md border border-white/20 bg-white/8 px-2 py-1 text-xs text-white outline-none focus:border-emerald-300/70"
                          placeholder="Range label"
                        />
                      </label>

                      <label className="block">
                        <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-white/62">Kind</div>
                        <select
                          value={rangeInspectorDraft.kind}
                          onChange={(e) => setRangeInspectorDraft((prev) => ({ ...prev, kind: e.currentTarget.value }))}
                          className="w-full rounded-md border border-white/20 bg-white/8 px-2 py-1 text-xs text-white outline-none focus:border-emerald-300/70"
                        >
                          {RANGE_KIND_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value} className="bg-slate-950 text-white">
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-2">
                      {[
                        { key: "start", marker: selectedRangeStartMarker, label: "Start Anchor" },
                        { key: "end", marker: selectedRangeEndMarker, label: "End Anchor" },
                      ].map((item) => {
                        const marker = item.marker as any;
                        const markerId = String(marker?.id ?? "").trim();
                        return (
                          <div key={item.key} className="rounded-xl border border-white/10 bg-white/[0.04] px-2 py-2">
                            <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-white/58">{item.label}</div>
                            <div className="mt-1 text-[12px] font-semibold text-white/92">
                              {String(marker?.label ?? marker?.note ?? "Missing marker").trim() || "Missing marker"}
                            </div>
                            <div className="mt-1 text-[10px] uppercase tracking-[0.08em] text-white/48">
                              {marker?.frameIndex != null ? `F${marker.frameIndex}` : `${Number(marker?.timeSec ?? 0).toFixed(2)}s`} · {formatMarkerTypeLabel(marker?.type)}
                            </div>
                            <button
                              type="button"
                              className="mt-2 rounded-md border border-white/14 bg-white/8 px-2 py-1 text-[10px] font-semibold text-white/86"
                              onClick={() => markerId && jumpToMarker(markerId, `range-inspector/${item.key}-anchor`)}
                              disabled={!markerId}
                            >
                              Jump
                            </button>
                          </div>
                        );
                      })}
                    </div>

                    <div className="mt-3 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2">
                      <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-white/58">Structural Binding</div>
                      <div className="mt-1 text-[11px] leading-[1.45] text-white/66">
                        Selected clip: {selectedTimelineClip ? resolveTimelineClipTitle(selectedTimelineClip as any, selectedTimelineClip?.label ?? null, selectedTimelineClip?.kind ?? null) : "None"}
                      </div>
                      <div className="text-[11px] leading-[1.45] text-white/66">
                        Target lane: {selectedTimelineTrack ? String((selectedTimelineTrack as any)?.name ?? selectedTimelineTrack?.id ?? "Unknown lane") : "Select a clip to bind its lane"}
                      </div>
                      <div className="mt-1 text-[10px] uppercase tracking-[0.08em] text-white/42">
                        Kind: {formatRangeKindLabel(rangeInspectorDraft.kind)}
                      </div>
                      {selectedTimelineClip?.rangeAnchor?.rangeId ? (
                        <label className="mt-2 flex items-center gap-2 text-[11px] text-white/72">
                          <input
                            type="checkbox"
                            checked={selectedClipFollowsRangeUpdates}
                            onChange={(e) => toggleSelectedClipRangeFollowUpdates(e.currentTarget.checked)}
                            className="h-4 w-4 rounded border-white/20 bg-transparent text-emerald-300 focus:ring-emerald-300/40"
                          />
                          Follow anchor updates automatically
                        </label>
                      ) : null}
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="rounded-md border border-emerald-300/35 bg-emerald-300/12 px-2 py-1 text-[11px] font-semibold text-emerald-50"
                        onClick={saveSelectedPromotedRange}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className="rounded-md border border-white/14 bg-white/8 px-2 py-1 text-[11px] font-semibold text-white/90"
                        onClick={rebindSelectedRangeFromMarkerSelection}
                        disabled={selectedMarkerIds.length < 2}
                      >
                        Rebind To Selected Markers
                      </button>
                      <button
                        type="button"
                        className="rounded-md border border-cyan-300/24 bg-cyan-300/10 px-2 py-1 text-[11px] font-semibold text-cyan-50"
                        onClick={bindSelectedClipToPromotedRange}
                        disabled={!selectedTimelineClip}
                      >
                        Fit Selected Clip
                      </button>
                      <button
                        type="button"
                        className="rounded-md border border-cyan-300/24 bg-cyan-300/10 px-2 py-1 text-[11px] font-semibold text-cyan-50"
                        onClick={anchorSelectedClipLaneToPromotedRange}
                        disabled={!selectedTimelineTrack}
                      >
                        Anchor Clip Lane
                      </button>
                    </div>

                    <div className="mt-2 text-[10px] leading-[1.4] text-white/42">
                      Use the marker browser selection to rebind anchor markers. Clip and lane actions target the currently selected clip.
                    </div>
                  </div>
                </div>
              )}

              {markerSurface && (
                <div className="pointer-events-none fixed inset-0 z-[2147483645]">
                  <div
                    ref={markerSurfaceRef}
                    className="pointer-events-auto fixed z-[2147483646] flex min-w-[220px] flex-col overflow-hidden rounded-xl border border-white/28 bg-white/[0.16] dark:bg-black/[0.34] shadow-[0_20px_54px_rgba(0,0,0,0.34)]"
                    style={{
                      left: markerSurfacePosition?.left ?? MARKER_SURFACE_VIEWPORT_GAP_PX,
                      top: markerSurfacePosition?.top ?? MARKER_SURFACE_VIEWPORT_GAP_PX,
                      width: markerSurfacePosition?.width ?? MARKER_SURFACE_ESTIMATED_MENU_WIDTH_PX,
                      height: markerSurfacePosition?.height ?? MARKER_SURFACE_ESTIMATED_MENU_HEIGHT_PX,
                      maxHeight: "calc(100vh - 20px)",
                      transformOrigin: markerSurfacePosition?.transformOrigin ?? "bottom left",
                      backdropFilter: "blur(14px)",
                      WebkitBackdropFilter: "blur(14px)",
                    }}
                  >
                    <div
                      className="min-h-0 flex-1 overflow-y-scroll p-2 pr-3"
                      style={{
                        scrollbarGutter: "stable both-edges",
                        scrollbarWidth: "thin",
                        overscrollBehavior: "contain",
                      }}
                    >
                      {(() => {
                        const marker = markerById.get(markerSurface.markerId);
                        const markerOrigin = normalizeMarkerOrigin(marker?.origin ?? marker?.annotation?.origin, "");
                        const showOriginPreset = markerOrigin === "transport_panel" || markerOrigin === "annotation_surface";
                        const originPresetLabel = markerOrigin === "transport_panel" ? "Reset To Panel Preset" : "Reset To Annotation Preset";
                        const originHelper = markerOrigin === "transport_panel"
                          ? "Panel markers default to cue semantics and same-source navigation when possible."
                          : markerOrigin === "annotation_surface"
                            ? "Annotation markers keep review-oriented defaults and annotation color mapping."
                            : "";

                        return (
                          <>
                    <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-cyan-50 [text-shadow:0_0_16px_rgba(56,189,248,0.28)]">
                      Marker Actions
                    </div>

                    <div className="mb-2 flex items-center justify-between gap-2">
                      <div className="rounded-full border border-cyan-300/20 bg-cyan-300/[0.08] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-sky-300/80 dark:text-cyan-200/68 [text-shadow:0_0_10px_rgba(56,189,248,0.12)]">
                        {formatMarkerOriginLabel(markerOrigin || "marker")}
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.08em] text-sky-300/72 dark:text-cyan-200/54 [text-shadow:0_0_8px_rgba(56,189,248,0.1)]">
                        Nav: {markerNavigationScopeLabel}
                      </div>
                    </div>

                    {showOriginPreset ? (
                      <div className="mb-2 rounded-lg border border-white/22 bg-white/[0.14] dark:bg-white/[0.08] px-2 py-1.5">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-sky-300/82 dark:text-cyan-200/70 [text-shadow:0_0_10px_rgba(56,189,248,0.12)]">
                          Source Preset
                        </div>
                        <div className="mt-1 text-[11px] leading-[1.42] text-sky-200/92 dark:text-sky-200/78 [text-shadow:0_0_10px_rgba(56,189,248,0.12)]">{originHelper}</div>
                        <button
                          type="button"
                          className="mt-2 rounded-md border border-white/22 bg-white/10 px-2 py-1 text-[11px] font-semibold text-white"
                          onClick={() => setMarkerSurfaceDraft((prev) => applyMarkerOriginPreset(marker, prev))}
                        >
                          {originPresetLabel}
                        </button>
                      </div>
                    ) : null}

                    {markerSurface.mode === "edit" ? (
                      <div className="space-y-2">
                        <div className="grid grid-cols-[minmax(0,1.45fr)_minmax(0,1fr)_88px] gap-2">
                          <label className="block min-w-0">
                            <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-sky-300/82 dark:text-cyan-200/70 [text-shadow:0_0_8px_rgba(56,189,248,0.1)]">Label</div>
                            <input
                              type="text"
                              value={markerSurfaceDraft.label}
                              onChange={(e) => setMarkerSurfaceDraft((prev) => ({ ...prev, label: e.currentTarget.value }))}
                              className="w-full rounded-md border border-white/24 bg-white/[0.16] dark:bg-white/[0.08] px-2 py-1 text-xs text-white outline-none focus:border-cyan-300/70"
                              placeholder="Marker label"
                              autoFocus
                            />
                          </label>

                          <label className="block min-w-0">
                            <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-sky-300/82 dark:text-cyan-200/70 [text-shadow:0_0_8px_rgba(56,189,248,0.1)]">Type</div>
                            <select
                              value={markerSurfaceDraft.type}
                              onChange={(e) => setMarkerSurfaceDraft((prev) => ({ ...prev, type: e.currentTarget.value }))}
                              className="w-full rounded-md border border-white/24 bg-white/[0.16] dark:bg-white/[0.08] px-2 py-1 text-xs text-white outline-none focus:border-cyan-300/70"
                            >
                              {MARKER_TYPE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value} className="bg-slate-950 text-white">
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </label>

                          <label className="block">
                            <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-sky-300/82 dark:text-cyan-200/70 [text-shadow:0_0_8px_rgba(56,189,248,0.1)]">Visible</div>
                            <div className="flex h-[30px] items-center justify-center rounded-md border border-white/24 bg-white/[0.16] dark:bg-white/[0.08] px-2 text-xs text-white">
                              <input
                                type="checkbox"
                                checked={markerSurfaceDraft.visible}
                                onChange={(e) => setMarkerSurfaceDraft((prev) => ({ ...prev, visible: e.currentTarget.checked }))}
                                className="h-3.5 w-3.5 rounded border-white/20 bg-transparent text-cyan-300 focus:ring-cyan-300/40"
                              />
                            </div>
                          </label>
                        </div>

                        <div className="grid grid-cols-[minmax(0,1fr)_176px] gap-3">
                          <label className="block min-w-0">
                            <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-sky-300/82 dark:text-cyan-200/70 [text-shadow:0_0_8px_rgba(56,189,248,0.1)]">Note</div>
                            <textarea
                              value={markerSurfaceDraft.note}
                              onChange={(e) => setMarkerSurfaceDraft((prev) => ({ ...prev, note: e.currentTarget.value }))}
                              onKeyDown={(e) => {
                                if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
                                  e.preventDefault();
                                  saveMarkerDraft(markerSurface.markerId, markerSurfaceDraft);
                                }
                              }}
                              className="min-h-[92px] w-full resize-none rounded-md border border-white/24 bg-white/[0.16] dark:bg-white/[0.08] px-2 py-1 text-xs text-white outline-none focus:border-cyan-300/70"
                              placeholder="Editorial note"
                            />
                          </label>

                          <div className="min-w-0">
                            <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-sky-300/82 dark:text-cyan-200/70 [text-shadow:0_0_8px_rgba(56,189,248,0.1)]">Color</div>
                            <div className="rounded-md border border-white/22 bg-white/[0.14] dark:bg-white/[0.08] p-2">
                              <div className="flex flex-wrap gap-1.5">
                                {MARKER_COLOR_SWATCHES.map((color) => {
                                  const selected = markerSurfaceDraft.color.toLowerCase() === color.toLowerCase();
                                  return (
                                    <button
                                      key={color}
                                      type="button"
                                      className="h-6 w-6 rounded-full border"
                                      style={{
                                        backgroundColor: color,
                                        borderColor: selected ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.2)",
                                        boxShadow: selected ? "0 0 0 1px rgba(255,255,255,0.32)" : "none",
                                      }}
                                      onClick={() => setMarkerSurfaceDraft((prev) => ({ ...prev, color }))}
                                      aria-label={`Marker color ${color}`}
                                    />
                                  );
                                })}
                              </div>
                              <div className="mt-2 flex items-center gap-2">
                                <input
                                  type="color"
                                  value={markerSurfaceDraft.color || "#38bdf8"}
                                  onChange={(e) => setMarkerSurfaceDraft((prev) => ({ ...prev, color: e.currentTarget.value }))}
                                  className="h-8 w-10 rounded border border-white/24 bg-white/[0.16] dark:bg-white/[0.08] p-0.5"
                                  aria-label="Custom marker color"
                                />
                                <button
                                  type="button"
                                  className="rounded-md border border-white/24 bg-white/[0.16] dark:bg-white/[0.08] px-2 py-1 text-[11px] font-semibold text-white"
                                  onClick={() => setMarkerSurfaceDraft((prev) => ({ ...prev, color: "" }))}
                                >
                                  Clear
                                </button>
                              </div>
                              <div className="mt-2 rounded-md border border-white/18 bg-white/[0.14] dark:bg-white/[0.08] px-2 py-1 text-[11px] text-sky-200/84 dark:text-sky-100/72">
                                {`F${Math.max(0, Number(markerById.get(markerSurface.markerId)?.frameIndex ?? Math.round(Number(markerById.get(markerSurface.markerId)?.timeSec ?? 0) * (Number(norm.snap?.fps ?? 30) || 30))))}`}
                              </div>
                            </div>
                          </div>
                        </div>

                        <input
                          type="text"
                          value={`${formatMarkerTypeLabel(markerSurfaceDraft.type)} · ${markerSurfaceDraft.visible ? "Visible" : "Hidden"}`}
                          readOnly
                          className="w-full rounded-md border border-white/18 bg-white/[0.14] dark:bg-white/[0.08] px-2 py-1 text-[11px] text-sky-200/84 dark:text-sky-100/72 outline-none"
                        />

                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            className="rounded-md border border-white/24 bg-white/[0.16] dark:bg-white/[0.08] px-2 py-1 text-[11px] font-semibold text-white"
                            onClick={() => setMarkerSurface((prev) => (prev ? { ...prev, mode: "menu" } : prev))}
                          >
                            Back
                          </button>
                          <button
                            type="button"
                            className="rounded-md border border-cyan-300/45 bg-cyan-300/18 px-2 py-1 text-[11px] font-semibold text-cyan-50"
                            onClick={() => saveMarkerDraft(markerSurface.markerId, markerSurfaceDraft)}
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <button
                          type="button"
                          className="block w-full rounded-md border border-white/24 bg-white/[0.16] dark:bg-white/[0.08] px-2 py-1 text-left text-[11px] font-semibold text-white hover:bg-white/[0.22] dark:hover:bg-white/[0.12]"
                          onClick={() => {
                            jumpToMarker(markerSurface.markerId);
                            closeMarkerSurface();
                          }}
                        >
                          Jump To Marker
                        </button>

                        <button
                          type="button"
                          className="block w-full rounded-md border border-white/24 bg-white/[0.16] dark:bg-white/[0.08] px-2 py-1 text-left text-[11px] font-semibold text-white hover:bg-white/[0.22] dark:hover:bg-white/[0.12]"
                          onClick={() => {
                            const marker = markerById.get(markerSurface.markerId);
                            setMarkerSurfaceDraft(buildMarkerSurfaceDraft(marker));
                            setMarkerSurface((prev) => (prev ? { ...prev, mode: "edit" } : prev));
                          }}
                        >
                          Edit Marker
                        </button>

                        <button
                          type="button"
                          className="block w-full rounded-md border border-rose-400/40 bg-rose-400/12 px-2 py-1 text-left text-[11px] font-semibold text-rose-100 hover:bg-rose-400/22"
                          onClick={() => deleteMarker(markerSurface.markerId)}
                        >
                          Delete Marker
                        </button>
                      </div>
                    )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              )}

              {markerBrowserOpen && (
                <div
                  className="pointer-events-none fixed inset-0 z-[2147483644] flex items-start justify-center px-6"
                  style={{
                    paddingTop: "clamp(9.75rem, 22vh, 12rem)",
                  }}
                >
                  <div
                    data-oc-marker-browser-root="1"
                    className="pointer-events-auto w-full max-w-3xl rounded-2xl border border-white/28 bg-white/[0.16] dark:bg-black/[0.34] shadow-[0_34px_96px_rgba(0,0,0,0.34)]"
                    style={{
                      backdropFilter: "blur(18px)",
                      WebkitBackdropFilter: "blur(18px)",
                    }}
                  >
                    <div className="border-b border-white/10 px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div>
                          <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-cyan-50 [text-shadow:0_0_16px_rgba(56,189,248,0.28)]">Marker Browser</div>
                          <div className="text-[11px] text-sky-300/72 dark:text-cyan-200/58 [text-shadow:0_0_8px_rgba(56,189,248,0.08)]">
                            {filteredMarkerBrowserEntries.length} visible marker{filteredMarkerBrowserEntries.length === 1 ? "" : "s"} · session restore enabled
                          </div>
                        </div>
                        <input
                          type="text"
                          value={markerBrowserQuery}
                          onChange={(e) => setMarkerBrowserQuery(e.currentTarget.value)}
                          placeholder="Search label, note, frame, type, origin"
                          className="ml-auto w-full max-w-[360px] rounded-lg border border-white/22 bg-white/[0.16] dark:bg-white/[0.08] px-3 py-2 text-sm text-white placeholder:text-sky-300/52 dark:placeholder:text-cyan-200/34 outline-none focus:border-cyan-300/65"
                          autoFocus
                        />
                        <button
                          type="button"
                          className="rounded-lg border border-white/24 bg-white/[0.16] dark:bg-white/[0.08] px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-white"
                          onClick={closeMarkerBrowser}
                        >
                          Close
                        </button>
                      </div>

                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <label
                          className={[
                            "inline-flex h-9 items-center gap-2 rounded-full border px-3 text-[11px] font-semibold uppercase tracking-[0.08em]",
                            markerBrowserChipClass("violet"),
                          ].join(" ")}
                        >
                          <span className="text-sky-300/82 dark:text-cyan-200/64">Origin</span>
                          <select
                            value={markerBrowserOriginFilter}
                            onChange={(e) => setMarkerBrowserOriginFilter(e.currentTarget.value)}
                            className="min-w-[132px] bg-transparent text-[11px] font-semibold uppercase tracking-[0.08em] text-inherit outline-none"
                          >
                            <option value="all" className="bg-slate-950 text-white">All origins</option>
                            {markerBrowserOriginOptions.map((origin) => (
                              <option key={origin} value={origin} className="bg-slate-950 text-white">
                                {formatMarkerOriginLabel(origin)}
                              </option>
                            ))}
                          </select>
                        </label>

                        <label
                          className={[
                            "inline-flex h-9 items-center gap-2 rounded-full border px-3 text-[11px] font-semibold uppercase tracking-[0.08em]",
                            markerBrowserChipClass("emerald"),
                          ].join(" ")}
                        >
                          <span className="text-sky-300/82 dark:text-cyan-200/64">Type</span>
                          <select
                            value={markerBrowserTypeFilter}
                            onChange={(e) => setMarkerBrowserTypeFilter(e.currentTarget.value)}
                            className="min-w-[116px] bg-transparent text-[11px] font-semibold uppercase tracking-[0.08em] text-inherit outline-none"
                          >
                            <option value="all" className="bg-slate-950 text-white">All types</option>
                            {markerBrowserTypeOptions.map((type) => (
                              <option key={type} value={type} className="bg-slate-950 text-white">
                                {formatMarkerTypeLabel(type)}
                              </option>
                            ))}
                          </select>
                        </label>

                        <div className="flex flex-wrap items-center gap-2">
                          {(["time", "origin", "type"] as MarkerBrowserGroupMode[]).map((mode) => {
                            const tone = mode === "time" ? "cyan" : mode === "origin" ? "violet" : "emerald";
                            const active = markerBrowserGroupMode === mode;
                            return (
                              <button
                                key={mode}
                                type="button"
                                className={[
                                  "inline-flex h-9 items-center rounded-full border px-3 text-[11px] font-semibold uppercase tracking-[0.08em] transition",
                                  markerBrowserChipClass(active ? tone : "slate"),
                                ].join(" ")}
                                onClick={() => setMarkerBrowserGroupMode(mode)}
                              >
                                {mode}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-white/8 pt-3">
                        <button
                          type="button"
                          className="rounded-md border border-white/22 bg-white/[0.16] dark:bg-white/[0.08] px-2 py-1 text-[11px] font-semibold text-white"
                          onClick={selectAllVisibleMarkers}
                        >
                          Select All Visible
                        </button>
                        <button
                          type="button"
                          className="rounded-md border border-white/22 bg-white/[0.16] dark:bg-white/[0.08] px-2 py-1 text-[11px] font-semibold text-white"
                          onClick={clearSelectedMarkers}
                        >
                          Clear Selection
                        </button>
                        <button
                          type="button"
                          className="rounded-md border border-emerald-300/24 bg-emerald-300/10 px-2 py-1 text-[11px] font-semibold text-emerald-50"
                          onClick={promoteSelectedMarkersToRange}
                          disabled={selectedMarkerIds.length < 2}
                        >
                          Promote To Range
                        </button>
                        <button
                          type="button"
                          className="rounded-md border border-white/22 bg-white/[0.16] dark:bg-white/[0.08] px-2 py-1 text-[11px] font-semibold text-white"
                          onClick={() => applyBulkVisibility(false)}
                          disabled={!selectedMarkerBrowserEntries.length}
                        >
                          Hide Selected
                        </button>
                        <button
                          type="button"
                          className="rounded-md border border-white/22 bg-white/[0.16] dark:bg-white/[0.08] px-2 py-1 text-[11px] font-semibold text-white"
                          onClick={() => applyBulkVisibility(true)}
                          disabled={!selectedMarkerBrowserEntries.length}
                        >
                          Show Selected
                        </button>
                        <button
                          type="button"
                          className="rounded-md border border-cyan-300/24 bg-cyan-300/10 px-2 py-1 text-[11px] font-semibold text-cyan-50"
                          onClick={() => exportMarkerNotes("text")}
                        >
                          Copy Notes
                        </button>
                        <button
                          type="button"
                          className="rounded-md border border-cyan-300/24 bg-cyan-300/10 px-2 py-1 text-[11px] font-semibold text-cyan-50"
                          onClick={() => exportMarkerNotes("json")}
                        >
                          Export JSON
                        </button>
                        <button
                          type="button"
                          className="rounded-md border border-rose-400/34 bg-rose-400/10 px-2 py-1 text-[11px] font-semibold text-rose-100"
                          onClick={deleteSelectedMarkers}
                          disabled={!selectedMarkerBrowserEntries.length}
                        >
                          Delete Selected
                        </button>
                        <div className="ml-auto text-[11px] text-sky-300/72 dark:text-cyan-200/58 [text-shadow:0_0_8px_rgba(56,189,248,0.08)]">
                          {selectedMarkerBrowserEntries.length} selected
                        </div>
                      </div>
                    </div>

                    <div className="max-h-[60vh] overflow-y-auto px-4 py-3">
                      {groupedMarkerBrowserEntries.length ? (
                        <div className="space-y-3">
                          {groupedMarkerBrowserEntries.map((group) => (
                            <div key={group.label} className="space-y-2">
                              <div className="sticky top-0 z-[1] rounded-lg border border-white/18 bg-white/[0.14] dark:bg-white/[0.08] px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-sky-300/78 dark:text-cyan-200/66 [text-shadow:0_0_8px_rgba(56,189,248,0.08)]">
                                {group.label} · {group.markers.length}
                              </div>
                              {group.markers.map((marker) => {
                                const selected = selectedMarkerIdSet.has(marker.id);
                                return (
                                  <div
                                    key={marker.id}
                                    className="rounded-xl border px-3 py-2"
                                    style={{
                                      borderColor: selected ? "rgba(56,189,248,0.42)" : "rgba(255,255,255,0.1)",
                                      background: selected ? "rgba(56,189,248,0.18)" : "rgba(255,255,255,0.12)",
                                    }}
                                  >
                                    <div className="flex items-start gap-3">
                                      <input
                                        type="checkbox"
                                        checked={selected}
                                        onChange={() => toggleMarkerBrowserSelection(marker.id)}
                                        className="mt-1 h-4 w-4 rounded border-white/20 bg-transparent text-cyan-300 focus:ring-cyan-300/40"
                                      />

                                      <div className="min-w-0 flex-1">
                                        <div className="flex flex-wrap items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.08em]">
                                          <span
                                            className={[
                                              "inline-flex h-6 items-center rounded-full border px-2.5",
                                              markerBrowserChipClass(resolveMarkerBrowserOriginTone(marker.origin || "marker")),
                                            ].join(" ")}
                                          >
                                            {formatMarkerOriginLabel(marker.origin || "marker")}
                                          </span>
                                          <span
                                            className={[
                                              "inline-flex h-6 items-center rounded-full border px-2.5",
                                              markerBrowserChipClass(resolveMarkerBrowserTypeTone(marker.type)),
                                            ].join(" ")}
                                          >
                                            {formatMarkerTypeLabel(marker.type)}
                                          </span>
                                          <span className={["inline-flex h-6 items-center rounded-full border px-2.5", markerBrowserChipClass("slate")].join(" ")}>
                                            {`${marker.timeSec.toFixed(2)}s`}
                                          </span>
                                          {marker.frameIndex != null ? (
                                            <span className={["inline-flex h-6 items-center rounded-full border px-2.5", markerBrowserChipClass("amber")].join(" ")}>
                                              {`F${marker.frameIndex}`}
                                            </span>
                                          ) : null}
                                          <span className={[
                                            "inline-flex h-6 items-center rounded-full border px-2.5",
                                            markerBrowserChipClass(marker.visible ? "emerald" : "rose"),
                                          ].join(" ")}>
                                            {marker.visible ? "Visible" : "Hidden"}
                                          </span>
                                        </div>
                                        <div className="mt-1 text-sm font-semibold text-sky-100/94 dark:text-sky-100/88 [text-shadow:0_0_10px_rgba(56,189,248,0.1)]">
                                          {marker.label || marker.note || "Untitled marker"}
                                        </div>
                                        {marker.note ? (
                                          <div className="mt-1 line-clamp-2 text-[12px] leading-5 text-sky-200/82 dark:text-sky-100/74">{marker.note}</div>
                                        ) : null}
                                      </div>

                                      <div className="flex items-center gap-1">
                                        <button
                                          type="button"
                                          className="rounded-md border border-white/22 bg-white/[0.16] dark:bg-white/[0.08] px-2 py-1 text-[11px] font-semibold text-white"
                                          onClick={() => {
                                            jumpToMarker(marker.id, "marker-browser/jump");
                                            closeMarkerBrowser();
                                          }}
                                        >
                                          Jump
                                        </button>
                                        <button
                                          type="button"
                                          className="rounded-md border border-cyan-300/24 bg-cyan-300/10 px-2 py-1 text-[11px] font-semibold text-cyan-50"
                                          onClick={() => {
                                            openMarkerSurface({ markerId: marker.id }, "edit");
                                            closeMarkerBrowser();
                                          }}
                                        >
                                          Edit
                                        </button>
                                        <button
                                          type="button"
                                          className="rounded-md border border-rose-400/34 bg-rose-400/10 px-2 py-1 text-[11px] font-semibold text-rose-100"
                                          onClick={() => deleteMarker(marker.id)}
                                        >
                                          Delete
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="rounded-xl border border-white/18 bg-white/[0.14] dark:bg-white/[0.08] px-4 py-6 text-center text-sm text-sky-200/82 dark:text-sky-100/74">
                          No markers match the current search or filters.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="absolute inset-0 z-30">
                <EmotionalArcOverlay
                  segments={authoredEmotionalIntentSegments}
                  pxPerSec={pxPerSec}
                  viewportStartSec={norm.viewportStartSec}
                  viewportEndSec={norm.viewportEndSec}
                  scrollLeft={norm.viewportStartSec * pxPerSec}
                />

                <FxRegionOverlay
                  nodes={authoredFxIntentNodes}
                  pxPerSec={pxPerSec}
                  viewportStartSec={norm.viewportStartSec}
                  viewportEndSec={norm.viewportEndSec}
                />

                <ContinuityWarningOverlay
                  diagnostics={continuityDiagnostics}
                  pxPerSec={pxPerSec}
                  viewportStartSec={norm.viewportStartSec}
                  viewportEndSec={norm.viewportEndSec}
                />

                {narrativePreview.active && Array.isArray(narrativePreview.overlays) && narrativePreview.overlays.length > 0 ? (
                  <Suspense fallback={null}>
                    <LazyNarrativePreviewOverlay
                      overlays={narrativePreview.overlays}
                      pxPerSec={pxPerSec}
                      viewportStartSec={norm.viewportStartSec}
                      viewportEndSec={norm.viewportEndSec}
                    />
                  </Suspense>
                ) : null}

                <RangeOverlay
                  ranges={((state as any).ranges ?? []) as any}
                  pxPerSec={pxPerSec}
                  viewportStartSec={norm.viewportStartSec}
                  viewportEndSec={norm.viewportEndSec}
                  selectedRangeId={(state as any).selectedRangeId ?? null}
                  onSelect={(id) =>
                    dispatch({
                      type: "range/select",
                      payload: { rangeId: id },
                      meta: { source: "user" },
                    } as any)
                  }
                  durationSec={norm.durationSec}
                  snap={(state as any).snap ?? { enabled: false, mode: "second", fps: 30 }}
                  dispatch={dispatch as any}
                />

                <MarkerOverlay
                  markers={norm.markers}
                  pxPerSec={pxPerSec}
                  viewportStartSec={norm.viewportStartSec}
                  viewportEndSec={norm.viewportEndSec}
                  durationSec={norm.durationSec}
                  dispatch={dispatch}
                  snap={norm.snap}
                  activeMarkerId={activeMarkerId}
                  onActiveMarkerChange={(markerId, source) => setActiveMarker(markerId, source)}
                />

                {devNarrativeEnabled && (
                  <Suspense fallback={null}>
                    <LazyTurningPointMarkers
                      turningPoints={turningPoints}
                      pxPerSec={pxPerSec}
                      viewportStartSec={norm.viewportStartSec}
                      viewportEndSec={norm.viewportEndSec}
                      widthPx={laneWidthPx}
                    />
                  </Suspense>
                )}

                {devNarrativeEnabled ? (
                  <Suspense fallback={null}>
                    <LazyTurningPointProbe turningPoints={turningPoints} />
                  </Suspense>
                ) : null}
              </div>

              {lanesNode}
            </div>
          </div>

          <div
            data-oc-ruler-base-rail="1"
            className="pointer-events-none absolute inset-x-4 bottom-0 z-40"
            style={{ paddingBottom: RULER_BASE_SCROLLBAR_GAP_PX }}
          >
            <div
              className="pointer-events-auto relative overflow-visible border-t border-white/15 bg-black/70"
              style={{
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                boxShadow: "0 -10px 28px rgba(0,0,0,0.34)",
              }}
            >
              <div
                className="pointer-events-none relative z-[4] flex items-center border-b border-white/10 px-2"
                style={{
                  height: RULER_SEMANTIC_BAR_HEIGHT_PX,
                  background:
                    "linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.008))",
                }}
              >
                <div className="pointer-events-auto flex items-center gap-2">
                  <button
                    type="button"
                    className="rounded-full px-[10px] py-[1px] text-[10px] font-semibold uppercase tracking-[0.08em]"
                    style={{
                      color: showRulerEventMarkers
                        ? "rgba(233,225,255,0.98)"
                        : "rgba(210,204,228,0.6)",
                      background: showRulerEventMarkers
                        ? "linear-gradient(180deg, rgba(196,181,253,0.38), rgba(147,51,234,0.22))"
                        : "linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0.04))",
                      border: showRulerEventMarkers
                        ? "1px solid rgba(196,181,253,0.70)"
                        : "1px solid rgba(255,255,255,0.18)",
                      boxShadow: showRulerEventMarkers
                        ? "0 0 10px rgba(167,139,250,0.34), inset 0 0 8px rgba(216,180,254,0.2)"
                        : "0 0 0 rgba(0,0,0,0)",
                    }}
                    onClick={() => setShowRulerEventMarkers((v) => !v)}
                    aria-pressed={showRulerEventMarkers}
                  >
                    Events
                  </button>

                  <button
                    type="button"
                    className="rounded-full px-[10px] py-[1px] text-[10px] font-semibold uppercase tracking-[0.08em]"
                    style={{
                      color: showRulerBeatMarkers
                        ? "rgba(220,255,255,0.98)"
                        : "rgba(186,218,218,0.6)",
                      background: showRulerBeatMarkers
                        ? "linear-gradient(180deg, rgba(34,211,238,0.38), rgba(14,116,144,0.22))"
                        : "linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0.04))",
                      border: showRulerBeatMarkers
                        ? "1px solid rgba(34,211,238,0.70)"
                        : "1px solid rgba(255,255,255,0.18)",
                      boxShadow: showRulerBeatMarkers
                        ? "0 0 10px rgba(34,211,238,0.34), inset 0 0 8px rgba(103,232,249,0.2)"
                        : "0 0 0 rgba(0,0,0,0)",
                    }}
                    onClick={() => setShowRulerBeatMarkers((v) => !v)}
                    aria-pressed={showRulerBeatMarkers}
                  >
                    Beats
                  </button>

                  <button
                    type="button"
                    className="rounded-full px-[10px] py-[1px] text-[10px] font-semibold uppercase tracking-[0.08em]"
                    style={{
                      color: markerBrowserOpen
                        ? "rgba(224,255,250,0.98)"
                        : "rgba(202,216,228,0.72)",
                      background: markerBrowserOpen
                        ? "linear-gradient(180deg, rgba(56,189,248,0.34), rgba(14,116,144,0.18))"
                        : "linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0.04))",
                      border: markerBrowserOpen
                        ? "1px solid rgba(56,189,248,0.66)"
                        : "1px solid rgba(255,255,255,0.18)",
                      boxShadow: markerBrowserOpen
                        ? "0 0 10px rgba(56,189,248,0.28), inset 0 0 8px rgba(125,211,252,0.18)"
                        : "0 0 0 rgba(0,0,0,0)",
                    }}
                    onClick={() => setMarkerBrowserOpen((open) => !open)}
                    aria-pressed={markerBrowserOpen}
                    title="Recall all markers (Shift+M)"
                  >
                    Markers {norm.markers.length}
                  </button>

                  <div
                    className="flex items-center overflow-hidden rounded-full"
                    style={{
                      background: rulerZoomModeEnabled
                        ? "linear-gradient(180deg, rgba(251,191,36,0.40), rgba(217,119,6,0.24))"
                        : "linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0.04))",
                      border: rulerZoomModeEnabled
                        ? "1px solid rgba(251,191,36,0.72)"
                        : "1px solid rgba(255,255,255,0.18)",
                      boxShadow: rulerZoomModeEnabled
                        ? "0 0 12px rgba(251,191,36,0.35), inset 0 0 8px rgba(253,230,138,0.18)"
                        : "0 0 0 rgba(0,0,0,0)",
                    }}
                  >
                    <button
                      type="button"
                      className="px-[10px] py-[1px] text-[10px] font-semibold uppercase tracking-[0.08em] text-left"
                      style={{
                        minWidth: 52,
                        color: rulerZoomModeEnabled ? "rgba(255,248,220,0.98)" : "rgba(214,206,184,0.62)",
                      }}
                      onClick={() => setTemporaryRulerZoomMode(!rulerZoomModeEnabled)}
                      aria-pressed={rulerZoomModeEnabled}
                      title={
                        rulerZoomModeEnabled
                          ? "Ruler-only zoom armed (auto-off in 10s)"
                          : "Arm temporary ruler-only zoom mode"
                      }
                    >
                      Zoom
                    </button>

                    <div aria-hidden className="h-3 w-px" style={{ background: "rgba(255,255,255,0.22)" }} />

                    <button
                      type="button"
                      className="px-[8px] py-[1px] text-[10px] font-bold uppercase tracking-[0.08em]"
                      style={{ color: rulerZoomModeEnabled ? "rgba(255,248,220,0.96)" : "rgba(214,206,184,0.42)" }}
                      onClick={() => onRulerZoomStep("out")}
                      disabled={!rulerZoomModeEnabled}
                      title="Ruler zoom out"
                    >
                      -
                    </button>

                    <div aria-hidden className="h-3 w-px" style={{ background: "rgba(255,255,255,0.22)" }} />

                    <button
                      type="button"
                      className="px-[8px] py-[1px] text-[10px] font-bold uppercase tracking-[0.08em]"
                      style={{ color: rulerZoomModeEnabled ? "rgba(255,248,220,0.96)" : "rgba(214,206,184,0.42)" }}
                      onClick={() => onRulerZoomStep("in")}
                      disabled={!rulerZoomModeEnabled}
                      title="Ruler zoom in"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>

              <div className="relative" style={{ height: RULER_HEIGHT_PX }}>
                <Ruler
                  durationSec={norm.durationSec}
                  viewportStartSec={effectiveRulerViewportStartSec}
                  viewportEndSec={effectiveRulerViewportEndSec}
                  viewportWidthPx={viewportWidthPx}
                  pxPerSec={effectiveRulerPxPerSec}
                  playheadSec={norm.playheadSec}
                  markers={norm.markers}
                  snap={norm.snap}
                  fps={Number(norm.snap?.fps ?? 30) || 30}
                  dispatch={dispatch as any}
                  onRequestPanByPx={requestRulerPanByPx}
                  onRequestZoomAtViewportX={ignoreRulerWheelZoom}
                  onRequestModifierZoomAtViewportX={requestRulerZoomAtViewportX}
                  onSetZoomModeEnabled={setTemporaryRulerZoomMode}
                  zoomModeEnabled={rulerZoomModeEnabled}
                  onScrubVisualStateChange={({ hoverViewportX, scrubViewportX }) => {
                    setRulerHoverViewportX(hoverViewportX);
                    setRulerScrubViewportX(scrubViewportX);
                  }}
                />

                <RulerSemanticOverlay
                  markers={rulerSemanticMarkers}
                  viewportStartSec={effectiveRulerViewportStartSec}
                  viewportEndSec={effectiveRulerViewportEndSec}
                  viewportWidthPx={viewportWidthPx}
                  pxPerSec={effectiveRulerPxPerSec}
                  showEvents={showRulerEventMarkers}
                  showBeats={showRulerBeatMarkers}
                  activeMarkerId={activeMarkerId}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}